import api from '../httpClient';
import { 
  CommonMealPlanResponse,
  GenerateMealPlanRequest,
  GenerateMealPlanWithOverridesRequest,
  UpdateDraftRequest,
  UpdateCustomizationsRequest,
  UpdateCustomizationsResponse,
  RecipeCustomizations,
  IngredientOverride,
  FinalizeDraftResponse,
  FinalizedMealPlanResponse,
  FinalizedMealPlanData,
  FinalizedMealDay,
  FinalizedMeal,
  CulinaryRating,
  DetailedMealPlanResponse,
  DeleteMealSlotOptions,
  ManageMealSlotRequest,
  ManageMealSlotResponse,
  CopyManualMealPlanDayRequest,
  CopyManualMealPlanDayResponse,
  RecipeSearchClientParams,
  ChangeMealPageResponse,
  GetFinalizedMealPlansParams,
  GetFinalizedMealPlansResponse,
  GetMealPlansAllParams,
  GetMealPlansAllResponse,
  CreateMealProgramRequest,
  UpdateMealProgramRequest,
  CreateManualMealPlanDraftRequest,
  SearchMealPlanItemsParams,
  AddManualMealPlanItemRequest,
  AddFinalizedMealPlanItemRequest,
  UpdateManualMealPlanIngredientRequest,
  RemoveManualMealPlanItemRequest,
  UpdateFinalizedMealPlanItemRequest,
  CustomizeFinalizedMealPlanItemRequest,
  AddRecipeToManualMealPlanRequest,
  AddRecipesToManualMealPlanRequest,
  RemoveRecipeFromManualMealPlanRequest,
  UpdateManualServingsRequest,
  MealPlanByIdResponse,
  MealPlanExportForPdfResponse,
  MealPlanByIdDay,
  MealPlanByIdMeal,
  MealPlanByIdItem,
  ChangeMealPlanDatesRequest,
  ChangeMealPlanDatesResponse,
  DraftGenerationProgressResponse,
  DraftGenerationBlueprintSelection,
  DraftGenerationPromptProgress,
  NutritionalAnalysisMealPlanNameItem,
  MealPlanNutritionalAnalysisApiResponse,
  MealPlanNutritionalAnalysisFoodsApiResponse,
  MealPlanNutrientsVisibilityApiResponse,
  MealPlanNutrientsVisibilityBody,
  MealPlanFeedbackLevel,
  MealPlanFeedbackListResponse,
} from '@/types/meal-plan';
import { IngredientNutritionResponse } from '@/types/ingredient-nutrition';
import {
  CreateCustomRecipeRequest,
  CreateCustomRecipeResponse,
  CustomRecipesListResponse,
  GetCustomRecipesParams,
  CustomRecipeDetailsApiData,
  CustomRecipeDetailsResponse,
  DeleteCustomRecipeResponse,
  CustomRecipeCreateUpdateBody,
  RecipeServingSize,
} from '@/types/custom-recipe';
import { normalizeDraftApiResponseToDraftData, isApiDraftShape } from '@/utils/meal-plan-draft-normalizer';
import { resolveMealDisplayKey } from '@/utils/meal-type-utils';
import { formatGramMlWeightLabel, resolveGramsPerMl, roundTo2, shouldShowDistinctMlValue, toPositiveNumber } from '@/utils/gram-ml';
import type { DraftData } from '@/types/meal-plan';
import type { AIModelOption } from '@/constants/ai-model-options';

const uncachedGetConfig = () => ({
  headers: {
    'Cache-Control': 'no-cache',
    Pragma: 'no-cache',
  },
  params: { _: Date.now() },
});

const sanitizeMealProgramMeals = (
  meals: unknown
): CreateMealProgramRequest['meals'] => {
  if (!Array.isArray(meals)) {
    return [];
  }

  return meals
    .filter((meal): meal is Record<string, unknown> => !!meal && typeof meal === 'object')
    .map((meal, index) => {
      const mealOrderValue = Number(meal.mealOrder);
      const targetCalories = toPositiveNumber(meal.targetCalories);
      const sanitizedMeal: CreateMealProgramRequest['meals'][number] = {
        mealOrder:
          Number.isFinite(mealOrderValue) && mealOrderValue > 0
            ? Math.floor(mealOrderValue)
            : index + 1,
        mealName: String(meal.mealName ?? '').trim(),
        mealTime: String(meal.mealTime ?? '').trim(),
        mealType: String(meal.mealType ?? '').trim(),
      };
      const mealId = typeof meal.id === 'string' ? meal.id.trim() : '';
      if (mealId) {
        sanitizedMeal.id = mealId;
      }
      if (targetCalories != null) {
        sanitizedMeal.targetCalories = targetCalories;
      }
      return sanitizedMeal;
    });
};

const sanitizeOverrideMealProgram = (
  overrideMealProgram: GenerateMealPlanRequest['overrideMealProgram'] | undefined
): GenerateMealPlanRequest['overrideMealProgram'] | undefined => {
  const meals = sanitizeMealProgramMeals(overrideMealProgram?.meals);
  if (!meals.length) {
    return undefined;
  }
  return { meals };
};

type DietaryPreferencePayloadSource = {
  allergies?: string[];
  intolerances?: string[];
  cuisineTypes?: string[];
  preferences?: string[];
  dietaryPreferences?: string[];
};

const toPayloadStringArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  return Array.from(
    new Set(
      value
        .map((item) => (typeof item === 'string' ? item.trim() : ''))
        .filter(Boolean)
    )
  );
};

const buildDietaryPreferencePayload = (
  source?: DietaryPreferencePayloadSource
): Partial<GenerateMealPlanRequest> => {
  if (!source) return {};

  const allergies = toPayloadStringArray(source.allergies);
  const intolerances = toPayloadStringArray(source.intolerances);
  const cuisineTypes = toPayloadStringArray(source.cuisineTypes);
  const preferences = toPayloadStringArray(source.preferences).length > 0
    ? toPayloadStringArray(source.preferences)
    : toPayloadStringArray(source.dietaryPreferences);
  const payload: Partial<GenerateMealPlanRequest> = {};

  if (allergies.length > 0) payload.allergies = allergies;
  if (intolerances.length > 0) payload.intolerances = intolerances;
  if (cuisineTypes.length > 0) payload.cuisineTypes = cuisineTypes;
  if (preferences.length > 0) {
    payload.preferences = preferences;
    payload.dietaryPreferences = preferences;
  }

  return payload;
};

// Generate meal plan with AI and overrides
export const generateMealPlanWithOverrides = async (payload: GenerateMealPlanWithOverridesRequest): Promise<CommonMealPlanResponse> => {
  // Calculate number of days between start and end date
  const start = new Date(payload.startDate);
  const end = new Date(payload.endDate);
  const timeDiff = end.getTime() - start.getTime();
  const days = Math.max(1, Math.ceil(timeDiff / (1000 * 3600 * 24)) + 1); // Ensure minimum 1 day

  // Build the request payload
  const requestPayload: GenerateMealPlanRequest = {
    clientId: payload.clientId,
    startDate: payload.startDate,
    days,
  };
  if (payload.name?.trim()) {
    requestPayload.name = payload.name.trim();
  }

  // Only include override fields if they have values
  if (payload.overrideClientGoals && Object.keys(payload.overrideClientGoals).length > 0) {
    requestPayload.overrideClientGoals = payload.overrideClientGoals;
  }

  const sanitizedOverrideMealProgram = sanitizeOverrideMealProgram(payload.overrideMealProgram);
  if (sanitizedOverrideMealProgram) {
    requestPayload.overrideMealProgram = sanitizedOverrideMealProgram;
  }

  Object.assign(requestPayload, buildDietaryPreferencePayload(payload));

  const response = await api.post('/meal-plans/generate', requestPayload);
  return response.data;
};

// Generate meal plan (legacy - deprecated, use generateMealPlanWithOverrides instead)
export const generateMealPlan = async (clientId: string, planDate: string) => {
  const response = await api.post('/meal-plans', {
    action: 'preview',
    clientId,
    planDate,
  });
  return response.data;
};

// Meal Program API functions
// Create meal program
export const createMealProgram = async (
  clientId: string,
  mealProgramData: CreateMealProgramRequest
): Promise<{ success: boolean; message?: string; data?: any }> => {
  const requestPayload: {
    clientId: string;
    meals: CreateMealProgramRequest['meals'];
    overrideCalorieMismatch?: boolean;
  } = {
    clientId,
    meals: sanitizeMealProgramMeals(mealProgramData.meals),
  };
  if (typeof mealProgramData.overrideCalorieMismatch === 'boolean') {
    requestPayload.overrideCalorieMismatch = mealProgramData.overrideCalorieMismatch;
  }
  const response = await api.post(`/clients/${clientId}/meal-programs`, requestPayload);
  return response.data;
};

// Update meal program (only sends meals that have changed)
export const updateMealProgram = async (
  clientId: string, 
  mealProgramId: string, 
  updateData: UpdateMealProgramRequest
): Promise<{ success: boolean; message?: string; data?: any }> => {
  // Only send meals if they are provided
  const payload: any = {};
  if (updateData.meals) {
    payload.meals = sanitizeMealProgramMeals(updateData.meals);
  }
  if (updateData.name) {
    payload.name = updateData.name;
  }
  if (updateData.description) {
    payload.description = updateData.description;
  }
  if (typeof updateData.overrideCalorieMismatch === 'boolean') {
    payload.overrideCalorieMismatch = updateData.overrideCalorieMismatch;
  }
  
  const response = await api.put(`/clients/${clientId}/meal-programs`, payload);
  return response.data;
};

// Get meal program from API
export const getMealPrograms = async (clientId: string): Promise<any> => {
  try {
    const response = await api.get(`/clients/${clientId}/meal-programs`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Save meal plan from preview
export const saveMealPlanFromPreview = async (
  previewId: string,
  planName: string,
  isActive: boolean = true
): Promise<{ success: boolean; message: string; data?: any }> => {
  const response = await api.post('/meal-plans', {
    type: 'meal-plan',
    action: 'save-from-preview',
    previewId,
    planName,
    isActive
  });
  return response.data;
};

const DUPLICATE_UNIT_SUFFIX_REGEX = /^(.*)_\d+$/;

const SPOONACULAR_ML_VISIBLE_EXACT_CATEGORIES = new Set([
  'alcoholic drink',
  'beer',
  'broth',
  'cava',
  'canned and jarred',
  'chicken broth',
  'chutney',
  'condiment',
  'condiments',
  'cooking oil',
  'drink',
  'extract',
  'fat free yogurt',
  'fish sauce',
  'fruit juice',
  'glaze',
  'gravy',
  'greek yogurt',
  'honey',
  'honey mustard',
  'juice',
  'liqueur',
  'low fat yogurt',
  'mayonnaise',
  'milk',
  'milk substitute',
  'nectar',
  'nut milk',
  'oil, vinegar, salad dressing',
  'olive oil',
  'plain yogurt',
  'pop',
  'rice wine',
  'rum',
  'sangiovese',
  'sauce',
  'soy sauce',
  'sparkling wine',
  'stock',
  'syrup',
  'tea',
  'tea and coffee',
  'tomato sauce',
  'vanilla yogurt',
  'vegetable juice',
  'vegetable oil',
  'vinaigrette',
  'vinegar',
  'whiskey',
  'wine',
  'yogurt',
  'yogurt substitute',
]);

const SPOONACULAR_ML_VISIBLE_EXACT_CATEGORY_LIST = Array.from(SPOONACULAR_ML_VISIBLE_EXACT_CATEGORIES);
const SPOONACULAR_ML_VISIBLE_SUFFIXES = [' sauce', ' juice', ' yogurt', ' oil', ' condiment', ' condiments'];

const SPOONACULAR_ML_VISIBLE_NAME_TOKENS = [
  'broth',
  'cream',
  'juice',
  'kefir',
  'marinade',
  'milk',
  'oil',
  'sauce',
  'smoothie',
  'stock',
  'syrup',
  'vinegar',
  'vinaigrette',
  'yogurt',
];

const normalizeAutocompleteUnit = (value: unknown): string => {
  const trimmed = String(value ?? '').trim();
  if (!trimmed) return '';

  const match = trimmed.match(DUPLICATE_UNIT_SUFFIX_REGEX);
  if (!match) return trimmed;

  const base = (match[1] ?? '').trim();
  return base || trimmed;
};

const normalizeAutocompleteUnits = (units: unknown[]): string[] => {
  const seen = new Set<string>();
  const normalized: string[] = [];

  for (const rawUnit of units) {
    const unit = normalizeAutocompleteUnit(rawUnit);
    if (!unit) continue;

    const key = unit.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    normalized.push(unit);
  }

  return normalized;
};

const normalizeSpoonacularCategory = (value: unknown): string =>
  String(value ?? '').trim().toLowerCase();

const spoonacularTextIncludesPhrase = (text: string, phrase: string): boolean => {
  const escapedPhrase = phrase.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`(^|[^a-z])${escapedPhrase}($|[^a-z])`).test(text);
};

function shouldShowMlForSpoonacularCategory(category: unknown): boolean {
  const normalizedCategory = normalizeSpoonacularCategory(category);
  if (!normalizedCategory) return false;
  if (normalizedCategory.includes('broth')) return true;
  if (SPOONACULAR_ML_VISIBLE_EXACT_CATEGORIES.has(normalizedCategory)) return true;
  return SPOONACULAR_ML_VISIBLE_SUFFIXES.some((suffix) => normalizedCategory.endsWith(suffix));
}

function shouldShowMlForSpoonacularName(name: unknown): boolean {
  const normalizedName = String(name ?? '').trim().toLowerCase();
  if (!normalizedName) return false;
  if (normalizedName.includes('broth')) return true;
  if (SPOONACULAR_ML_VISIBLE_NAME_TOKENS.some((token) => spoonacularTextIncludesPhrase(normalizedName, token))) {
    return true;
  }
  if (SPOONACULAR_ML_VISIBLE_EXACT_CATEGORY_LIST.some((category) => spoonacularTextIncludesPhrase(normalizedName, category))) {
    return true;
  }
  return SPOONACULAR_ML_VISIBLE_SUFFIXES.some((suffix) => normalizedName.endsWith(suffix));
}

function shouldHideMlForSpoonacularPayload(source: unknown, category: unknown, name: unknown): boolean {
  const normalizedSource = String(source ?? '').trim().toLowerCase();
  if (normalizedSource !== 'spoonacular') return false;
  return !shouldShowMlForSpoonacularCategory(category) && !shouldShowMlForSpoonacularName(name);
}

type AutocompleteIngredientItem = {
  id?: string;
  name: string;
  description?: string | null;
  possibleUnits?: string[];
  allergens?: string[];
  allergenTagIds?: string[];
  dietaryTagIds?: string[];
  dietLabels?: string[];
  intoleranceTagIds?: string[];
  intoleranceTags?: string[];
  source?: string;
  sourceId?: string;
  provider?: string;
  isCached?: boolean;
  tagClassificationStatus?: SpoonacularTagClassificationStatus;
  tagClassificationSource?: SpoonacularTagClassificationSource;
  tagClassificationUpdatedAt?: string | null;
  internalId?: string;
  spoonacularId?: string;
  yieldFactor?: number | null;
  retentionFactor?: number | null;
  image?: string;
  imageUrl?: string;
  aisle?: string;
  clinicalWarnings?: ClinicalMealPlanWarning[];
};

export interface ClinicalMealPlanWarning {
  ruleId: string;
  severity: 'warning' | 'blocking';
  condition: string;
  trigger: string;
  message: string;
}

export type SpoonacularTagClassificationStatus = 'not_cached' | 'queued' | 'processing' | 'completed' | 'failed';
export type SpoonacularTagClassificationSource = 'preview' | 'ai' | 'none';
export type SpoonacularTagEnrichmentItemType = 'ingredient' | 'recipe';

export interface SpoonacularTagEnrichmentItem {
  itemType: SpoonacularTagEnrichmentItemType;
  sourceId: string;
}

export interface SpoonacularTagClassificationStatusItem extends SpoonacularTagEnrichmentItem {
  status: SpoonacularTagClassificationStatus;
  tagClassificationStatus: SpoonacularTagClassificationStatus;
  tagClassificationSource: SpoonacularTagClassificationSource;
  tagClassificationUpdatedAt: string | null;
  allergenTagIds: string[];
  intoleranceTagIds: string[];
  dietaryTagIds: string[];
  allergens: string[];
  intoleranceTags: string[];
  dietLabels: string[];
  isVegan: boolean | null;
  isVegetarian: boolean | null;
  isGlutenFree: boolean | null;
  isDairyFree: boolean | null;
  lastError?: string | null;
}

export interface SpoonacularTagEnrichmentResponse {
  items: SpoonacularTagClassificationStatusItem[];
  failedItems?: Array<SpoonacularTagEnrichmentItem & { error: string }>;
}

const normalizeTagClassificationStatus = (value: unknown): SpoonacularTagClassificationStatus | undefined => {
  const normalized = String(value ?? '').trim().toLowerCase();
  return normalized === 'not_cached'
    || normalized === 'queued'
    || normalized === 'processing'
    || normalized === 'completed'
    || normalized === 'failed'
    ? normalized
    : undefined;
};

const normalizeTagClassificationSource = (value: unknown): SpoonacularTagClassificationSource | undefined => {
  const normalized = String(value ?? '').trim().toLowerCase();
  return normalized === 'preview' || normalized === 'ai' || normalized === 'none'
    ? normalized
    : undefined;
};

const extractTagClassificationFields = (item: Record<string, unknown>) => ({
  allergenTagIds: normalizeStringArray(item.allergenTagIds ?? item.allergens),
  dietaryTagIds: normalizeStringArray(item.dietaryTagIds ?? item.dietLabels ?? item.tags),
  dietLabels: normalizeStringArray(item.dietLabels ?? item.dietaryTagIds ?? item.tags),
  intoleranceTagIds: normalizeStringArray(item.intoleranceTagIds ?? item.intoleranceTags),
  intoleranceTags: normalizeStringArray(item.intoleranceTags ?? item.intoleranceTagIds),
  tagClassificationStatus: normalizeTagClassificationStatus(item.tagClassificationStatus ?? item.status),
  tagClassificationSource: normalizeTagClassificationSource(item.tagClassificationSource),
  tagClassificationUpdatedAt: typeof item.tagClassificationUpdatedAt === 'string'
    ? item.tagClassificationUpdatedAt
    : null,
});

const TAG_STATUS_RANK: Record<SpoonacularTagClassificationStatus, number> = {
  failed: 0,
  not_cached: 1,
  queued: 2,
  processing: 3,
  completed: 4,
};

const TAG_SOURCE_RANK: Record<SpoonacularTagClassificationSource, number> = {
  none: 0,
  preview: 1,
  ai: 2,
};

const mergeAutocompleteTagArrays = (...values: unknown[]): string[] => {
  const merged: string[] = [];
  const seen = new Set<string>();

  for (const value of values) {
    const entries = Array.isArray(value)
      ? normalizeStringArray(value)
      : value
        ? [String(value)]
        : [];

    for (const entry of entries) {
      const normalized = String(entry ?? '').trim();
      const key = normalized.toLowerCase();
      if (!normalized || seen.has(key)) continue;
      seen.add(key);
      merged.push(normalized);
    }
  }

  return merged;
};

const preferTagStatus = (
  current: SpoonacularTagClassificationStatus | undefined,
  next: SpoonacularTagClassificationStatus | undefined,
): SpoonacularTagClassificationStatus | undefined => {
  if (!current) return next;
  if (!next) return current;
  return TAG_STATUS_RANK[next] > TAG_STATUS_RANK[current] ? next : current;
};

const preferTagSource = (
  current: SpoonacularTagClassificationSource | undefined,
  next: SpoonacularTagClassificationSource | undefined,
): SpoonacularTagClassificationSource | undefined => {
  if (!current) return next;
  if (!next) return current;
  return TAG_SOURCE_RANK[next] > TAG_SOURCE_RANK[current] ? next : current;
};

const preferTagUpdatedAt = (current: string | null | undefined, next: string | null | undefined): string | null | undefined => {
  if (!current) return next;
  if (!next) return current;
  const currentTime = Date.parse(current);
  const nextTime = Date.parse(next);
  if (!Number.isFinite(currentTime)) return next;
  if (!Number.isFinite(nextTime)) return current;
  return nextTime > currentTime ? next : current;
};

const toCsvQueryParam = (value?: string | string[]): string | undefined => {
  const values = Array.isArray(value) ? value : String(value ?? '').split(',');
  const normalized = values
    .map((entry) => String(entry ?? '').trim())
    .filter(Boolean);
  return normalized.length > 0 ? Array.from(new Set(normalized)).join(',') : undefined;
};

const dedupeAutocompleteItemsByName = (items: AutocompleteIngredientItem[]): AutocompleteIngredientItem[] => {
  const mapByName = new Map<string, AutocompleteIngredientItem>();

  const resolveInternalId = (item: AutocompleteIngredientItem): string | undefined => {
    const explicit = String(item.internalId ?? '').trim();
    if (explicit) return explicit;

    const source = String(item.source ?? '').trim().toLowerCase();
    const id = String(item.id ?? '').trim();
    return source === 'internal' && id ? id : undefined;
  };

  const resolveSpoonacularId = (item: AutocompleteIngredientItem): string | undefined => {
    const explicit = String(item.spoonacularId ?? '').trim();
    if (explicit) return explicit;

    const source = String(item.source ?? '').trim().toLowerCase();
    const id = String(item.id ?? '').trim();
    return source === 'spoonacular' && id ? id : undefined;
  };

  for (const item of items) {
    const key = String(item.name ?? '').trim().toLowerCase();
    if (!key) continue;

    const existing = mapByName.get(key);
    if (!existing) {
      mapByName.set(key, {
        ...item,
        possibleUnits: normalizeAutocompleteUnits(item.possibleUnits ?? []),
        internalId: resolveInternalId(item),
        spoonacularId: resolveSpoonacularId(item),
      });
      continue;
    }

    existing.possibleUnits = normalizeAutocompleteUnits([
      ...(existing.possibleUnits ?? []),
      ...(item.possibleUnits ?? []),
    ]);

    const allergenTags = mergeAutocompleteTagArrays(
      existing.allergens,
      existing.allergenTagIds,
      item.allergens,
      item.allergenTagIds,
    );
    existing.allergens = allergenTags;
    existing.allergenTagIds = allergenTags;

    const dietaryTags = mergeAutocompleteTagArrays(
      existing.dietaryTagIds,
      existing.dietLabels,
      item.dietaryTagIds,
      item.dietLabels,
    );
    existing.dietaryTagIds = dietaryTags;
    existing.dietLabels = dietaryTags;

    const intoleranceTags = mergeAutocompleteTagArrays(
      existing.intoleranceTagIds,
      existing.intoleranceTags,
      item.intoleranceTagIds,
      item.intoleranceTags,
    );
    existing.intoleranceTagIds = intoleranceTags;
    existing.intoleranceTags = intoleranceTags;
    existing.tagClassificationStatus = preferTagStatus(existing.tagClassificationStatus, item.tagClassificationStatus);
    existing.tagClassificationSource = preferTagSource(existing.tagClassificationSource, item.tagClassificationSource);
    existing.tagClassificationUpdatedAt = preferTagUpdatedAt(existing.tagClassificationUpdatedAt, item.tagClassificationUpdatedAt);

    if (!existing.id && item.id) existing.id = item.id;
    if (!existing.source && item.source) existing.source = item.source;
    if (!existing.sourceId && item.sourceId) existing.sourceId = item.sourceId;
    if (!existing.provider && item.provider) existing.provider = item.provider;
    if (!existing.image && item.image) existing.image = item.image;
    if (!existing.imageUrl && item.imageUrl) existing.imageUrl = item.imageUrl;
    if (!existing.aisle && item.aisle) existing.aisle = item.aisle;

    const internalId = resolveInternalId(item);
    const spoonacularId = resolveSpoonacularId(item);
    if (!existing.internalId && internalId) existing.internalId = internalId;
    if (!existing.spoonacularId && spoonacularId) existing.spoonacularId = spoonacularId;

    if (existing.isCached !== true) {
      const nextIsCached = item.isCached === true || String(item.source ?? '').trim().toLowerCase() === 'cached';
      if (nextIsCached) {
        existing.isCached = true;
      }
    }
  }

  return Array.from(mapByName.values());
};

type IngredientPayloadUnitMeasurement = {
  value: string;
  grams?: number;
  ml?: number;
};

const collectIngredientPayloadUnitMeasurements = (
  payload: Record<string, unknown> | undefined,
  hideMl: boolean,
): IngredientPayloadUnitMeasurement[] => {
  if (!payload || typeof payload !== 'object') return [];

  const measurementByKey = new Map<string, IngredientPayloadUnitMeasurement>();
  const ensureUnit = (rawUnit: unknown): IngredientPayloadUnitMeasurement | null => {
    const value = normalizeAutocompleteUnit(rawUnit);
    if (!value) return null;

    const key = value.toLowerCase();
    if (hideMl && key === 'ml') return null;

    let measurement = measurementByKey.get(key);
    if (!measurement) {
      measurement = { value };
      measurementByKey.set(key, measurement);
    }

    return measurement;
  };
  const setGrams = (rawUnit: unknown, rawGrams: unknown) => {
    const measurement = ensureUnit(rawUnit);
    const grams = toPositiveNumber(rawGrams);
    if (!measurement || grams == null) return;
    if (measurement.grams == null) {
      measurement.grams = roundTo2(grams);
    }
  };
  const setMl = (rawUnit: unknown, rawMl: unknown) => {
    const measurement = ensureUnit(rawUnit);
    const ml = toPositiveNumber(rawMl);
    if (!measurement || ml == null) return;
    if (measurement.ml == null) {
      measurement.ml = roundTo2(ml);
    }
  };

  const possibleUnits = Array.isArray(payload.possibleUnits) ? payload.possibleUnits : [];
  for (const unit of possibleUnits) {
    ensureUnit(unit);
  }

  const topLevelUnits = Array.isArray(payload.units) ? payload.units : [];
  for (const unit of topLevelUnits) {
    ensureUnit(unit);
  }

  const servings = Array.isArray(payload.servings) ? payload.servings : [];
  for (const serving of servings) {
    const servingRecord = serving as { servingId?: unknown; unit?: unknown; name?: unknown };
    ensureUnit(servingRecord.servingId ?? servingRecord.unit ?? servingRecord.name);
  }

  const defaultServingId = (payload as { defaultServingId?: unknown }).defaultServingId;
  ensureUnit(defaultServingId);

  const possibleUnitWeights = Array.isArray(payload.possibleUnitWeights) ? payload.possibleUnitWeights : [];
  for (const entry of possibleUnitWeights) {
    const unitWeight = entry as { unit?: unknown; grams?: unknown };
    setGrams(unitWeight.unit, unitWeight.grams);
  }

  const servingWeights = payload.servingWeights;
  if (servingWeights && typeof servingWeights === 'object') {
    for (const [unit, grams] of Object.entries(servingWeights as Record<string, unknown>)) {
      setGrams(unit, grams);
    }
  }

  const servingOverrides = Array.isArray(payload.servingOverrides) ? payload.servingOverrides : [];
  for (const entry of servingOverrides) {
    const servingOverride = entry as { servingId?: unknown; grams?: unknown };
    setGrams(servingOverride.servingId, servingOverride.grams);
  }

  const possibleUnitMlWeights = Array.isArray(payload.possibleUnitMlWeights) ? payload.possibleUnitMlWeights : [];
  for (const entry of possibleUnitMlWeights) {
    const unitMlWeight = entry as { unit?: unknown; ml?: unknown };
    setMl(unitMlWeight.unit, unitMlWeight.ml);
  }

  const servingMlWeights = payload.servingMlWeights;
  if (servingMlWeights && typeof servingMlWeights === 'object') {
    for (const [unit, ml] of Object.entries(servingMlWeights as Record<string, unknown>)) {
      setMl(unit, ml);
    }
  }

  return Array.from(measurementByKey.values());
};

const buildIngredientServingWeights = (
  measurements: IngredientPayloadUnitMeasurement[],
): Record<string, number> | undefined => {
  const servingWeights = Object.fromEntries(
    measurements
      .filter((measurement) => measurement.grams != null)
      .map((measurement) => [measurement.value, measurement.grams] as const),
  );

  return Object.keys(servingWeights).length > 0 ? servingWeights : undefined;
};

const formatIngredientUnitOptionLabel = (
  unit: string,
  grams: unknown,
  ml: unknown,
  gramsPerMl: unknown,
  hideMl: boolean,
): string => {
  const gramsValue = toPositiveNumber(grams);
  if (gramsValue != null) {
    const gramsText = `${roundTo2(gramsValue)}g`;
    if (hideMl) return `${unit} (${gramsText})`;

    const mlValue = toPositiveNumber(ml);
    if (mlValue != null && shouldShowDistinctMlValue(gramsValue, mlValue)) {
      return `${unit} (${gramsText} / ${roundTo2(mlValue)}ml)`;
    }

    return formatGramMlWeightLabel(unit, gramsValue, gramsPerMl);
  }

  const mlValue = hideMl ? null : toPositiveNumber(ml);
  if (mlValue != null) {
    return `${unit} (${roundTo2(mlValue)}ml)`;
  }

  return unit;
};

const buildIngredientUnitOptions = (
  measurements: IngredientPayloadUnitMeasurement[],
  gramsPerMl: unknown,
  hideMl: boolean,
): IngredientUnitOption[] | undefined => {
  if (measurements.length === 0) return undefined;

  return measurements.map((measurement) => ({
    value: measurement.value,
    label: formatIngredientUnitOptionLabel(
      measurement.value,
      measurement.grams,
      measurement.ml,
      gramsPerMl,
      hideMl,
    ),
  }));
};

const normalizeAutocompleteSearchToken = (value: string): string =>
  value.trim().toLowerCase().replace(/\s+/g, ' ');

const stripIngredientSourceSuffix = (value: string): string => {
  const raw = String(value ?? '').trim();
  if (!raw) return '';
  return raw.replace(/\s*\((?:caloriescience|spoonacular|edamam|internal)\)\s*$/i, '').trim();
};

const resolveAutocompleteUnits = (
  items: AutocompleteIngredientItem[],
  topLevelUnits: string[],
  query: string,
  mode: 'basic' | 'with_units' | 'units_only'
): string[] => {
  const unitsFromItems = items.length
    ? normalizeAutocompleteUnits(items.flatMap((item) => item.possibleUnits ?? []))
    : [];

  if (mode === 'units_only') {
    const normalizedQuery = normalizeAutocompleteSearchToken(query);
    const exactItem = items.find((item) =>
      normalizeAutocompleteSearchToken(String(item.name ?? '')) === normalizedQuery
    );
    const exactItemUnits = normalizeAutocompleteUnits(exactItem?.possibleUnits ?? []);
    if (exactItemUnits.length > 0) {
      return exactItemUnits;
    }
  }

  return topLevelUnits.length > 0 ? topLevelUnits : unitsFromItems;
};

const hasAutocompleteValue = (value: unknown): boolean => String(value ?? '').trim().length > 0;

const inferAutocompleteItemSource = (
  item: {
    source?: string;
    spoonacularId?: string;
    spoonacularIngredientId?: string;
    internalId?: string;
    internalIngredientId?: string;
  },
  requestedSource: 'all' | 'spoonacular' | 'edamam' | 'internal',
): string | undefined => {
  const explicit = String(item.source ?? '').trim();
  if (explicit) return explicit;
  if (
    requestedSource === 'spoonacular'
    || hasAutocompleteValue(item.spoonacularId)
    || hasAutocompleteValue(item.spoonacularIngredientId)
  ) {
    return 'spoonacular';
  }
  if (
    requestedSource === 'internal'
    || hasAutocompleteValue(item.internalId)
    || hasAutocompleteValue(item.internalIngredientId)
  ) {
    return 'internal';
  }
  if (requestedSource === 'edamam') {
    return 'edamam';
  }
  return undefined;
};

const inferAutocompleteItemProvider = (
  item: { provider?: string },
  sourceValue: string | undefined,
): string | undefined => {
  const explicit = String(item.provider ?? '').trim();
  if (explicit) return explicit;
  return sourceValue === 'spoonacular' || sourceValue === 'internal' || sourceValue === 'edamam'
    ? sourceValue
    : undefined;
};

// Ingredients and Recipes
export const getIngredientsAutocomplete = async (
  query: string, 
  mode: 'basic' | 'with_units' | 'units_only' = 'basic',
  selectedUnit?: string,
  source: 'all' | 'spoonacular' | 'edamam' | 'internal' = 'spoonacular',
  allergies?: string | string[],
  clientId?: string
): Promise<{
  count: number;
  query: string;
  success: boolean;
  suggestions?: string[];
  units?: string[];
  /** Full items from API (e.g. data.results.spoonacular) with name, possibleUnits, id, etc. */
  items?: Array<{
    id?: string;
    name: string;
    possibleUnits?: string[];
    allergens?: string[];
    allergenTagIds?: string[];
    dietaryTagIds?: string[];
    dietLabels?: string[];
    intoleranceTagIds?: string[];
    intoleranceTags?: string[];
    source?: string;
    sourceId?: string;
    provider?: string;
    isCached?: boolean;
    tagClassificationStatus?: SpoonacularTagClassificationStatus;
    tagClassificationSource?: SpoonacularTagClassificationSource;
    tagClassificationUpdatedAt?: string | null;
    internalId?: string;
    spoonacularId?: string;
    yieldFactor?: number | null;
    retentionFactor?: number | null;
    image?: string;
    imageUrl?: string;
    aisle?: string;
    description?: string | null;
    clinicalWarnings?: ClinicalMealPlanWarning[];
  }>;
}> => {
  let url = `/ingredients/autocomplete?q=${encodeURIComponent(query)}&source=${source}&mode=${mode}`;
  
  // Only add selectedUnit if provided (don't send it for basic mode or when ingredient changes)
  if (selectedUnit) {
    url += `&selectedUnit=${encodeURIComponent(selectedUnit)}`;
  }

  const allergyParam = toCsvQueryParam(allergies);
  if (allergyParam) {
    url += `&allergies=${encodeURIComponent(allergyParam)}`;
  }

  if (clientId?.trim()) {
    url += `&clientId=${encodeURIComponent(clientId.trim())}`;
  }
  
  const response = await api.get(url);
  const data = response.data;
  // If backend returns wrong shape (e.g. client object instead of autocomplete), return empty suggestions
  if (data && typeof data === 'object' && 'client' in data && !('suggestions' in data)) {
    return { success: true, query, count: 0, suggestions: [], units: [] };
  }
  // Normalize API shape: data.ingredients (array) -> suggestions + items (with possibleUnits)
  const ingredientsArray = data?.data?.ingredients ?? data?.ingredients;
  if (Array.isArray(ingredientsArray)) {
    const mappedItems: AutocompleteIngredientItem[] = ingredientsArray.map((item: {
      id?: string;
      name?: string;
      possibleUnits?: string[];
      allergens?: unknown;
      allergenTagIds?: unknown;
      dietaryTagIds?: unknown;
      dietLabels?: unknown;
      intoleranceTagIds?: unknown;
      intoleranceTags?: unknown;
      tagClassificationStatus?: unknown;
      tagClassificationSource?: unknown;
      tagClassificationUpdatedAt?: unknown;
      source?: string;
      sourceId?: string;
      provider?: string;
      isCached?: boolean;
      cached?: boolean;
      internalId?: string;
      internalIngredientId?: string;
      spoonacularId?: string;
      spoonacularIngredientId?: string;
      yieldFactor?: number | null;
      retentionFactor?: number | null;
      image?: string;
      imageUrl?: string;
      category?: string;
      aisle?: string;
      description?: string | null;
      clinicalWarnings?: ClinicalMealPlanWarning[];
    }) => {
      const sourceValue = inferAutocompleteItemSource(item, source);
      return {
        id: item.id,
        name: item?.name ?? '',
        description: typeof item.description === 'string' && item.description.trim().length > 0
          ? item.description.trim()
          : null,
        possibleUnits: normalizeAutocompleteUnits(Array.isArray(item.possibleUnits) ? item.possibleUnits : []),
        allergens: normalizeStringArray(item.allergens),
        allergenTagIds: normalizeStringArray(item.allergenTagIds),
        ...extractTagClassificationFields(item as Record<string, unknown>),
        source: sourceValue,
        sourceId: item.sourceId,
        provider: inferAutocompleteItemProvider(item, sourceValue),
        isCached: item.isCached ?? item.cached,
        internalId: item.internalId ?? item.internalIngredientId ?? (sourceValue === 'internal' ? item.id : undefined),
        spoonacularId: item.spoonacularId ?? item.spoonacularIngredientId ?? (sourceValue === 'spoonacular' ? item.id : undefined),
        yieldFactor: item.yieldFactor ?? null,
        retentionFactor: item.retentionFactor ?? null,
        image: item.image ?? item.imageUrl,
        imageUrl: item.imageUrl ?? item.image,
        aisle: item.aisle ?? item.category,
        clinicalWarnings: Array.isArray(item.clinicalWarnings) ? item.clinicalWarnings : [],
      };
    }).filter((i) => typeof i.name === 'string' && i.name.length > 0);
    const items = dedupeAutocompleteItemsByName(mappedItems);
    const suggestions = items.map((i) => i.name);
    const queryVal = data?.data?.query ?? data?.query ?? query;
    const topLevelUnits = normalizeAutocompleteUnits(Array.isArray(data?.units) ? data.units : []);
    const resolvedUnits = resolveAutocompleteUnits(items, topLevelUnits, queryVal, mode);
    return {
      success: Boolean(data?.success ?? true),
      query: queryVal,
      count: items.length,
      suggestions,
      units: resolvedUnits,
      items,
    };
  }
  // Legacy: data.results.spoonacular / data.results.internal
  const resultsPayload = data?.data?.results ?? data?.results;
  if (resultsPayload && typeof resultsPayload === 'object') {
    const results = resultsPayload;
    const spoonacular = Array.isArray(results.spoonacular)
      ? results.spoonacular.map((item: {
          id?: string;
          name?: string;
          possibleUnits?: string[];
          allergens?: unknown;
          allergenTagIds?: unknown;
          dietaryTagIds?: unknown;
          dietLabels?: unknown;
          intoleranceTagIds?: unknown;
          intoleranceTags?: unknown;
          tagClassificationStatus?: unknown;
          tagClassificationSource?: unknown;
          tagClassificationUpdatedAt?: unknown;
          source?: string;
          sourceId?: string;
          provider?: string;
          isCached?: boolean;
          cached?: boolean;
          internalId?: string;
          internalIngredientId?: string;
          spoonacularId?: string;
          spoonacularIngredientId?: string;
          yieldFactor?: number | null;
          retentionFactor?: number | null;
          image?: string;
          imageUrl?: string;
          aisle?: string;
          category?: string;
          description?: string | null;
        }) => ({
          id: item.id,
          name: item?.name ?? '',
          description: typeof item.description === 'string' && item.description.trim().length > 0
            ? item.description.trim()
            : null,
          possibleUnits: normalizeAutocompleteUnits(Array.isArray(item.possibleUnits) ? item.possibleUnits : []),
          allergens: normalizeStringArray(item.allergens),
          allergenTagIds: normalizeStringArray(item.allergenTagIds),
          ...extractTagClassificationFields(item as Record<string, unknown>),
          source: item.source ?? 'spoonacular',
          sourceId: item.sourceId,
          provider: item.provider ?? 'spoonacular',
          isCached: item.isCached ?? item.cached,
          internalId: item.internalId ?? item.internalIngredientId,
          spoonacularId: item.spoonacularId ?? item.spoonacularIngredientId ?? item.id,
          yieldFactor: item.yieldFactor ?? null,
          retentionFactor: item.retentionFactor ?? null,
          image: item.image ?? item.imageUrl,
          imageUrl: item.imageUrl ?? item.image,
          aisle: item.aisle ?? item.category,
        }))
      : [];
    const internal = Array.isArray(results.internal)
      ? results.internal.map((item: {
          id?: string;
          name?: string;
          possibleUnits?: string[];
          allergens?: unknown;
          allergenTagIds?: unknown;
          dietaryTagIds?: unknown;
          dietLabels?: unknown;
          intoleranceTagIds?: unknown;
          intoleranceTags?: unknown;
          tagClassificationStatus?: unknown;
          tagClassificationSource?: unknown;
          tagClassificationUpdatedAt?: unknown;
          source?: string;
          sourceId?: string;
          provider?: string;
          isCached?: boolean;
          cached?: boolean;
          internalId?: string;
          internalIngredientId?: string;
          spoonacularId?: string;
          spoonacularIngredientId?: string;
          yieldFactor?: number | null;
          retentionFactor?: number | null;
          image?: string;
          imageUrl?: string;
          aisle?: string;
          category?: string;
          description?: string | null;
        }) => ({
          id: item.id,
          name: item?.name ?? '',
          description: typeof item.description === 'string' && item.description.trim().length > 0
            ? item.description.trim()
            : null,
          possibleUnits: normalizeAutocompleteUnits(Array.isArray(item.possibleUnits) ? item.possibleUnits : []),
          allergens: normalizeStringArray(item.allergens),
          allergenTagIds: normalizeStringArray(item.allergenTagIds),
          ...extractTagClassificationFields(item as Record<string, unknown>),
          source: item.source ?? 'internal',
          sourceId: item.sourceId,
          provider: item.provider ?? 'internal',
          isCached: item.isCached ?? item.cached,
          internalId: item.internalId ?? item.internalIngredientId ?? item.id,
          spoonacularId: item.spoonacularId ?? item.spoonacularIngredientId,
          yieldFactor: item.yieldFactor ?? null,
          retentionFactor: item.retentionFactor ?? null,
          image: item.image ?? item.imageUrl,
          imageUrl: item.imageUrl ?? item.image,
          aisle: item.aisle ?? item.category,
        }))
      : [];
    const edamam = Array.isArray((results as { edamam?: unknown[] }).edamam)
      ? ((results as {
          edamam?: Array<{
            id?: string;
            name?: string;
            possibleUnits?: string[];
            allergens?: unknown;
            allergenTagIds?: unknown;
            dietaryTagIds?: unknown;
            dietLabels?: unknown;
            intoleranceTagIds?: unknown;
            intoleranceTags?: unknown;
            tagClassificationStatus?: unknown;
            tagClassificationSource?: unknown;
            tagClassificationUpdatedAt?: unknown;
            source?: string;
            sourceId?: string;
            provider?: string;
            isCached?: boolean;
            internalId?: string;
            internalIngredientId?: string;
            spoonacularId?: string;
            spoonacularIngredientId?: string;
            yieldFactor?: number | null;
            retentionFactor?: number | null;
            image?: string;
            imageUrl?: string;
            aisle?: string;
            category?: string;
            description?: string | null;
          }>;
        }).edamam ?? []).map((item) => ({
          id: item.id,
          name: item?.name ?? '',
          description: typeof item.description === 'string' && item.description.trim().length > 0
            ? item.description.trim()
            : null,
          possibleUnits: normalizeAutocompleteUnits(Array.isArray(item.possibleUnits) ? item.possibleUnits : []),
          allergens: normalizeStringArray(item.allergens),
          allergenTagIds: normalizeStringArray(item.allergenTagIds),
          ...extractTagClassificationFields(item as Record<string, unknown>),
          source: item.source ?? 'edamam',
          sourceId: item.sourceId,
          provider: item.provider,
          isCached: item.isCached,
          internalId: item.internalId ?? item.internalIngredientId,
          spoonacularId: item.spoonacularId ?? item.spoonacularIngredientId,
          yieldFactor: item.yieldFactor ?? null,
          retentionFactor: item.retentionFactor ?? null,
          image: item.image ?? item.imageUrl,
          imageUrl: item.imageUrl ?? item.image,
          aisle: item.aisle ?? item.category,
        }))
      : [];
    const mergedItems: AutocompleteIngredientItem[] = [...internal, ...spoonacular, ...edamam]
      .filter((i) => typeof i.name === 'string' && i.name.length > 0);
    const items = dedupeAutocompleteItemsByName(mergedItems);
    const suggestions = items.map((i) => i.name);
    const queryVal = data?.data?.query ?? data?.query ?? query;
    const topLevelUnits = normalizeAutocompleteUnits(Array.isArray(data?.units) ? data.units : []);
    const resolvedUnits = resolveAutocompleteUnits(items, topLevelUnits, queryVal, mode);
    return {
      success: Boolean(data?.success ?? true),
      query: queryVal,
      count: suggestions.length,
      suggestions,
      units: resolvedUnits,
      items,
    };
  }
  return data;
};

// Search ingredients using /ingredients/search endpoint
export const searchIngredients = async (
  query: string,
  limit: number = 30,
  clientId?: string
): Promise<{
  success: boolean;
  data?: any[];
  recipes?: any[];
  ingredients?: any[];
  message?: string;
}> => {
  try {
    let url = `/ingredients/search?query=${encodeURIComponent(query)}&limit=${limit}`;
    if (clientId) {
      url += `&clientId=${encodeURIComponent(clientId)}`;
    }
    const response = await api.get(url);
    return response.data;
  } catch (error) {
    console.error('Error searching ingredients:', error);
    throw error;
  }
};

// Get recipe details from CalorieScience API
export const getRecipeDetails = async (recipeId: string | number): Promise<any> => {
  try {
    const response = await api.get(`/recipes/${recipeId}/details`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Recipe search for client with filters
export const recipeSearchClient = async (params: RecipeSearchClientParams): Promise<any> => {
  try {
    // Build query parameters
    const queryParams = new URLSearchParams();
    
    queryParams.append('clientId', params.clientId);
    
    if (params.searchType) {
      queryParams.append('searchType', params.searchType);
    }
    
    // For broad/name search, use query parameter
    if (params.query && params.query.trim().length >= 2) {
      queryParams.append('query', params.query.trim());
    }
    
    // For ingredient search, use ingredients parameter
    if (params.ingredients) {
      const ingredientsStr = Array.isArray(params.ingredients) 
        ? params.ingredients.join(',') 
        : params.ingredients;
      queryParams.append('ingredients', ingredientsStr);
    }
    
    if (params.cuisineType) {
      const cuisineStr = Array.isArray(params.cuisineType) 
        ? params.cuisineType.join(',') 
        : params.cuisineType;
      queryParams.append('cuisineType', cuisineStr);
    }
    
    if (params.maxResults) {
      queryParams.append('maxResults', params.maxResults.toString());
    }
    
    // Add allergies parameter
    if (params.allergies) {
      const allergiesStr = Array.isArray(params.allergies)
        ? params.allergies.join(',')
        : params.allergies;
      queryParams.append('allergies', allergiesStr);
    }
    
    if (params.provider) {
      queryParams.append('provider', params.provider);
    }
    
    const response = await api.get(`/recipe-search-client?${queryParams.toString()}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Manual Meal Plan Functions
// Create manual meal plan draft
/** `planDate` is yyyy-MM-dd. Optional `overrideMealProgram` matches AI generate (meal slot seeding). */
function normalizeManualCreatePlanDate(planDate: string): string {
  const trimmed = planDate.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
  const d = new Date(trimmed);
  return Number.isNaN(d.getTime()) ? trimmed : d.toISOString().slice(0, 10);
}

export const createManualMealPlanDraft = async (
  payload: CreateManualMealPlanDraftRequest
): Promise<CommonMealPlanResponse> => {
  try {
    const body: Record<string, unknown> = {
      clientId: payload.clientId,
      planDate: normalizeManualCreatePlanDate(payload.planDate),
    };
    if (payload.demo) {
      body.demo = true;
      const response = await api.post('/meal-plans/manual/create', body);
      return response.data;
    }
    body.durationDays = Math.max(1, Math.floor(Number(payload.durationDays)) || 1);
    if (payload.name?.trim()) {
      body.name = payload.name.trim();
    }
    const sanitizedOverrideMealProgram = sanitizeOverrideMealProgram(payload.overrideMealProgram);
    if (sanitizedOverrideMealProgram) {
      body.overrideMealProgram = sanitizedOverrideMealProgram;
    }
    if (payload.overrideClientGoals && Object.keys(payload.overrideClientGoals).length > 0) {
      body.overrideClientGoals = payload.overrideClientGoals;
    }
    Object.assign(body, buildDietaryPreferencePayload(payload));
    const response = await api.post('/meal-plans/manual/create', body);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Search for recipes and ingredients (Unified Search)
export const searchMealPlanItems = async (
  params: SearchMealPlanItemsParams
): Promise<{
  results: Array<{
    id: string;
    type: 'recipe' | 'ingredient';
    source: 'cached' | 'spoonacular';
    provider?: 'internal' | 'spoonacular' | 'custom';
    sourceId?: string;
    name: string;
    imageUrl?: string;
    nutrition?: any;
    isVegan?: boolean;
    isVegetarian?: boolean;
    isGlutenFree?: boolean;
    isDairyFree?: boolean;
    allergens?: string[];
    allergenTagIds?: string[];
    dietaryTagIds?: string[];
    dietLabels?: string[];
    intoleranceTagIds?: string[];
    intoleranceTags?: string[];
    tagClassificationStatus?: SpoonacularTagClassificationStatus;
    tagClassificationSource?: SpoonacularTagClassificationSource;
    tagClassificationUpdatedAt?: string | null;
    clinicalWarnings?: ClinicalMealPlanWarning[];
    recipe?: {
      readyInMinutes?: number;
      servings?: number;
      defaultServing?: number;
      allowedServings?: number[];
      defaultServingId?: string | null;
      defaultServingGrams?: number | null;
      servingSizes?: RecipeServingSize[];
      cuisines?: string[];
      mealTypes?: string[];
      [key: string]: unknown;
    };
    ingredient?: any;
  }>;
  totalResults: number;
  offset: number;
  limit: number;
  searchFilters?: {
    allergies?: string[];
    [key: string]: unknown;
  };
  sources: Record<string, number>;
}> => {
  try {
    const queryParams = new URLSearchParams({
      q: params.q,
    });
    
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.offset) queryParams.append('offset', params.offset.toString());
    if (params.type) queryParams.append('type', params.type);
    if (params.source) queryParams.append('source', params.source);
    if (params.cuisine) queryParams.append('cuisine', params.cuisine);
    if (params.mealType) queryParams.append('mealType', params.mealType);
    if (params.diet) queryParams.append('diet', params.diet);
    if (params.allergies) queryParams.append('allergies', params.allergies);
    if (params.clientId) queryParams.append('clientId', params.clientId);

    const response = await api.get(`/meal-plans/search?${queryParams.toString()}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const ensureSpoonacularTagEnrichment = async (
  items: SpoonacularTagEnrichmentItem[]
): Promise<SpoonacularTagEnrichmentResponse> => {
  const response = await api.post<SpoonacularTagEnrichmentResponse>(
    '/spoonacular/tag-enrichment/ensure',
    { items }
  );
  return response.data;
};

export const getSpoonacularTagEnrichmentStatus = async (
  items: SpoonacularTagEnrichmentItem[]
): Promise<SpoonacularTagEnrichmentResponse> => {
  const query = items
    .map((item) => `${item.itemType}:${item.sourceId}`)
    .join(',');
  const response = await api.get<SpoonacularTagEnrichmentResponse>(
    `/spoonacular/tag-enrichment/status?items=${encodeURIComponent(query)}`
  );
  return response.data;
};

// Add item (recipe or ingredient) to manual meal plan
export const addManualMealPlanItem = async (
  request: AddManualMealPlanItemRequest
): Promise<{ success: boolean; data?: any; message?: string }> => {
  try {
    const response = await api.post('/meal-plans/manual/add-item', request);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Add recipe item to finalized meal plan
export const addFinalizedMealPlanItem = async (
  mealPlanId: string,
  request: AddFinalizedMealPlanItemRequest
): Promise<{ success: boolean; data?: any; message?: string }> => {
  try {
    const response = await api.post(`/meal-plans/${mealPlanId}/add-item`, request);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Update ingredient amount in manual meal plan
export const updateManualMealPlanIngredient = async (
  request: UpdateManualMealPlanIngredientRequest
): Promise<{ success: boolean; data?: any; message?: string }> => {
  try {
    const response = await api.put('/meal-plans/manual/update-ingredient', request);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Remove item (recipe or ingredient) from manual meal plan - DELETE /meal-plans/manual/remove-item
export const removeManualMealPlanItem = async (
  request: RemoveManualMealPlanItemRequest
): Promise<{ success: boolean; data?: unknown; message?: string }> => {
  try {
    const response = await api.delete('/meal-plans/manual/remove-item', { data: request });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Update item in finalized meal plan
export const updateFinalizedMealPlanItem = async (
  mealPlanId: string,
  request: UpdateFinalizedMealPlanItemRequest
): Promise<{ success: boolean; data?: any; message?: string }> => {
  try {
    const response = await api.put(`/meal-plans/${mealPlanId}/update-item`, request);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Customize recipe item in finalized meal plan
export const customizeFinalizedMealPlanItem = async (
  mealPlanId: string,
  request: CustomizeFinalizedMealPlanItemRequest
): Promise<{ success: boolean; data?: any; message?: string }> => {
  try {
    const response = await api.put(`/meal-plans/${mealPlanId}/customize-item`, request);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Get effective ingredient list for a finalized recipe item (merges overrides)
export const getFinalizedMealPlanItemIngredients = async (
  mealPlanId: string,
  itemId: string
): Promise<{ success: boolean; data?: any; message?: string }> => {
  try {
    const response = await api.get(`/meal-plans/${mealPlanId}/item-ingredients`, {
      params: { itemId },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Add recipe to manual meal plan
export const addRecipeToManualMealPlan = async (
  request: AddRecipeToManualMealPlanRequest
): Promise<{ success: boolean; data?: any; message?: string }> => {
  try {
    const response = await api.post('/meal-plans/manual/add-recipe', request);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Add multiple recipes to manual meal plan
export const addRecipesToManualMealPlan = async (
  request: AddRecipesToManualMealPlanRequest
): Promise<{ success: boolean; data?: any; message?: string }> => {
  try {
    const response = await api.post('/meal-plans/manual/add-recipes', request);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Remove recipe from manual meal plan - body supports optional recipeId, removeMealSlot
export const removeRecipeFromManualMealPlan = async (
  draftId: string,
  day: number,
  mealName: string,
  recipeId?: string,
  removeMealSlot?: boolean
): Promise<{ success: boolean; data?: any; message?: string }> => {
  try {
    const body: RemoveRecipeFromManualMealPlanRequest = { draftId, day, mealName };
    if (recipeId !== undefined) body.recipeId = recipeId;
    if (removeMealSlot !== undefined) body.removeMealSlot = removeMealSlot;
    const response = await api.delete('/meal-plans/manual/remove-recipe', { data: body });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Finalize manual meal plan - POST /meal-plans/manual/{id}/finalize
export const finalizeManualMealPlan = async (
  draftId: string,
  planName?: string,
  tags?: string[],
  overwriteConflicts?: boolean,
  acknowledgeClinicalIssues?: boolean,
): Promise<CommonMealPlanResponse> => {
  try {
    const normalizedTags = Array.isArray(tags)
      ? tags.map((tag) => String(tag ?? '').trim()).filter(Boolean)
      : [];

    const response = await api.post(`/meal-plans/manual/${draftId}/finalize`, {
      draftId,
      planName,
      ...(normalizedTags.length > 0 ? { tags: normalizedTags } : {}),
      ...(overwriteConflicts ? { overwriteConflicts: true } : {}),
      ...(acknowledgeClinicalIssues ? { acknowledgeClinicalIssues: true } : {}),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export interface ManualFinalizeBlockingIssue {
  ruleId: string;
  severity: 'blocking';
  message: string;
  guidelineName?: string;
  condition?: string;
  trigger?: string;
  dayNumber?: number;
  mealName?: string;
  itemType?: 'recipe' | 'ingredient';
  itemName?: string;
}

export interface ManualFinalizeWarningIssue {
  ruleId: string;
  severity: 'warning';
  message: string;
  guidelineName?: string;
  condition?: string;
  trigger?: string;
  dayNumber?: number;
  mealName?: string;
  itemType?: 'recipe' | 'ingredient';
  itemName?: string;
  actualGrams?: number;
  targetMaxGrams?: number;
}

export interface ManualFinalizeValidation {
  draftId: string;
  status: 'incomplete' | 'ready' | 'blocked';
  canFinalize: boolean;
  /** Clinical issues are soft blocks: the nutritionist confirms a popup, then finalization proceeds. */
  requiresAcknowledgement?: boolean;
  allMealSlotsFilled: boolean;
  checkedAt: string;
  summary: {
    totalMealSlots: number;
    filledMealSlots: number;
    missingMealSlots: number;
    hardBlockCount: number;
    warningCount: number;
  };
  missingMealSlots: Array<{ dayNumber: number; mealOrder: number; mealName: string }>;
  blockingIssues: ManualFinalizeBlockingIssue[];
  warnings: ManualFinalizeWarningIssue[];
}

export const validateManualMealPlanFinalization = async (
  draftId: string,
): Promise<{ success: boolean; data: ManualFinalizeValidation }> => {
  const response = await api.post(`/meal-plans/manual/${draftId}/finalize-validation`, { draftId });
  return response.data;
};

// PUT /meal-plans/manual/update-servings - manual draft only
export const updateManualMealPlanServings = async (
  request: UpdateManualServingsRequest
): Promise<{ success: boolean; data?: any; message?: string }> => {
  const nutritionServings = request.nutritionServings ?? request.servings;
  if (nutritionServings == null) {
    throw new Error('nutritionServings or servings is required');
  }

  const response = await api.put('/meal-plans/manual/update-servings', {
    draftId: request.draftId,
    day: request.day,
    mealName: request.mealName,
    ...(request.itemId ? { itemId: request.itemId } : {}),
    recipeId: request.recipeId ?? request.itemId,
    nutritionServings,
    servings: request.servings ?? nutritionServings,
    selectedServingId: request.selectedServingId ?? request.portionSizeId,
    portionSizeMultiplier: request.portionSizeMultiplier,
  });
  return response.data;
};

// Get manual meal plan draft
export const getManualMealPlanDraft = async (
  draftId: string
): Promise<CommonMealPlanResponse> => {
  try {
    const response = await api.get(`/meal-plans/manual/${draftId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Manage meal slots in manual meal plan
export const manageMealSlot = async (
  request: ManageMealSlotRequest
): Promise<ManageMealSlotResponse> => {
  try {
    // Filter out undefined values to avoid sending them to the API
    const filteredRequest = Object.fromEntries(
      Object.entries(request).filter(([_, value]) => value !== undefined)
    );
    
    const response = await api.post('/meal-plans/manual/meal-slot', filteredRequest);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Helper functions for meal slot management
export const createMealSlot = async (
  draftId: string,
  day: number,
  mealName: string,
  mealTime: string,
  targetCalories?: number | null,
  mealType?: string
): Promise<ManageMealSlotResponse> => {
  const requestData: any = {
    action: 'create',
    draftId,
    day,
    mealName,
    mealTime,
  };
  
  // Include targetCalories as null if not provided, or as the value if provided
  if (targetCalories !== null && targetCalories !== undefined && targetCalories > 0) {
    requestData.targetCalories = targetCalories;
  } else {
    requestData.targetCalories = null;
  }
  if (mealType && mealType.trim()) {
    requestData.mealType = mealType.trim();
  }
  
  return manageMealSlot(requestData);
};

export const updateMealSlot = async (
  draftId: string,
  day: number,
  mealName: string,
  mealTime: string,
  targetCalories?: number | null,
  mealTypeOrOptions?: string | { mealType?: string; newMealName?: string; currentMealTime?: string; currentMealName?: string }
): Promise<ManageMealSlotResponse> => {
  const currentMealName =
    typeof mealTypeOrOptions === 'string'
      ? mealName
      : (mealTypeOrOptions?.currentMealName?.trim() || mealName);
  const currentMealTime =
    typeof mealTypeOrOptions === 'string'
      ? mealTime
      : (mealTypeOrOptions?.currentMealTime?.trim() || mealTime);
  const updatedMealTime = mealTime?.trim() || currentMealTime;
  const requestData: any = {
    action: 'update',
    draftId,
    day,
    mealName: currentMealName,
    currentMealTime,
    mealTime: updatedMealTime,
  };
  
  // Include targetCalories as null if not provided, or as the value if provided
  if (targetCalories !== null && targetCalories !== undefined && targetCalories > 0) {
    requestData.targetCalories = targetCalories;
  } else {
    requestData.targetCalories = null;
  }
  const mealType =
    typeof mealTypeOrOptions === 'string'
      ? mealTypeOrOptions
      : mealTypeOrOptions?.mealType;
  if (mealType && mealType.trim()) {
    requestData.mealType = mealType.trim();
  }
  const newMealName =
    typeof mealTypeOrOptions === 'string'
      ? undefined
      : mealTypeOrOptions?.newMealName;
  if (newMealName && newMealName.trim()) {
    requestData.newMealName = newMealName.trim();
  }
  
  return manageMealSlot(requestData);
};

export const deleteMealSlot = async (
  draftId: string,
  day: number,
  mealName: string,
  options: DeleteMealSlotOptions = {}
): Promise<ManageMealSlotResponse> => {
  return manageMealSlot({
    action: 'delete',
    draftId,
    day,
    mealName,
    ...options,
  });
};

export const reorderManualMealSlots = async (
  draftId: string,
  day: number,
  orderedMealOrders: number[]
): Promise<ManageMealSlotResponse> => {
  return manageMealSlot({
    action: 'reorder',
    draftId,
    day,
    orderedMealOrders,
  });
};

export const reorderMealPlanMeals = async (
  mealPlanId: string,
  body: { day: number; orderedMealOrders: number[] }
): Promise<{ success: boolean; message?: string; data?: unknown }> => {
  const response = await api.put(`/meal-plans/${mealPlanId}/reorder-meals`, body);
  return response.data;
};

export const swapMealPlanMeals = async (
  mealPlanOrDraftId: string,
  body: {
    day: number;
    firstMealName?: string;
    secondMealName?: string;
    firstMealOrder?: number;
    secondMealOrder?: number;
  }
): Promise<{ success: boolean; message?: string; data?: unknown }> => {
  const response = await api.post(`/meal-plans/${mealPlanOrDraftId}/swap-meals`, body);
  return response.data;
};

export type GenericClinicalTag = {
  id?: string;
  key?: string;
  value?: string;
  label?: string;
  name?: string;
  [key: string]: unknown;
};

export type GenericMealPlanSlotRequest = {
  mealName: string;
  mealTime: string;
  mealType: string;
  targetCalories?: number | null;
};

export type GenericPlanCalorieTargetMode = 'direct' | 'reference_person';

export type GenericPlanReferencePerson = {
  ageYears: number;
  gender: 'male' | 'female';
  weightKg: number;
  heightCm: number;
  activityLevel: string;
  bmrFormulaId?: 'iom';
  weightGoal?:
    | 'aggressive_loss'
    | 'moderate_loss'
    | 'mild_loss'
    | 'maintain'
    | 'mild_gain'
    | 'moderate_gain'
    | 'aggressive_gain';
  customCalorieAdjustment?: number | null;
};

export type GenericPlanCalorieTarget = {
  mode: GenericPlanCalorieTargetMode;
  dailyCalories?: number;
  referencePerson?: GenericPlanReferencePerson;
  calculation?: Record<string, unknown>;
};

export type CohortIntakeMessage = {
  role: 'user' | 'assistant';
  content: string;
};

export type CohortIntakeDetails = {
  planName?: string | null;
  startDate?: string | null;
  age: number | null;
  gender: string | null;
  medicalConditions: string[] | null;
  allergies: string[] | null;
  intolerances?: string[] | null;
  cuisineTypes?: string[] | null;
  dietaryPreferences?: string[] | null;
  cityOrCounty?: string | null;
  country?: string | null;
  pregnancyTrimester?: string | null;
  activityLevel?: string | null;
  bmi?: number | null;
  bmiCategory?: string | null;
  mealPlanPattern: string | null;
  durationDays?: number | null;
  mealSlots: GenericMealPlanSlotRequest[] | null;
  targetCalories?: number | null;
  targetProtein?: { min: number; max: number } | null;
  targetCarbs?: { min: number; max: number } | null;
  targetFat?: { min: number; max: number } | null;
  clinicalTags?: string[];
  additionalInstructions?: string[];
};

export type CohortIntakeResult = {
  assistantMessage: string;
  details: CohortIntakeDetails;
  missingFields: string[];
  ready: boolean;
  finalPrompt: string | null;
  model: string;
};

export type GenericMealPlanSummary = {
  id: string;
  clientId?: string;
  name?: string;
  description?: string;
  clinicalTags?: string[];
  availableClinicalTags?: Array<{ id?: string; value?: string; label?: string; name?: string }>;
  allergens?: string[];
  intoleranceTags?: string[];
  mealsCount?: number;
  mealSlots?: Array<Record<string, unknown>>;
  calories?: number;
  totalCalories?: number;
  targetCalories?: number;
  calorieTarget?: GenericPlanCalorieTarget | null;
  totals?: Record<string, unknown>;
  createdAt?: string;
  updatedAt?: string;
  [key: string]: unknown;
};

export type GenericMealPlanDetails = GenericMealPlanSummary & {
  mealSlots?: Array<Record<string, unknown>>;
  meals?: Array<Record<string, unknown>>;
  days?: Array<Record<string, unknown>>;
  daySuggestions?: Array<Record<string, unknown>>;
  /** Per-day rollup; some APIs return a single object instead of an array. */
  dailyNutrition?: Array<Record<string, unknown>> | Record<string, unknown>;
  targetDaily?: Record<string, unknown>;
  nutritionSummary?: Record<string, unknown>;
  notes?: string;
  planNotes?: string;
  promptProgress?: DraftGenerationPromptProgress | null;
  blueprintSelection?: DraftGenerationBlueprintSelection | null;
};

export type CreateGenericMealPlanRequest = {
  clientId?: string;
  name: string;
  description?: string;
  clinicalTags?: string[];
  calorieTarget?: GenericPlanCalorieTarget;
  mealSlots?: GenericMealPlanSlotRequest[];
};

export type UpdateGenericMealPlanRequest = {
  name?: string;
  description?: string;
  clinicalTags?: string[];
  calorieTarget?: GenericPlanCalorieTarget;
};

export type GenericMealSlotRequest =
  | ({
      genericPlanId: string;
      action: 'create';
    } & GenericMealPlanSlotRequest)
  | {
      genericPlanId: string;
      action: 'update';
      mealName: string;
      newMealName?: string;
      mealTime?: string;
      mealType?: string;
      targetCalories?: number | null;
    }
  | {
      genericPlanId: string;
      action: 'delete';
      mealName: string;
    };

export type GenericMealPlanRecipeRef = {
  id: string;
  provider: string;
  source?: string;
  nutritionServings?: number;
  portionSizeMultiplier?: number;
  selectedServingId?: string;
  optionGroupId?: string | null;
  optionGroupLabel?: string | null;
  selectedOptionGroupId?: string | null;
};

export type AddRecipeToGenericMealPlanRequest = {
  genericPlanId: string;
  mealName: string;
  recipe: GenericMealPlanRecipeRef;
  nutritionServings?: number;
  portionSizeMultiplier?: number;
  selectedServingId?: string;
  optionGroupId?: string | null;
  optionGroupLabel?: string | null;
  selectedOptionGroupId?: string | null;
};

export type AddRecipesToGenericMealPlanRequest = {
  genericPlanId: string;
  mealName: string;
  recipes: GenericMealPlanRecipeRef[];
};

export type AddGenericMealPlanItemRequest = {
  genericPlanId: string;
  mealName: string;
  itemType: 'recipe' | 'ingredient';
  recipe?: GenericMealPlanRecipeRef;
  ingredient?: {
    id: string;
    provider: string;
    amount: number;
    servingId: string;
  };
  nutritionServings?: number;
  portionSizeMultiplier?: number;
  selectedServingId?: string;
  optionGroupId?: string | null;
  optionGroupLabel?: string | null;
  selectedOptionGroupId?: string | null;
};

export type RemoveGenericMealPlanItemRequest = {
  genericPlanId: string;
  mealName: string;
  itemType: 'recipe' | 'ingredient';
  itemId: string;
};

export type UpdateGenericMealPlanServingsRequest = {
  genericPlanId: string;
  mealName: string;
  recipeId: string;
  nutritionServings: number;
  portionSizeMultiplier?: number;
  selectedServingId?: string;
};

export type UpdateGenericMealPlanIngredientRequest = {
  genericPlanId: string;
  mealName: string;
  itemId?: string;
  ingredientId: string;
  amount: number;
  servingId: string;
};

export type UpdateGenericMealPlanItemRequest = {
  genericPlanId: string;
  mealName: string;
  itemId: string;
  nutritionServings?: number;
  servings?: number;
  selectedServingId?: string;
  portionSizeId?: string;
  portionSizeMultiplier?: number;
  servingId?: string;
  notes?: string;
  optionGroupId?: string | null;
  optionGroupLabel?: string | null;
  selectedOptionGroupId?: string | null;
};

export type UpdateGenericMealPlanNotesRequest =
  | {
      genericPlanId: string;
      level: 'plan';
      notes: string;
    }
  | {
      genericPlanId: string;
      level: 'meal';
      mealName: string;
      notes: string;
    }
  | {
      genericPlanId: string;
      level: 'item';
      itemId: string;
      notes: string;
    };

export type ImportGenericMealPlanRequest = {
  genericPlanId: string;
  draftId: string;
  day: number;
  dayName?: string;
};

export type ImportExistingGenericMealPlanRequest = {
  sourceGenericPlanId: string;
  dayName?: string | null;
};

export type MealPlanDateConflict = {
  id: string;
  name: string;
  startDate: string | null;
  endDate: string | null;
  status?: string;
};

export type AssignGenericMealPlanRequest = {
  genericPlanId: string;
  clientId: string;
  startDate: string;
  endDate: string;
  overwriteConflicts?: boolean;
};

type GenericApiResponse<T = unknown> = {
  success: boolean;
  data?: T;
  message?: string;
};

const readGenericResponseSuccess = (payload: unknown): boolean => {
  if (payload && typeof payload === 'object' && 'success' in payload) {
    return (payload as { success?: boolean }).success !== false;
  }
  return true;
};

const getRecord = (value: unknown): Record<string, unknown> | undefined => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  return value as Record<string, unknown>;
};

const readArrayByKeys = (payload: unknown, keys: string[]): unknown[] => {
  if (Array.isArray(payload)) return payload;
  const record = getRecord(payload);
  if (!record) return [];

  for (const key of keys) {
    const value = record[key];
    if (Array.isArray(value)) return value;
  }

  const nestedData = record.data;
  if (Array.isArray(nestedData)) return nestedData;

  const nestedRecord = getRecord(nestedData);
  if (!nestedRecord) return [];

  for (const key of keys) {
    const value = nestedRecord[key];
    if (Array.isArray(value)) return value;
  }

  return [];
};

const readRecordByKeys = (payload: unknown, keys: string[]): Record<string, unknown> | undefined => {
  const record = getRecord(payload);
  if (!record) return undefined;

  for (const key of keys) {
    const value = getRecord(record[key]);
    if (value) return value;
  }

  const nestedRecord = getRecord(record.data);
  if (nestedRecord) {
    for (const key of keys) {
      const value = getRecord(nestedRecord[key]);
      if (value) return value;
    }
    return nestedRecord;
  }

  return record;
};

/** True when a record looks like a generic plan document (not just `{ success, message }`). */
const looksLikeGenericMealPlanDetail = (rec: Record<string, unknown>): boolean =>
  typeof rec.id === 'string'
  || Array.isArray(rec.mealSlots)
  || Array.isArray(rec.meals)
  || Array.isArray(rec.days)
  || Array.isArray(rec.daySuggestions)
  || Array.isArray(rec.dailyNutrition)
  || (rec.dailyNutrition != null && typeof rec.dailyNutrition === 'object' && !Array.isArray(rec.dailyNutrition));

/**
 * Normalizes GET/POST `/meal-plans/generic/...` bodies where the plan may live under
 * `data`, `genericPlan`, `genericMealPlan`, `plan`, or at the root (dev-api varies).
 */
const readGenericMealPlanDetailPayload = (payload: unknown): GenericMealPlanDetails | undefined => {
  const nestedKeys = [
    'genericPlan',
    'genericMealPlan',
    'plan',
    'mealPlan',
    'mealPlanDetails',
    'result',
    'payload',
    'entity',
  ];

  const fromKeys = readRecordByKeys(payload, nestedKeys);
  if (fromKeys && looksLikeGenericMealPlanDetail(fromKeys)) {
    return fromKeys as GenericMealPlanDetails;
  }

  const record = getRecord(payload);
  if (record && looksLikeGenericMealPlanDetail(record)) {
    return record as GenericMealPlanDetails;
  }

  const dataRec = getRecord(record?.data);
  if (dataRec && looksLikeGenericMealPlanDetail(dataRec)) {
    return dataRec as GenericMealPlanDetails;
  }

  if (fromKeys) {
    return fromKeys as GenericMealPlanDetails;
  }

  return undefined;
};

const wrapGenericMealPlanMutationResponse = (
  payload: unknown,
): GenericApiResponse<GenericMealPlanDetails> => {
  const record = getRecord(payload);
  return {
    ...(record ?? {}),
    success: readGenericResponseSuccess(payload),
    data: readGenericMealPlanDetailPayload(payload),
  };
};

export const getGenericClinicalTags = async (): Promise<GenericApiResponse<GenericClinicalTag[]>> => {
  const response = await api.get('/meal-plans/generic/clinical-tags');
  const payload = response.data;
  return {
    ...payload,
    success: readGenericResponseSuccess(payload),
    data: readArrayByKeys(payload, ['clinicalTags', 'tags', 'items']) as GenericClinicalTag[],
  };
};

export const createGenericMealPlan = async (
  body: CreateGenericMealPlanRequest,
): Promise<GenericApiResponse<GenericMealPlanDetails>> => {
  const response = await api.post('/meal-plans/generic/create', body);
  return wrapGenericMealPlanMutationResponse(response.data);
};

export const continueCohortMealPlanIntake = async (
  messages: CohortIntakeMessage[],
): Promise<GenericApiResponse<CohortIntakeResult>> => {
  const response = await api.post('/meal-plans/generic/intake/chat', { messages });
  return response.data;
};

export const generateCohortMealPlan = async (
  body: CohortIntakeDetails & { finalPrompt: string },
): Promise<GenericApiResponse<{
  id?: string;
  genericPlanId?: string;
  name?: string;
  days?: number;
  generationState?: 'queued' | 'in_progress' | 'completed' | 'failed';
}>> => {
  const response = await api.post('/meal-plans/generic/generate-ai', body);
  return response.data;
};

export const smartEditCohortMealSlot = async (
  genericPlanId: string,
  body: { dayNumber: number; mealName: string; instruction: string },
): Promise<GenericApiResponse<{ suggestionsCount?: number }>> => {
  const response = await api.post(
    `/meal-plans/generic/${encodeURIComponent(genericPlanId)}/smart-edit`,
    body,
  );
  const payload = response.data as GenericApiResponse<{
    suggestionsCount?: number;
    smartEditState?: 'queued' | 'in_progress' | 'completed' | 'failed';
  }>;
  const initialState = payload?.data?.smartEditState;
  if (response.status !== 202 && initialState !== 'queued' && initialState !== 'in_progress') {
    return payload;
  }

  const pollIntervalMs = 5_000;
  const maxPollAttempts = 120;
  for (let attempt = 0; attempt < maxPollAttempts; attempt += 1) {
    await new Promise<void>((resolve) => {
      setTimeout(resolve, pollIntervalMs);
    });
    const statusResponse = await api.get(
      `/meal-plans/generic/${encodeURIComponent(genericPlanId)}/smart-edit/status`,
    );
    const statusPayload = statusResponse.data as GenericApiResponse<{
      smartEditState?: 'queued' | 'in_progress' | 'completed' | 'failed';
      smartEditError?: string | null;
    }>;
    const state = statusPayload?.data?.smartEditState;
    if (state === 'completed') {
      return {
        success: true,
        message: 'Cohort meal slot smart edited successfully',
        data: {},
      };
    }
    if (state === 'failed') {
      throw new Error(statusPayload.data?.smartEditError || 'Unable to smart edit this meal slot');
    }
  }

  throw new Error('Smart edit is taking longer than expected. Please try again.');
};

export const listGenericMealPlans = async (params: {
  clientId?: string;
  search?: string;
  page?: number;
  pageSize?: number;
  limit?: number;
} = {}): Promise<
  GenericApiResponse<{ genericPlans: GenericMealPlanSummary[]; pagination?: MealPlanTemplateListPagination }>
> => {
  const queryParams: Record<string, string | number> = {};
  if (params.clientId?.trim()) queryParams.clientId = params.clientId.trim();
  if (params.search?.trim()) queryParams.search = params.search.trim();
  if (params.page) queryParams.page = params.page;
  if (params.pageSize) {
    queryParams.pageSize = params.pageSize;
    queryParams.limit = params.limit ?? params.pageSize;
  } else if (params.limit) {
    queryParams.limit = params.limit;
  }

  const response = await api.get('/meal-plans/generic', {
    params: Object.keys(queryParams).length ? queryParams : undefined,
  });
  const payload = response.data;
  const root = getRecord(payload) ?? {};
  const dataRecord = getRecord((payload as { data?: unknown })?.data) ?? getRecord(payload) ?? {};
  const genericPlans = readArrayByKeys(payload, ['genericPlans', 'plans', 'items', 'templates']) as GenericMealPlanSummary[];
  const pagination = pickPaginationFromListPayload(root, dataRecord);
  return {
    ...payload,
    success: readGenericResponseSuccess(payload),
    data: {
      ...dataRecord,
      genericPlans,
      ...(pagination ? { pagination } : {}),
    },
  };
};

export const getGenericMealPlan = async (
  genericPlanId: string,
): Promise<GenericApiResponse<GenericMealPlanDetails>> => {
  const response = await api.get(`/meal-plans/generic/${genericPlanId}`);
  const payload = response.data;
  const data = readGenericMealPlanDetailPayload(payload);
  return {
    ...payload,
    success: readGenericResponseSuccess(payload),
    data,
  };
};

export const updateGenericMealPlan = async (
  genericPlanId: string,
  body: UpdateGenericMealPlanRequest,
): Promise<GenericApiResponse<GenericMealPlanDetails>> => {
  const response = await api.put(`/meal-plans/generic/${genericPlanId}`, body);
  return wrapGenericMealPlanMutationResponse(response.data);
};

export const deleteGenericMealPlan = async (
  genericPlanId: string,
): Promise<GenericApiResponse> => {
  const response = await api.delete(`/meal-plans/generic/${genericPlanId}`);
  return response.data;
};

export const manageGenericMealSlot = async (
  body: GenericMealSlotRequest,
): Promise<GenericApiResponse<GenericMealPlanDetails>> => {
  const response = await api.post('/meal-plans/generic/meal-slot', body);
  return wrapGenericMealPlanMutationResponse(response.data);
};

export const createGenericMealSlot = async (
  genericPlanId: string,
  mealName: string,
  mealTime: string,
  mealType: string,
  targetCalories?: number | null,
): Promise<GenericApiResponse<GenericMealPlanDetails>> =>
  manageGenericMealSlot({
    genericPlanId,
    action: 'create',
    mealName,
    mealTime,
    mealType,
    targetCalories,
  });

export const updateGenericMealSlot = async (
  genericPlanId: string,
  mealName: string,
  body: {
    newMealName?: string;
    mealTime?: string;
    mealType?: string;
    targetCalories?: number | null;
  },
): Promise<GenericApiResponse<GenericMealPlanDetails>> =>
  manageGenericMealSlot({
    genericPlanId,
    action: 'update',
    mealName,
    ...body,
  });

export const deleteGenericMealSlot = async (
  genericPlanId: string,
  mealName: string,
): Promise<GenericApiResponse<GenericMealPlanDetails>> =>
  manageGenericMealSlot({
    genericPlanId,
    action: 'delete',
    mealName,
  });

export const addRecipeToGenericMealPlan = async (
  body: AddRecipeToGenericMealPlanRequest,
): Promise<GenericApiResponse<GenericMealPlanDetails>> => {
  const response = await api.post('/meal-plans/generic/add-recipe', body);
  return wrapGenericMealPlanMutationResponse(response.data);
};

export const addRecipesToGenericMealPlan = async (
  body: AddRecipesToGenericMealPlanRequest,
): Promise<GenericApiResponse<GenericMealPlanDetails>> => {
  const response = await api.post('/meal-plans/generic/add-recipes', body);
  return wrapGenericMealPlanMutationResponse(response.data);
};

export const addGenericMealPlanItem = async (
  body: AddGenericMealPlanItemRequest,
): Promise<GenericApiResponse<GenericMealPlanDetails>> => {
  const response = await api.post('/meal-plans/generic/add-item', body);
  return wrapGenericMealPlanMutationResponse(response.data);
};

export const removeGenericMealPlanItem = async (
  body: RemoveGenericMealPlanItemRequest,
): Promise<GenericApiResponse> => {
  const response = await api.delete('/meal-plans/generic/remove-item', { data: body });
  return response.data;
};

export const updateGenericMealPlanServings = async (
  body: UpdateGenericMealPlanServingsRequest,
): Promise<GenericApiResponse<GenericMealPlanDetails>> => {
  const response = await api.put('/meal-plans/generic/update-servings', body);
  return wrapGenericMealPlanMutationResponse(response.data);
};

export const updateGenericMealPlanIngredient = async (
  body: UpdateGenericMealPlanIngredientRequest,
): Promise<GenericApiResponse<GenericMealPlanDetails>> => {
  const response = await api.put('/meal-plans/generic/update-ingredient', body);
  return wrapGenericMealPlanMutationResponse(response.data);
};

export const updateGenericMealPlanItem = async (
  body: UpdateGenericMealPlanItemRequest,
): Promise<GenericApiResponse<GenericMealPlanDetails>> => {
  const response = await api.put(`/meal-plans/generic/${encodeURIComponent(body.genericPlanId)}/update-item`, body);
  return wrapGenericMealPlanMutationResponse(response.data);
};

export const updateGenericMealPlanNotes = async (
  body: UpdateGenericMealPlanNotesRequest,
): Promise<GenericApiResponse<GenericMealPlanDetails>> => {
  const response = await api.put('/meal-plans/generic/notes', body);
  return wrapGenericMealPlanMutationResponse(response.data);
};

export const importGenericMealPlan = async (
  body: ImportGenericMealPlanRequest,
): Promise<GenericApiResponse> => {
  const response = await api.post('/meal-plans/generic/import', body);
  return response.data;
};

export const importExistingGenericMealPlan = async (
  targetGenericPlanId: string,
  body: ImportExistingGenericMealPlanRequest,
): Promise<GenericApiResponse<GenericMealPlanDetails>> => {
  const response = await api.post(`/meal-plans/generic/${encodeURIComponent(targetGenericPlanId)}/import`, body);
  return wrapGenericMealPlanMutationResponse(response.data);
};

export const assignGenericMealPlan = async (
  body: AssignGenericMealPlanRequest,
): Promise<GenericApiResponse<{ mealPlanId?: string; id?: string }>> => {
  const response = await api.post('/meal-plans/generic/assign', body);
  return response.data;
};

export type MealPlanTemplateKind = 'meal_plan' | 'meal_slot';
export type MealPlanTemplateVisibility = 'private' | 'shared_with_practice';

export type MealPlanTemplateSummary = {
  id: string;
  name?: string;
  templateName?: string;
  description?: string;
  durationDays?: number;
  startDate?: string;
  endDate?: string;
  generationType?: string;
  generationSource?: string;
  aiProvider?: string | null;
  templateKind?: MealPlanTemplateKind;
  isOwnTemplate?: boolean;
  canDelete?: boolean;
  canEdit?: boolean;
  suitableFor?: string;
  clinicalTags?: string[];
  allergens?: string[];
  intoleranceTags?: string[];
  visibility?: MealPlanTemplateVisibility | string;
  nutritionistId?: string;
  createdBy?: {
    id?: string;
    role?: string;
    fullName?: string;
    name?: string;
  } | null;
  tags?: string[];
  sourceClientId?: string;
  createdAt?: string;
  updatedAt?: string;
  calories?: number;
  protein?: number;
  carbs?: number;
  fat?: number;
  fiber?: number;
};

export type TemplateMacroTotals = {
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
  fiber?: number;
};

export type MealPlanTemplateItem = {
  id: string;
  /** Backend template detail uses `days[].meals[].items[].id` as the mutation itemId. */
  itemId?: string;
  itemType: 'recipe' | 'ingredient';
  recipeId?: string | null;
  ingredientId?: string | null;
  name: string;
  servings: number;
  servingId?: string | null;
  servingGrams?: number;
  nutritionServings?: number;
  portionSizeMultiplier?: number;
  servingSizeMultiplier?: number;
  sortOrder: number;
  notes?: string | null;
  allergens?: string[];
  intoleranceTags?: string[];
  nutrition: Record<string, number>;
  baseNutrition?: Record<string, number>;
  hasOverrides?: boolean;
  ingredientOverrides?: unknown[];
};

export type MealPlanTemplateMeal = {
  dayId: string;
  dayNumber: number;
  date?: string | null;
  mealId: string;
  mealName: string;
  mealType: string;
  mealOrder: number;
  time?: string | null;
  notes?: string | null;
  items: MealPlanTemplateItem[];
  totals: TemplateMacroTotals;
};

export type MealPlanTemplateDay = {
  dayId: string;
  dayNumber: number;
  date?: string | null;
  dayName?: string | null;
  notes?: string | null;
  meals: MealPlanTemplateMeal[];
  totals: TemplateMacroTotals;
};

/** One row from meal plan `data.importedTemplates` (import-template audit trail). */
export type ImportedMealPlanTemplateEntry = {
  templateId: string;
  templateName?: string;
  templateKind?: string;
  targetDay?: number;
  templateDay?: number;
  importedAt?: string;
  importedMeals?: number;
  importedItems?: number;
};

export type MealPlanTemplateDetails = {
  id: string;
  templateKind: MealPlanTemplateKind;
  templateName: string;
  name?: string;
  description?: string | null;
  suitableFor?: string | null;
  clinicalTags?: string[];
  allergens?: string[];
  intoleranceTags?: string[];
  visibility?: MealPlanTemplateVisibility | string;
  durationDays: number;
  startDate?: string | null;
  endDate?: string | null;
  generationType?: string;
  generationSource?: string;
  aiProvider?: string | null;
  tags?: string[];
  days: MealPlanTemplateDay[];
  meals: MealPlanTemplateMeal[];
  totals: TemplateMacroTotals;
  createdAt?: string;
  updatedAt?: string;
  /** When false, template detail is view-only for the current user. */
  canEdit?: boolean;
  canDelete?: boolean;
  isOwnTemplate?: boolean;
  nutritionistId?: string;
  createdBy?: {
    id?: string;
    role?: string;
    fullName?: string;
    name?: string;
  } | null;
  /** Present when this template was built from imported sources (API-specific). */
  importedTemplates?: ImportedMealPlanTemplateEntry[];
};

export type ListMealPlanTemplatesParams = {
  page?: number;
  pageSize?: number;
  search?: string;
  /** Selects GET /meal-plans/templates/meal-plans vs /meal-plans/templates/meal-slots. */
  templateKind: MealPlanTemplateKind;
};

/** Pagination object returned with GET .../templates/meal-plans and .../meal-slots (field names may vary). */
export type MealPlanTemplateListPagination = {
  page?: number;
  pageSize?: number;
  limit?: number;
  perPage?: number;
  total?: number;
  totalCount?: number;
  totalItems?: number;
  count?: number;
  total_pages?: number;
  totalPages?: number;
  pages?: number;
  pageCount?: number;
  hasNextPage?: boolean;
  hasPreviousPage?: boolean;
  [key: string]: unknown;
};

export type ListMealPlanTemplatesResponse = {
  success: boolean;
  data?: {
    templates: MealPlanTemplateSummary[];
    pagination?: MealPlanTemplateListPagination;
  };
  message?: string;
};

function asObjectRecord(value: unknown): Record<string, unknown> | null {
  if (value == null || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function pickTemplatesArrayFromListPayload(envelope: Record<string, unknown>): unknown[] {
  const candidates = [
    envelope.templates,
    envelope.items,
    envelope.results,
    envelope.rows,
    envelope.mealPlans,
    envelope.mealSlots,
    envelope.mealPlanTemplates,
    envelope.mealSlotTemplates,
    envelope.data,
  ];
  for (const c of candidates) {
    if (Array.isArray(c)) return c;
  }
  return [];
}

function pickPaginationFromListPayload(
  root: Record<string, unknown>,
  envelope: Record<string, unknown>,
): MealPlanTemplateListPagination | undefined {
  const direct =
    asObjectRecord(envelope.pagination)
    ?? asObjectRecord(envelope.meta)
    ?? asObjectRecord(root.pagination);
  if (direct) return direct as MealPlanTemplateListPagination;

  const lifted: Record<string, unknown> = {};
  for (const key of [
    'total',
    'totalCount',
    'totalItems',
    'count',
    'page',
    'pageSize',
    'limit',
    'perPage',
    'totalPages',
    'pages',
    'pageCount',
    'total_pages',
  ] as const) {
    const v = envelope[key] ?? root[key];
    if (v != null) lifted[key] = v;
  }
  return Object.keys(lifted).length ? (lifted as MealPlanTemplateListPagination) : undefined;
}

function normalizeTemplateSummaryCreatedBy(value: unknown): MealPlanTemplateSummary['createdBy'] {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const cb = value as Record<string, unknown>;
  const id = String(cb.id ?? '').trim() || undefined;
  const fullName = typeof cb.fullName === 'string' ? cb.fullName.trim() : undefined;
  const name = typeof cb.name === 'string' ? cb.name.trim() : undefined;
  const role = typeof cb.role === 'string' ? cb.role.trim() : undefined;
  if (!id && !fullName && !name) return undefined;
  const resolvedName = name || fullName;
  return {
    ...(id ? { id } : {}),
    ...(role ? { role } : {}),
    ...(fullName ? { fullName } : {}),
    ...(resolvedName ? { name: resolvedName } : {}),
  };
}

/** Coerce one list row into `MealPlanTemplateSummary` (supports `_id`, nested names, etc.). */
function normalizeMealPlanTemplateSummaryRow(entry: unknown): MealPlanTemplateSummary {
  const o = asObjectRecord(entry) ?? {};
  const id = String(o.id ?? o._id ?? o.templateId ?? '').trim();
  const templateName =
    typeof o.templateName === 'string'
      ? o.templateName
      : typeof o.template_name === 'string'
        ? o.template_name
        : undefined;
  const name = typeof o.name === 'string' ? o.name : templateName;
  const base = o as MealPlanTemplateSummary;
  const createdBy = normalizeTemplateSummaryCreatedBy(o.createdBy);
  return {
    ...base,
    id,
    ...(name != null ? { name } : {}),
    ...(templateName != null ? { templateName } : {}),
    allergens: normalizeStringArray(o.allergens),
    intoleranceTags: normalizeStringArray(o.intoleranceTags),
    ...(createdBy !== undefined ? { createdBy } : {}),
  };
}

/**
 * Normalizes list responses from GET /meal-plans/templates/meal-plans and .../meal-slots
 * (envelope `data`, alternate array keys, flat totals, Mongo `_id`).
 */
export function normalizeListMealPlanTemplatesResponse(raw: unknown): ListMealPlanTemplatesResponse {
  const root = asObjectRecord(raw);
  if (!root) {
    return { success: false, message: 'Invalid templates list response', data: { templates: [] } };
  }

  if (Array.isArray(root.data)) {
    const templates = root.data
      .map(normalizeMealPlanTemplateSummaryRow)
      .filter((t) => Boolean(t.id?.trim()));
    const paginationFlat = pickPaginationFromListPayload(root, root);
    return {
      success: typeof root.success === 'boolean' ? root.success : true,
      message: typeof root.message === 'string' ? root.message : undefined,
      data: {
        templates,
        ...(paginationFlat ? { pagination: paginationFlat } : {}),
      },
    };
  }

  const dataBlock = asObjectRecord(root.data);
  const envelope = dataBlock ?? root;
  const rawList = pickTemplatesArrayFromListPayload(envelope);
  const templates = rawList
    .map(normalizeMealPlanTemplateSummaryRow)
    .filter((t) => Boolean(t.id?.trim()));

  const pagination = pickPaginationFromListPayload(root, envelope);

  const success = typeof root.success === 'boolean' ? root.success : true;

  return {
    success,
    message: typeof root.message === 'string' ? root.message : undefined,
    data: {
      templates,
      ...(pagination ? { pagination } : {}),
    },
  };
}

export type GetMealPlanTemplateResponse = {
  success: boolean;
  data?: MealPlanTemplateDetails;
  message?: string;
};

export type SaveFullMealPlanTemplateRequest = {
  templateName: string;
  description?: string;
  suitableFor?: string;
  tags?: string[];
  visibility?: MealPlanTemplateVisibility;
  dayNumber?: number;
};

export type SaveMealSlotTemplateRequest = {
  /** Plan day index — disambiguates meal slot when the same meal exists on multiple days. */
  day?: number;
  dayNumber?: number;
  mealId?: string;
  mealOrder?: number;
  mealName?: string;
  mealType?: string;
  templateName: string;
  description?: string;
  suitableFor?: string;
  clinicalTags?: string[];
  visibility?: MealPlanTemplateVisibility;
  overwrite?: boolean;
};

export type SaveMealPlanTemplateResponse = {
  success: boolean;
  data?: Record<string, unknown>;
  message?: string;
};

export type UseMealPlanTemplateRequest = {
  newStartDate: string;
  clientId?: string;
  name?: string;
};

export type UseMealPlanTemplateResponse = {
  success: boolean;
  data?: Record<string, unknown>;
  message?: string;
};

export type ImportMealPlanTemplateRequest = {
  templateId: string;
  day: number;
  templateDay: number;
};

export type ImportMealPlanTemplateResponse = {
  success: boolean;
  data?: Record<string, unknown> & {
    importedTemplates?: ImportedMealPlanTemplateEntry[];
  };
  message?: string;
};

/** Unique `templateId` values from `importedTemplates` on plan payloads (generic, manual, draft, finalized, template detail). */
export function collectImportedTemplateIdsFromPlanRecords(
  ...records: Array<Record<string, unknown> | null | undefined>
): string[] {
  const ids = new Set<string>();
  for (const source of records) {
    if (!source) continue;
    const raw = source.importedTemplates;
    if (!Array.isArray(raw)) continue;
    for (const entry of raw) {
      if (!entry || typeof entry !== 'object') continue;
      const o = entry as Record<string, unknown>;
      const id = String(o.templateId ?? o._id ?? '').trim();
      if (id) ids.add(id);
    }
  }
  return Array.from(ids);
}

export type UpdateMealPlanTemplateRequest = {
  /**
   * Frontend-only guard: meal-slot templates must remain one-day templates.
   * This field is stripped before the API request is sent.
   */
  templateKind?: MealPlanTemplateKind;
  templateName?: string;
  description?: string | null;
  suitableFor?: string | null;
  clinicalTags?: string[];
  visibility?: MealPlanTemplateVisibility;
  tags?: string[];
  startDate?: string;
  days?: Array<{
    dayNumber?: number;
    date?: string | null;
    dayName?: string | null;
    notes?: string | null;
    meals?: Array<{
      mealId?: string;
      mealName?: string;
      mealType?: string;
      mealOrder?: number;
      sortOrder?: number;
      time?: string | null;
      notes?: string | null;
      items?: Array<{
        itemId?: string;
        itemType?: 'recipe' | 'ingredient';
        recipeId?: string | null;
        ingredientId?: string | null;
        name?: string;
        servings?: number;
        servingId?: string | null;
        servingGrams?: number;
        nutritionServings?: number;
        portionSizeMultiplier?: number;
        servingSizeMultiplier?: number;
        nutrition?: Record<string, number>;
        notes?: string | null;
      }>;
    }>;
  }>;
  meals?: Array<{
    mealId?: string;
    mealName?: string;
    mealType?: string;
    mealOrder?: number;
    sortOrder?: number;
    time?: string | null;
    notes?: string | null;
    items?: Array<{
      itemId?: string;
      itemType?: 'recipe' | 'ingredient';
      recipeId?: string | null;
      ingredientId?: string | null;
      name?: string;
      servings?: number;
      servingId?: string | null;
      servingGrams?: number;
      nutritionServings?: number;
      portionSizeMultiplier?: number;
      servingSizeMultiplier?: number;
      nutrition?: Record<string, number>;
      notes?: string | null;
    }>;
  }>;
};

export type UpdateMealPlanTemplateResponse = {
  success: boolean;
  data?: MealPlanTemplateDetails;
  message?: string;
};

const removeUndefinedValues = <T extends object>(payload: T): Partial<T> =>
  Object.fromEntries(Object.entries(payload).filter(([, value]) => value !== undefined)) as Partial<T>;

const normalizeMealPlanTemplateDetails = (
  detail: MealPlanTemplateDetails | undefined,
): MealPlanTemplateDetails | undefined => {
  if (!detail) return detail;
  const normalizeItem = (item: MealPlanTemplateItem): MealPlanTemplateItem => {
    const canonicalItemId = String(item.itemId ?? item.id ?? '').trim();
    const canonicalId = String(item.id ?? item.itemId ?? '').trim();
    return {
      ...item,
      id: canonicalId,
      itemId: canonicalItemId,
      itemType: item.itemType === 'ingredient' ? 'ingredient' : 'recipe',
      allergens: normalizeStringArray(item.allergens),
      intoleranceTags: normalizeStringArray(item.intoleranceTags),
    };
  };
  const normalizeMeal = (meal: MealPlanTemplateMeal): MealPlanTemplateMeal => ({
    ...meal,
    items: Array.isArray(meal.items) ? meal.items.map(normalizeItem) : [],
  });
  const days = Array.isArray(detail.days)
    ? detail.days.map((day) => ({
        ...day,
        meals: Array.isArray(day.meals) ? day.meals.map(normalizeMeal) : [],
      }))
    : [];
  return {
    ...detail,
    allergens: normalizeStringArray(detail.allergens),
    intoleranceTags: normalizeStringArray(detail.intoleranceTags),
    days,
    meals: Array.isArray(detail.meals) ? detail.meals.map(normalizeMeal) : [],
  };
};

const prepareUpdateMealPlanTemplateBody = (
  body: UpdateMealPlanTemplateRequest,
): Partial<Omit<UpdateMealPlanTemplateRequest, 'templateKind'>> => {
  const { templateKind, ...apiBody } = body;
  const days = Array.isArray(apiBody.days)
    ? (templateKind === 'meal_slot' ? apiBody.days.slice(0, 1) : apiBody.days)
    : apiBody.days;

  return removeUndefinedValues({
    ...apiBody,
    days,
  });
};

export const listMealPlanTemplates = async (
  params: ListMealPlanTemplatesParams,
): Promise<ListMealPlanTemplatesResponse> => {
  const kind = params.templateKind === 'meal_slot' ? 'meal_slot' : 'meal_plan';
  const path =
    kind === 'meal_slot'
      ? '/meal-plans/templates/meal-slots'
      : '/meal-plans/templates/meal-plans';
  const response = await api.get(path, {
    params: removeUndefinedValues({
      page: params.page,
      pageSize: params.pageSize,
      search: params.search?.trim() || undefined,
    }),
  });
  return normalizeListMealPlanTemplatesResponse(response.data);
};

/** Legacy combined template search endpoint used by add-day import picker. */
export const searchMealPlanTemplates = async (params: {
  search?: string;
  page?: number;
  pageSize?: number;
}): Promise<ListMealPlanTemplatesResponse> => {
  const response = await api.get('/meal-plans/templates', {
    params: removeUndefinedValues({
      search: params.search?.trim() || undefined,
      page: params.page,
      pageSize: params.pageSize,
    }),
  });
  return normalizeListMealPlanTemplatesResponse(response.data);
};

export const getMealPlanTemplate = async (
  templateId: string,
): Promise<GetMealPlanTemplateResponse> => {
  const response = await api.get(`/meal-plans/templates/${templateId}`, uncachedGetConfig());
  return {
    ...response.data,
    data: normalizeMealPlanTemplateDetails(response.data?.data),
  };
};

export const updateMealPlanTemplate = async (
  templateId: string,
  body: UpdateMealPlanTemplateRequest,
): Promise<UpdateMealPlanTemplateResponse> => {
  const response = await api.put(
    `/meal-plans/templates/${templateId}`,
    prepareUpdateMealPlanTemplateBody(body),
  );
  return {
    ...response.data,
    data: normalizeMealPlanTemplateDetails(response.data?.data),
  };
};

export const saveFinalizedMealPlanAsTemplate = async (
  mealPlanId: string,
  body: SaveFullMealPlanTemplateRequest
): Promise<SaveMealPlanTemplateResponse> => {
  const response = await api.post(
    `/meal-plans/${mealPlanId}/template`,
    removeUndefinedValues({
      templateName: body.templateName,
      description: body.description,
      suitableFor: body.suitableFor,
      tags: body.tags,
      visibility: body.visibility,
      dayNumber: body.dayNumber,
    })
  );
  return response.data;
};

export const saveDraftMealPlanAsTemplate = async (
  draftId: string,
  body: SaveFullMealPlanTemplateRequest
): Promise<SaveMealPlanTemplateResponse> => {
  const response = await api.post(
    `/meal-plans/drafts/${draftId}/template`,
    removeUndefinedValues({
      templateName: body.templateName,
      description: body.description,
      suitableFor: body.suitableFor,
      tags: body.tags,
      visibility: body.visibility,
      dayNumber: body.dayNumber,
    })
  );
  return response.data;
};

export const saveFinalizedMealSlotAsTemplate = async (
  mealPlanId: string,
  body: SaveMealSlotTemplateRequest
): Promise<SaveMealPlanTemplateResponse> => {
  const response = await api.post(
    `/meal-plans/${mealPlanId}/meal-slot/template`,
    removeUndefinedValues(body)
  );
  return response.data;
};

export const saveDraftMealSlotAsTemplate = async (
  draftId: string,
  body: SaveMealSlotTemplateRequest
): Promise<SaveMealPlanTemplateResponse> => {
  const response = await api.post(
    `/meal-plans/drafts/${draftId}/meal-slot/template`,
    removeUndefinedValues(body)
  );
  return response.data;
};

export const useMealPlanTemplate = async (
  templateId: string,
  body: UseMealPlanTemplateRequest
): Promise<UseMealPlanTemplateResponse> => {
  const response = await api.post(
    `/meal-plans/templates/${templateId}/use`,
    removeUndefinedValues({
      newStartDate: body.newStartDate,
      clientId: body.clientId,
      name: body.name,
    })
  );
  return response.data;
};

export const importMealPlanTemplate = async (
  mealPlanId: string,
  body: ImportMealPlanTemplateRequest
): Promise<ImportMealPlanTemplateResponse> => {
  const response = await api.post(
    `/meal-plans/${mealPlanId}/import-template`,
    removeUndefinedValues({
      templateId: body.templateId,
      day: body.day,
      templateDay: body.templateDay,
    }),
  );
  return response.data;
};

export type MealPlanTemplateAddItemRequest = AddFinalizedMealPlanItemRequest & {
  mealName: string;
  itemType: 'recipe' | 'ingredient';
};

export type MealPlanTemplateRemoveItemRequest = {
  itemId: string;
};

/**
 * PUT /meal-plans/{id}/notes — works for draft IDs and finalized meal plan IDs.
 * Prefer this unified endpoint for practitioner annotations at all 4 levels.
 */
export type UpdateMealPlanNotesRequest =
  | {
      level: 'plan';
      notes?: string | null;
      tags?: string[];
    }
  | {
      level: 'day';
      day: number;
      dayName?: string | null;
      notes?: string | null;
    }
  | {
      level: 'meal';
      day: number;
      mealName: string;
      notes?: string | null;
    }
  | {
      /** Finalized item notes — use meal plan item Mongo `_id`. */
      level: 'item';
      itemId: string;
      notes?: string | null;
    }
  | {
      /** Draft item notes — recipeId + day + mealName required. */
      level: 'item';
      day: number;
      mealName: string;
      recipeId: string;
      notes?: string | null;
      itemId?: never;
    };

/** @deprecated Prefer UpdateMealPlanNotesRequest — same shape / endpoint. */
export type UpdateMealPlanTemplateNotesRequest = UpdateMealPlanNotesRequest;

export const updateMealPlanNotes = async (
  mealPlanId: string,
  request: UpdateMealPlanNotesRequest,
): Promise<{ success: boolean; data?: unknown; message?: string }> => {
  const response = await api.put(`/meal-plans/${mealPlanId}/notes`, removeUndefinedValues(request));
  return response.data;
};

export const addMealPlanTemplateItem = async (
  templateId: string,
  request: MealPlanTemplateAddItemRequest,
): Promise<{ success: boolean; data?: any; message?: string }> => {
  const response = await api.post(`/meal-plans/${templateId}/add-item`, removeUndefinedValues(request));
  return response.data;
};

export const updateMealPlanTemplateItem = async (
  templateId: string,
  request: UpdateFinalizedMealPlanItemRequest,
): Promise<{ success: boolean; data?: any; message?: string }> => {
  const response = await api.put(`/meal-plans/${templateId}/update-item`, removeUndefinedValues(request));
  return response.data;
};

export const customizeMealPlanTemplateItem = async (
  templateId: string,
  request: CustomizeFinalizedMealPlanItemRequest,
): Promise<{ success: boolean; data?: any; message?: string }> => {
  const response = await api.put(`/meal-plans/${templateId}/customize-item`, removeUndefinedValues(request));
  return response.data;
};

export const removeMealPlanTemplateItem = async (
  templateId: string,
  request: MealPlanTemplateRemoveItemRequest,
): Promise<{ success: boolean; data?: unknown; message?: string }> => {
  const response = await api.delete(`/meal-plans/${templateId}/remove-item`, { data: removeUndefinedValues(request) });
  return response.data;
};

export const updateMealPlanTemplateNotes = async (
  templateId: string,
  request: UpdateMealPlanTemplateNotesRequest,
): Promise<{ success: boolean; data?: unknown; message?: string }> =>
  updateMealPlanNotes(templateId, request);

export const createMealPlanTemplateMealSlot = async (
  templateId: string,
  day: number,
  mealName: string,
  mealTime: string,
  targetCalories?: number | null,
  mealType?: string,
): Promise<ManageMealSlotResponse> => createMealSlot(templateId, day, mealName, mealTime, targetCalories, mealType);

export const updateMealPlanTemplateMealSlot = async (
  templateId: string,
  day: number,
  currentMealName: string,
  updatedMealTime: string,
  targetCalories?: number | null,
  mealTypeOrOptions?: string | {
    mealType?: string;
    newMealName?: string;
    currentMealTime?: string;
  },
): Promise<ManageMealSlotResponse> => updateMealSlot(
  templateId,
  day,
  currentMealName,
  updatedMealTime,
  targetCalories,
  mealTypeOrOptions,
);

export const deleteMealPlanTemplateMealSlot = async (
  templateId: string,
  day: number,
  mealName: string,
  options?: DeleteMealSlotOptions,
): Promise<ManageMealSlotResponse> => deleteMealSlot(templateId, day, mealName, options);

export const reorderMealPlanTemplateMealSlots = async (
  templateId: string,
  day: number,
  orderedMealOrders: number[],
): Promise<ManageMealSlotResponse> => reorderManualMealSlots(templateId, day, orderedMealOrders);

export const copyMealPlanTemplateDay = async (
  templateId: string,
  body: { sourceDay: number; targetDays: number[] },
): Promise<{ success: boolean; data?: unknown; message?: string }> => {
  const response = await api.post(`/meal-plans/${templateId}/copy-day`, body);
  return response.data;
};

export const deleteMealPlanTemplateDay = async (
  templateId: string,
  dayNumber: number,
): Promise<{ success: boolean; data?: unknown; message?: string }> => {
  const response = await api.delete(`/meal-plans/${templateId}/day/${dayNumber}`);
  return response.data;
};

export const reorderMealPlanTemplateDays = async (
  templateId: string,
  body: { dayOrder: number[] },
): Promise<{ success: boolean; data?: unknown; message?: string }> => {
  const response = await api.put(`/meal-plans/${templateId}/reorder-days`, body);
  return response.data;
};

export const deleteMealPlanTemplate = async (
  templateId: string,
): Promise<{ success: boolean; message?: string }> => {
  const response = await api.delete(`/meal-plans/templates/${templateId}`);
  return response.data;
};

// Copy day in meal plan (draft/finalized) via unified endpoint
export const copyManualMealPlanDay = async (
  request: CopyManualMealPlanDayRequest
): Promise<CopyManualMealPlanDayResponse> => {
  try {
    const mealPlanId = request.mealPlanId || request.draftId;
    if (!mealPlanId) {
      throw new Error('mealPlanId is required');
    }

    const normalizedTargetDays = Array.isArray(request.targetDays)
      ? Array.from(
          new Set(
            request.targetDays
              .map((day) => Number(day))
              .filter((day): day is number => Number.isFinite(day))
          )
        ).sort((left, right) => left - right)
      : [];

    const payload: Record<string, unknown> = {
      sourceDay: request.sourceDay,
    };

    if (request.mode === 'next_day') {
      payload.mode = 'next_day';
    } else if (normalizedTargetDays.length > 1) {
      payload.targetDays = normalizedTargetDays;
    } else if (normalizedTargetDays.length === 1) {
      payload.targetDay = normalizedTargetDays[0];
    } else if (typeof request.targetDay === 'number' && Number.isFinite(request.targetDay)) {
      payload.targetDay = request.targetDay;
    } else if (request.mode) {
      payload.mode = request.mode;
    } else if (typeof request.applyToAll === 'boolean') {
      payload.applyToAll = request.applyToAll;
    }

    const response = await api.post(`/meal-plans/${mealPlanId}/copy-day`, {
      ...payload,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// AI Meal Plan Generation
// Generate meal plan asynchronously
export const generateMealPlanAsync = async (
  clientId: string,
  startDate: string,
  days: number,
  additionalText?: string,
  aiModel?: AIModelOption,
  options?: {
    kitchenProfile?: GenerateMealPlanRequest['kitchenProfile'];
    cuisineRegions?: string[];
    name?: string;
    allergies?: string[];
    intolerances?: string[];
    cuisineTypes?: string[];
    preferences?: string[];
    dietaryPreferences?: string[];
    medicalConditions?: string[];
    overrideMealProgram?: GenerateMealPlanRequest['overrideMealProgram'];
    overrideClientGoals?: GenerateMealPlanRequest['overrideClientGoals'];
  }
): Promise<{
  success: boolean;
  message: string;
  data?: {
    draftId?: string;
    id?: string;
    status?: string;
    generationState?: 'queued' | 'in_progress' | 'completed' | 'partial' | 'failed';
    source?: string;
    [key: string]: any;
  };
}> => {
  const requestPayload: GenerateMealPlanRequest & {
    additionalText?: string;
  } = {
    clientId,
    startDate,
    days,
  };
  if (options?.name?.trim()) {
    requestPayload.name = options.name.trim();
  }

  if (options?.kitchenProfile) requestPayload.kitchenProfile = options.kitchenProfile;
  if (options?.cuisineRegions?.length) requestPayload.cuisineRegions = options.cuisineRegions;
  // Only include additionalText if provided
  if (additionalText && additionalText.trim()) {
    requestPayload.additionalText = additionalText.trim();
  }

  // Include AI provider/model if specified
  if (aiModel) {
    requestPayload.aiProvider = 'openai';
    requestPayload.aiModel = aiModel;
  }

  Object.assign(requestPayload, buildDietaryPreferencePayload(options));
  if (options?.medicalConditions?.length) requestPayload.medicalConditions = options.medicalConditions;
  const sanitizedOverrideMealProgram = sanitizeOverrideMealProgram(options?.overrideMealProgram);
  if (sanitizedOverrideMealProgram) {
    requestPayload.overrideMealProgram = sanitizedOverrideMealProgram;
  }
  if (options?.overrideClientGoals) requestPayload.overrideClientGoals = options.overrideClientGoals;
  const response = await api.post('/meal-plans/generate-ai', requestPayload);
  return response.data;
};

// Draft Management (REST: GET/PUT/POST /meal-plans/drafts/{id})
// Get draft by ID - single endpoint for all automated drafts
export const getDraftById = async (draftId: string): Promise<{ success: boolean; message?: string; data?: any }> => {
  try {
    const response = await api.get(`/meal-plans/drafts/${draftId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Finish a partial or failed AI draft. Saved days and selections are kept; only
 * missing days and empty meal slots are generated. Poll generation progress afterwards.
 */
export const resumeMealPlanGeneration = async (draftId: string): Promise<{
  success: boolean;
  message?: string;
  data?: { draftId: string; generationState: 'queued'; missingDays: number; emptySlots: number };
}> => {
  const response = await api.post('/meal-plans/generate-ai/resume', { draftId });
  return response.data;
};

export const getDraftGenerationProgress = async (draftId: string): Promise<DraftGenerationProgressResponse> => {
  try {
    const response = await api.get(`/meal-plans/drafts/${draftId}/generation-progress`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export type DraftAsyncStatusResponse = {
  success?: boolean;
  message?: string;
  data?: Record<string, unknown>;
  [key: string]: unknown;
};

export const getDraftShuffleStatus = async (draftId: string): Promise<DraftAsyncStatusResponse> => {
  try {
    const response = await api.get(`/meal-plans/drafts/${draftId}/shuffle/status`);
    return response.data as DraftAsyncStatusResponse;
  } catch (error) {
    throw error;
  }
};

export const getDraftSmartEditStatus = async (draftId: string): Promise<DraftAsyncStatusResponse> => {
  try {
    const response = await api.get(`/meal-plans/drafts/${draftId}/smart-edit/status`);
    return response.data as DraftAsyncStatusResponse;
  } catch (error) {
    throw error;
  }
};

// List all drafts
export const getDrafts = async (): Promise<{ success: boolean; message?: string; data?: any[] }> => {
  try {
    const response = await api.get('/meal-plans/drafts');
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Get draft for display - normalizes API daySuggestions shape to DraftData when needed
export const getDraftForDisplay = async (draftId: string): Promise<{ success: boolean; message?: string; data?: DraftData }> => {
  const raw = await getDraftById(draftId);
  const payload = raw.data ?? raw;
  if (isApiDraftShape(payload)) {
    const draftData = normalizeDraftApiResponseToDraftData(payload);
    return { success: raw.success ?? true, message: raw.message, data: draftData };
  }
  return { success: raw.success ?? true, message: raw.message, data: payload };
};

/** @deprecated Use getDraftById or getMealPlanDraftById instead. Kept for compatibility; now calls GET drafts/{id}. */
export const getDraftDetails = async (draftId: string): Promise<{ success: boolean; message?: string; data?: any }> => {
  return getDraftById(draftId);
};

// PUT /meal-plans/drafts/{id} - select, deselect, or update customization
export const updateDraft = async (
  draftId: string,
  body: UpdateDraftRequest
): Promise<{ success: boolean; message?: string; data?: any }> => {
  try {
    const response = await api.put(`/meal-plans/drafts/${draftId}`, {
      ...body,
      draftId,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const selectDraftRecipe = async (
  draftId: string,
  dayNumber: number,
  mealOrder: number,
  recipeId: string,
  options?: { responseMode?: UpdateDraftRequest['responseMode'] }
): Promise<{ success: boolean; message?: string; data?: any }> => {
  return updateDraft(draftId, {
    action: 'selectRecipe',
    dayNumber,
    mealOrder,
    recipeId,
    ...(options?.responseMode ? { responseMode: options.responseMode } : {}),
  });
};

export const deselectDraftRecipe = async (
  draftId: string,
  dayNumber: number,
  mealOrder: number,
  options?: { responseMode?: UpdateDraftRequest['responseMode'] }
): Promise<{ success: boolean; message?: string; data?: any }> => {
  return updateDraft(draftId, {
    action: 'deselectRecipe',
    dayNumber,
    mealOrder,
    ...(options?.responseMode ? { responseMode: options.responseMode } : {}),
  });
};

const SHUFFLE_POLL_INTERVAL_MS = 15_000;
const SMART_EDIT_POLL_INTERVAL_MS = 15_000;

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

type DraftAsyncState = 'queued' | 'in_progress' | 'completed' | 'failed';

const normalizeDraftAsyncState = (value: unknown): DraftAsyncState | undefined => {
  if (typeof value !== 'string') return undefined;
  const normalized = value.toLowerCase();
  if (
    normalized === 'queued' ||
    normalized === 'in_progress' ||
    normalized === 'completed' ||
    normalized === 'failed'
  ) {
    return normalized;
  }
  return undefined;
};

const readDraftAsyncState = (
  source: Record<string, unknown>,
  keys: string[]
): DraftAsyncState | undefined => {
  for (const key of keys) {
    const state = normalizeDraftAsyncState(source[key]);
    if (state) return state;
  }
  return undefined;
};

const readStringField = (source: Record<string, unknown>, keys: string[]): string | null => {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'string' && value.trim().length > 0) {
      return value;
    }
  }
  return null;
};

const readNumberField = (
  source: Record<string, unknown>,
  keys: string[]
): number | undefined => {
  for (const key of keys) {
    const parsed = Number(source[key]);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return undefined;
};

type SmartEditUsageFields = {
  attemptsLeft?: number;
  attemptsUsed?: number;
  dailyLimit?: number;
  windowHours?: number;
  windowStartedAt?: string;
  resetAt?: string;
  retryAfterTimestamp?: string;
  retryAfterSeconds?: number;
  action?: string;
  smartEditInstruction?: string;
};

const extractSmartEditUsageFields = (payload: unknown): SmartEditUsageFields => {
  if (!payload || typeof payload !== 'object') {
    return {};
  }

  const record = payload as Record<string, unknown>;
  const nestedData =
    record.data && typeof record.data === 'object'
      ? (record.data as Record<string, unknown>)
      : null;
  const source = nestedData ?? record;

  const attemptsLeft = readNumberField(source, ['attemptsLeft', 'attempts_left']);
  const attemptsUsed = readNumberField(source, ['attemptsUsed', 'attempts_used']);
  const dailyLimit = readNumberField(source, ['dailyLimit', 'daily_limit']);
  const windowHours = readNumberField(source, ['windowHours', 'window_hours']);
  const retryAfterSeconds = readNumberField(source, ['retryAfterSeconds', 'retry_after_seconds']);
  const windowStartedAt = readStringField(source, ['windowStartedAt', 'window_started_at']) ?? undefined;
  const resetAt = readStringField(source, ['resetAt', 'reset_at']) ?? undefined;
  const retryAfterTimestamp =
    readStringField(source, ['retryAfterTimestamp', 'retry_after_timestamp']) ?? undefined;
  const action = readStringField(source, ['action']) ?? undefined;
  const smartEditInstruction =
    readStringField(source, ['smartEditInstruction', 'smart_edit_instruction']) ?? undefined;

  const usageFields: SmartEditUsageFields = {};
  if (attemptsLeft !== undefined) usageFields.attemptsLeft = attemptsLeft;
  if (attemptsUsed !== undefined) usageFields.attemptsUsed = attemptsUsed;
  if (dailyLimit !== undefined) usageFields.dailyLimit = dailyLimit;
  if (windowHours !== undefined) usageFields.windowHours = windowHours;
  if (windowStartedAt) usageFields.windowStartedAt = windowStartedAt;
  if (resetAt) usageFields.resetAt = resetAt;
  if (retryAfterTimestamp) usageFields.retryAfterTimestamp = retryAfterTimestamp;
  if (retryAfterSeconds !== undefined) usageFields.retryAfterSeconds = retryAfterSeconds;
  if (action) usageFields.action = action;
  if (smartEditInstruction) usageFields.smartEditInstruction = smartEditInstruction;
  return usageFields;
};

const extractShuffledRecipes = (
  draftPayload: unknown,
  dayNumber: number,
  mealOrder: number
): any[] => {
  if (!draftPayload || typeof draftPayload !== 'object') {
    return [];
  }

  const payload = draftPayload as Record<string, unknown>;
  const daySuggestions = Array.isArray(payload.daySuggestions) ? payload.daySuggestions : [];
  const day = daySuggestions.find((item) => Number((item as { dayNumber?: number }).dayNumber) === dayNumber) as
    | { meals?: unknown[] }
    | undefined;
  const meals = Array.isArray(day?.meals) ? day.meals : [];
  const meal = meals.find((item) => Number((item as { mealOrder?: number }).mealOrder) === mealOrder) as
    | { recipeSuggestions?: unknown[] }
    | undefined;

  return Array.isArray(meal?.recipeSuggestions) ? (meal.recipeSuggestions as any[]) : [];
};

const pollShuffleResult = async (
  draftId: string,
  dayNumber: number,
  mealOrder: number,
  page: number
): Promise<{
  success: boolean;
  message?: string;
  data?: {
    recipeSuggestions?: any[];
    page?: number;
    totalPages?: number;
    attemptsLeft?: number;
    attemptsUsed?: number;
    dailyLimit?: number;
    windowHours?: number;
    windowStartedAt?: string;
    resetAt?: string;
    retryAfterTimestamp?: string;
    retryAfterSeconds?: number;
    action?: string;
  };
}> => {
  // Keep waiting until backend reports a terminal shuffle state via status endpoint.
  // Once completed, fetch fresh draft details to return updated slot suggestions.
  while (true) {
    await sleep(SHUFFLE_POLL_INTERVAL_MS);

    let shuffleStatusResponse: DraftAsyncStatusResponse | null = null;
    try {
      shuffleStatusResponse = await getDraftShuffleStatus(draftId);
    } catch {
      continue;
    }
    if (!shuffleStatusResponse) continue;
    if (shuffleStatusResponse.success === false) continue;

    const statusPayload = (shuffleStatusResponse.data ?? shuffleStatusResponse) as Record<string, unknown>;
    const shuffleState = readDraftAsyncState(statusPayload, [
      'shuffleState',
      'shuffle_state',
      'state',
      'status',
    ]);
    const shuffleError = readStringField(statusPayload, [
      'shuffleError',
      'shuffle_error',
      'error',
      'message',
    ]);
    const usageFields = extractSmartEditUsageFields(statusPayload);

    if (!shuffleState || shuffleState === 'queued' || shuffleState === 'in_progress') {
      continue;
    }

    // Some backends can transiently report failed before settling to completed.
    // Do not fail client-side early; keep waiting for terminal successful payload.
    if (shuffleState === 'failed') {
      if (shuffleError) {
        console.warn('Shuffle reported failed state while polling; continuing to wait:', shuffleError);
      }
      continue;
    }

    let draftResponse: { success: boolean; message?: string; data?: any } | null = null;
    try {
      draftResponse = await getDraftById(draftId);
    } catch {
      continue;
    }
    if (!draftResponse) continue;
    if (draftResponse.success === false) continue;

    const draftPayload = (draftResponse.data ?? draftResponse) as Record<string, unknown>;
    const recipeSuggestions = extractShuffledRecipes(draftPayload, dayNumber, mealOrder);
    const draftUsageFields = extractSmartEditUsageFields(draftPayload);

    return {
      success: true,
      message: 'Recipes shuffled successfully',
      data: {
        ...draftUsageFields,
        ...usageFields,
        recipeSuggestions,
        page,
      },
    };
  }
};

type ShuffleDraftResponse = {
  success: boolean;
  message?: string;
  data?: {
    recipeSuggestions?: any[];
    page?: number;
    totalPages?: number;
    attemptsLeft?: number;
    attemptsUsed?: number;
    dailyLimit?: number;
    windowHours?: number;
    windowStartedAt?: string;
    resetAt?: string;
    retryAfterTimestamp?: string;
    retryAfterSeconds?: number;
    action?: string;
    shuffleState?: 'queued' | 'in_progress' | 'completed' | 'failed' | string;
    [key: string]: any;
  };
};

function normalizeShuffleResponse(raw: unknown): ShuffleDraftResponse {
  const payload = (raw && typeof raw === 'object') ? (raw as Record<string, unknown>) : {};
  const nestedData = (payload.data && typeof payload.data === 'object')
    ? (payload.data as Record<string, unknown>)
    : null;
  const nestedNestedData = (nestedData?.data && typeof nestedData.data === 'object')
    ? (nestedData.data as Record<string, unknown>)
    : null;
  const effectiveData = nestedData ?? payload;
  const explicitSuccess = payload.success;
  const nestedExplicitSuccess = nestedData?.success;

  if (typeof explicitSuccess === 'boolean') {
    return payload as ShuffleDraftResponse;
  }
  if (typeof nestedExplicitSuccess === 'boolean') {
    return nestedData as ShuffleDraftResponse;
  }

  const hasKnownShape =
    Array.isArray(effectiveData.recipeSuggestions) ||
    typeof effectiveData.shuffleState === 'string' ||
    typeof effectiveData.shuffle_state === 'string' ||
    typeof effectiveData.smartEditState === 'string' ||
    typeof effectiveData.smart_edit_state === 'string' ||
    typeof effectiveData.attemptsLeft === 'number' ||
    typeof effectiveData.attempts_left === 'number' ||
    typeof effectiveData.attemptsUsed === 'number' ||
    typeof effectiveData.attempts_used === 'number' ||
    typeof effectiveData.dailyLimit === 'number' ||
    typeof effectiveData.daily_limit === 'number' ||
    typeof effectiveData.dayNumber === 'number' ||
    typeof effectiveData.mealOrder === 'number' ||
    Array.isArray(nestedNestedData?.recipeSuggestions) ||
    typeof nestedNestedData?.shuffleState === 'string' ||
    typeof nestedNestedData?.shuffle_state === 'string' ||
    typeof nestedNestedData?.smartEditState === 'string' ||
    typeof nestedNestedData?.smart_edit_state === 'string' ||
    typeof nestedNestedData?.attemptsLeft === 'number' ||
    typeof nestedNestedData?.attempts_left === 'number' ||
    typeof nestedNestedData?.attemptsUsed === 'number' ||
    typeof nestedNestedData?.attempts_used === 'number' ||
    typeof nestedNestedData?.dailyLimit === 'number' ||
    typeof nestedNestedData?.daily_limit === 'number' ||
    typeof nestedNestedData?.dayNumber === 'number' ||
    typeof nestedNestedData?.mealOrder === 'number';

  if (hasKnownShape) {
    return {
      success: true,
      message: typeof payload.message === 'string' ? payload.message : undefined,
      data: (nestedNestedData ?? effectiveData) as ShuffleDraftResponse['data'],
    };
  }

  return {
    success: false,
    message: typeof payload.message === 'string' ? payload.message : 'Invalid shuffle response',
    data: effectiveData as ShuffleDraftResponse['data'],
  };
}

const extractDraftSlotRecipeSuggestions = (
  payload: unknown,
  dayNumber: number,
  mealOrder: number
): any[] => {
  if (!payload || typeof payload !== 'object') return [];

  const source = payload as Record<string, unknown>;
  if (Array.isArray(source.recipeSuggestions)) {
    return source.recipeSuggestions as any[];
  }

  const nestedData = source.data;
  if (nestedData && typeof nestedData === 'object') {
    const nestedRecord = nestedData as Record<string, unknown>;
    if (Array.isArray(nestedRecord.recipeSuggestions)) {
      return nestedRecord.recipeSuggestions as any[];
    }

    const nestedFromDraft = extractShuffledRecipes(nestedRecord, dayNumber, mealOrder);
    if (nestedFromDraft.length > 0) {
      return nestedFromDraft;
    }
  }

  return extractShuffledRecipes(source, dayNumber, mealOrder);
};

// POST /meal-plans/drafts/{id}/shuffle - alternate recipes / pagination
export const shuffleDraft = async (
  draftId: string,
  dayNumber: number,
  mealOrder: number,
  page: number = 1,
  recipeId?: string,
  dietaryContext?: DietaryPreferencePayloadSource
): Promise<{
  success: boolean;
  message?: string;
  data?: {
    recipeSuggestions?: any[];
    page?: number;
    totalPages?: number;
    attemptsLeft?: number;
    attemptsUsed?: number;
    dailyLimit?: number;
    windowHours?: number;
    windowStartedAt?: string;
    resetAt?: string;
    retryAfterTimestamp?: string;
    retryAfterSeconds?: number;
    action?: string;
    shuffleState?: 'queued' | 'in_progress' | 'completed' | 'failed' | string;
    [key: string]: any;
  };
}> => {
  try {
    const trimmedRecipeId = recipeId?.trim();
    const response = await api.post(`/meal-plans/drafts/${draftId}/shuffle`, {
      dayNumber,
      mealOrder,
      page,
      ...buildDietaryPreferencePayload(dietaryContext),
      ...(trimmedRecipeId
        ? {
            recipeId: trimmedRecipeId,
            currentRecipeId: trimmedRecipeId,
          }
        : {}),
    });
    const normalized = normalizeShuffleResponse(response.data);
    const normalizedData = (normalized.data as Record<string, unknown> | undefined) ?? {};
    const responsePayload =
      response.data && typeof response.data === 'object'
        ? (response.data as Record<string, unknown>)
        : {};
    const responseNestedData =
      responsePayload.data && typeof responsePayload.data === 'object'
        ? (responsePayload.data as Record<string, unknown>)
        : {};
    const responseNestedNestedData =
      responseNestedData.data && typeof responseNestedData.data === 'object'
        ? (responseNestedData.data as Record<string, unknown>)
        : {};
    const rawShuffleState =
      normalizedData.shuffleState ??
      normalizedData.shuffle_state ??
      responseNestedNestedData.shuffleState ??
      responseNestedNestedData.shuffle_state ??
      responseNestedData.shuffleState ??
      responseNestedData.shuffle_state ??
      responsePayload.shuffleState ??
      responsePayload.shuffle_state;
    const shuffleState = typeof rawShuffleState === 'string'
      ? rawShuffleState.toLowerCase()
      : undefined;
    const usageFields = extractSmartEditUsageFields(normalized.data ?? response.data);
    const resolvedPageRaw = normalizedData.page;
    const resolvedPage = Number.isFinite(Number(resolvedPageRaw)) ? Number(resolvedPageRaw) : page;
    const recipeSuggestions = extractDraftSlotRecipeSuggestions(
      normalized.data ?? response.data,
      dayNumber,
      mealOrder
    );
    const responseMessage = String(normalized.message ?? responsePayload.message ?? '').toLowerCase();
    const indicatesQueuedFromMessage = responseMessage.includes('queued');

    const shouldPoll =
      response.status === 202 ||
      shuffleState === 'queued' ||
      shuffleState === 'in_progress' ||
      indicatesQueuedFromMessage ||
      normalized.success === false;

    if (shouldPoll) {
      return await pollShuffleResult(draftId, dayNumber, mealOrder, resolvedPage);
    }

    return {
      success: normalized.success ?? true,
      message: normalized.message,
      data: {
        ...(normalized.data ?? {}),
        ...usageFields,
        page: resolvedPage,
        recipeSuggestions,
      },
    };
  } catch (error) {
    throw error;
  }
};

/** @deprecated Use shuffleDraft(draftId, dayNumber, mealOrder, page) instead. */
export const getAlternateDraftRecipes = async (
  draftId: string,
  day: number,
  mealNameOrOrder: string | number,
  _recipeId: string,
  _count: number = 3,
  page: number = 1
): Promise<{ success: boolean; message?: string; data?: any }> => {
  const parsedMealOrder =
    typeof mealNameOrOrder === 'number'
      ? mealNameOrOrder
      : Number.parseInt(mealNameOrOrder, 10);

  const mealOrder = Number.isFinite(parsedMealOrder) && parsedMealOrder > 0 ? parsedMealOrder : 1;
  return shuffleDraft(draftId, day, mealOrder, page);
};

const SMART_EDIT_STATE_FIELDS = [
  'smartEditState',
  'smart_edit_state',
  'shuffleState',
  'shuffle_state',
] as const;

const SMART_EDIT_ERROR_FIELDS = [
  'smartEditError',
  'smart_edit_error',
  'shuffleError',
  'shuffle_error',
] as const;

type SmartEditDraftResponse = {
  success: boolean;
  message?: string;
  data?: {
    recipeSuggestions?: any[];
    dayNumber?: number;
    mealOrder?: number;
    instruction?: string;
    attemptsLeft?: number;
    attemptsUsed?: number;
    dailyLimit?: number;
    windowHours?: number;
    windowStartedAt?: string;
    resetAt?: string;
    retryAfterTimestamp?: string;
    retryAfterSeconds?: number;
    action?: string;
    smartEditInstruction?: string;
    smartEditState?: DraftAsyncState | string;
    smart_edit_state?: DraftAsyncState | string;
    shuffleState?: DraftAsyncState | string;
    shuffle_state?: DraftAsyncState | string;
    [key: string]: any;
  };
};

const pollSmartEditResult = async (
  draftId: string,
  dayNumber: number,
  mealOrder: number,
  instruction: string
): Promise<SmartEditDraftResponse> => {
  while (true) {
    await sleep(SMART_EDIT_POLL_INTERVAL_MS);

    let draftResponse: { success: boolean; message?: string; data?: any } | null = null;
    try {
      draftResponse = await getDraftById(draftId);
    } catch {
      continue;
    }
    if (!draftResponse) continue;
    if (draftResponse.success === false) continue;

    const draftPayload = (draftResponse.data ?? draftResponse) as Record<string, unknown>;
    const smartEditState = readDraftAsyncState(draftPayload, [...SMART_EDIT_STATE_FIELDS]);
    const smartEditError = readStringField(draftPayload, [...SMART_EDIT_ERROR_FIELDS]);
    const usageFields = extractSmartEditUsageFields(draftPayload);

    if (smartEditState === 'queued' || smartEditState === 'in_progress') {
      continue;
    }

    const recipeSuggestions = extractDraftSlotRecipeSuggestions(draftPayload, dayNumber, mealOrder);

    if (recipeSuggestions.length > 0 || smartEditState === 'completed') {
      return {
        success: true,
        message: 'Smart edit suggestions generated',
        data: {
          ...usageFields,
          recipeSuggestions,
          dayNumber,
          mealOrder,
          instruction,
          smartEditState: smartEditState ?? 'completed',
        },
      };
    }

    // If state is omitted transiently, keep polling until backend settles.
    if (!smartEditState) {
      continue;
    }

    // Some backends can transiently report failed before settling to completed.
    // Do not fail client-side early; keep waiting for terminal successful payload.
    if (smartEditState === 'failed') {
      if (smartEditError) {
        console.warn('Smart edit reported failed state while polling; continuing to wait:', smartEditError);
      }
      continue;
    }

    return {
      success: true,
      message: 'Smart edit suggestions generated',
      data: {
        ...usageFields,
        recipeSuggestions,
        dayNumber,
        mealOrder,
        instruction,
      },
    };
  }
};

// POST /meal-plans/drafts/{id}/smart-edit - instruction based meal swap suggestions
export const smartEditDraft = async (
  draftId: string,
  dayNumber: number,
  mealOrder: number,
  instruction: string,
  recipeId?: string,
  dietaryContext?: DietaryPreferencePayloadSource
): Promise<SmartEditDraftResponse> => {
  const trimmedInstruction = instruction.trim();
  const trimmedRecipeId = recipeId?.trim();
  if (!trimmedInstruction) {
    return {
      success: false,
      message: 'Instruction is required',
      data: {
        recipeSuggestions: [],
        dayNumber,
        mealOrder,
      },
    };
  }

  try {
    const response = await api.post(`/meal-plans/drafts/${draftId}/smart-edit`, {
      dayNumber,
      mealOrder,
      instruction: trimmedInstruction,
      ...buildDietaryPreferencePayload(dietaryContext),
      ...(trimmedRecipeId
        ? {
            recipeId: trimmedRecipeId,
            currentRecipeId: trimmedRecipeId,
          }
        : {}),
    });

    const normalized = normalizeShuffleResponse(response.data);
    const normalizedData = (normalized.data as Record<string, unknown> | undefined) ?? {};
    const smartEditState = readDraftAsyncState(normalizedData, [...SMART_EDIT_STATE_FIELDS]);
    const usageFields = extractSmartEditUsageFields(normalized.data ?? response.data);
    const responsePayload = (response.data && typeof response.data === 'object')
      ? (response.data as Record<string, unknown>)
      : null;
    const explicitSuccess = responsePayload && typeof responsePayload.success === 'boolean'
      ? responsePayload.success
      : undefined;

    const responseMessage = String(normalized.message ?? responsePayload?.message ?? '').toLowerCase();
    const indicatesQueuedFromMessage = responseMessage.includes('queued');
    const shouldPoll =
      response.status === 202 ||
      smartEditState === 'queued' ||
      smartEditState === 'in_progress' ||
      indicatesQueuedFromMessage ||
      (normalized.success === false && explicitSuccess !== false);

    if (shouldPoll) {
      const recipeSuggestions = extractDraftSlotRecipeSuggestions(
        normalized.data ?? response.data,
        dayNumber,
        mealOrder
      );

      return {
        success: normalized.success ?? true,
        message: normalized.message,
        data: {
          ...(normalized.data ?? {}),
          ...usageFields,
          dayNumber,
          mealOrder,
          instruction: trimmedInstruction,
          recipeSuggestions,
        },
      };
    }

    const recipeSuggestions = extractDraftSlotRecipeSuggestions(
      normalized.data ?? response.data,
      dayNumber,
      mealOrder
    );

    return {
      success: normalized.success ?? true,
      message: normalized.message,
      data: {
        ...(normalized.data ?? {}),
        ...usageFields,
        dayNumber,
        mealOrder,
        instruction: trimmedInstruction,
        recipeSuggestions,
      },
    };
  } catch (error) {
    throw error;
  }
};

// POST /meal-plans/drafts/{id}/finalize
export const finalizeDraft = async (
  draftId: string,
  planName: string,
  description?: string,
  overwriteConflicts?: boolean,
  acknowledgeNutritionReview?: boolean,
): Promise<FinalizeDraftResponse> => {
  try {
    const response = await api.post(`/meal-plans/drafts/${draftId}/finalize`, {
      draftId,
      ...(acknowledgeNutritionReview ? { acknowledgeNutritionReview: true } : {}),
      name: planName,
      description,
      ...(overwriteConflicts ? { overwriteConflicts: true } : {}),
    });
    const data = response.data;
    if (data?.data) {
      const d = data.data;
      const id = d.mealPlanId ?? d.planId ?? d.id;
      if (id) {
        data.data.planId = d.planId ?? d.mealPlanId ?? d.id;
        data.data.mealPlanId = d.mealPlanId ?? d.planId ?? d.id;
      }
    }
    return data;
  } catch (error) {
    throw error;
  }
};

export const swapDraftRecipe = async (payload: {
  draftId: string;
  day: number;
  mealName: string;
  mealOrder?: number;
  oldRecipeId: string;
  newRecipe: any;
}): Promise<{ success: boolean; message: string; data?: any }> => {
  const mealOrder = payload.mealOrder ?? 0;
  if (!mealOrder) {
    throw new Error('mealOrder is required to select an alternate recipe');
  }
  const recipeId = payload?.newRecipe?.id;
  if (!recipeId) {
    throw new Error('newRecipe.id is required to select an alternate recipe');
  }
  const response = await updateDraft(payload.draftId, {
    action: 'selectRecipe',
    dayNumber: payload.day,
    mealOrder,
    recipeId,
  });
  return {
    success: response.success ?? false,
    message: response.message ?? '',
    data: response.data,
  };
};

// Change page for meal recipes in draft - uses POST /meal-plans/drafts/{id}/shuffle
export const changeMealPage = async (
  planId: string,
  day: number,
  mealOrder: number,
  _direction: 'next' | 'prev' | 'specific',
  pageNumber?: number
): Promise<{
  success: boolean;
  message: string;
  data: {
    displayedRecipes: any[];
    currentPage: number;
    totalPages: number;
    recipesCount: number;
  };
}> => {
  const page = pageNumber ?? 1;
  const response = await shuffleDraft(planId, day, mealOrder, page);
  return {
    success: response.success,
    message: response.message ?? '',
    data: {
      displayedRecipes: response.data?.recipeSuggestions ?? [],
      currentPage: response.data?.page ?? page,
      totalPages: response.data?.totalPages ?? 1,
      recipesCount: response.data?.recipeSuggestions?.length ?? 0,
    },
  };
};

// Update notes for a recipe in meal plan
export const updateRecipeNotes = async (
  planId: string,
  day: number,
  mealName: string,
  recipeId: string,
  notes: string
): Promise<{
  success: boolean;
  message: string;
  data?: any;
}> => {
  try {
    const response = await api.post('/meal-plans/draft', {
      action: 'update-notes',
      planId: planId,
      day: day,
      mealName: mealName,
      recipeId: recipeId,
      notes: notes
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Map new API shape (data.nutritionForAmount flat snake_case) to IngredientNutritionResponse
function normalizeNutritionForAmountToResponse(
  raw: { success?: boolean; data?: { name?: string; amount?: number; unit?: string; nutritionForAmount?: Record<string, number> } }
): IngredientNutritionResponse {
  const data = raw.data ?? raw;
  const shouldHideMlForFrontend = shouldHideMlForSpoonacularPayload(
    (data as { source?: unknown }).source,
    (data as { category?: unknown }).category,
    (data as { name?: unknown }).name,
  );
  const unitMeasurements = collectIngredientPayloadUnitMeasurements(
    data as Record<string, unknown>,
    shouldHideMlForFrontend,
  );
  const possibleUnits = unitMeasurements.map((measurement) => measurement.value);
  const possibleUnitWeights = unitMeasurements
    .filter((measurement) => measurement.grams != null)
    .map((measurement) => ({
      unit: measurement.value,
      grams: measurement.grams as number,
    }));
  const servingWeights = buildIngredientServingWeights(unitMeasurements);
  const possibleUnitMlWeights = unitMeasurements
    .filter((measurement) => measurement.ml != null)
    .map((measurement) => ({
      unit: measurement.value,
      ml: measurement.ml as number,
    }));
  const servingMlWeights = unitMeasurements.reduce((acc, measurement) => {
    if (measurement.ml != null) {
      acc[measurement.value] = measurement.ml;
    }
    return acc;
  }, {} as Record<string, number>);
  const n = (data as { nutritionForAmount?: Record<string, number> }).nutritionForAmount;
  const amountInGrams = (data as { amount_grams?: number; amountInGrams?: number; servingGrams?: number; serving_grams?: number }).amount_grams
    ?? (data as { amount_grams?: number; amountInGrams?: number; servingGrams?: number; serving_grams?: number }).amountInGrams
    ?? (data as { amount_grams?: number; amountInGrams?: number; servingGrams?: number; serving_grams?: number }).servingGrams
    ?? (data as { amount_grams?: number; amountInGrams?: number; servingGrams?: number; serving_grams?: number }).serving_grams;
  const amountInMl = (data as { amount_ml?: number; amountInMl?: number; servingMl?: number; serving_ml?: number }).amount_ml
    ?? (data as { amount_ml?: number; amountInMl?: number; servingMl?: number; serving_ml?: number }).amountInMl
    ?? (data as { amount_ml?: number; amountInMl?: number; servingMl?: number; serving_ml?: number }).servingMl
    ?? (data as { amount_ml?: number; amountInMl?: number; servingMl?: number; serving_ml?: number }).serving_ml;
  const yieldFactor = (data as { yieldFactor?: number; yield_factor?: number }).yieldFactor
    ?? (data as { yieldFactor?: number; yield_factor?: number }).yield_factor;
  const retentionFactor = (data as { retentionFactor?: number; retention_factor?: number }).retentionFactor
    ?? (data as { retentionFactor?: number; retention_factor?: number }).retention_factor;
  const allergens = normalizeStringArray((data as { allergens?: unknown }).allergens);
  const allergenTagIds = normalizeStringArray((data as { allergenTagIds?: unknown }).allergenTagIds);
  const gramsPerMl = shouldHideMlForFrontend
    ? undefined
    : resolveGramsPerMl(
        servingWeights as Record<string, unknown> | undefined,
        (data as { gramsPerMl?: number }).gramsPerMl,
      );
  const unitOptions = buildIngredientUnitOptions(unitMeasurements, gramsPerMl, shouldHideMlForFrontend);
  if (!n || typeof n !== 'object') {
    return {
      ...(raw as IngredientNutritionResponse),
      ...(amountInGrams != null && { amountInGrams }),
      ...(!shouldHideMlForFrontend && amountInMl != null && { amountInMl }),
      ...(yieldFactor != null && Number.isFinite(Number(yieldFactor)) ? { yieldFactor: Number(yieldFactor) } : {}),
      ...(retentionFactor != null && Number.isFinite(Number(retentionFactor)) ? { retentionFactor: Number(retentionFactor) } : {}),
      ...(allergens.length > 0 ? { allergens } : {}),
      ...(allergenTagIds.length > 0 ? { allergenTagIds } : {}),
      ...(gramsPerMl != null && Number.isFinite(Number(gramsPerMl)) ? { gramsPerMl: Number(gramsPerMl) } : {}),
      ...((data as { unit?: string }).unit ? { unit: (data as { unit?: string }).unit } : {}),
      ...(possibleUnits.length > 0 && { possibleUnits }),
      ...(possibleUnitWeights.length > 0 && { possibleUnitWeights }),
      ...(servingWeights && Object.keys(servingWeights).length > 0 && { servingWeights }),
      ...(possibleUnitMlWeights.length > 0 && { possibleUnitMlWeights }),
      ...(servingMlWeights && Object.keys(servingMlWeights).length > 0 && { servingMlWeights }),
      ...(unitOptions && unitOptions.length > 0 && { unitOptions }),
    };
  }
  const num = (k: string) => (n[k] != null ? Number(n[k]) : 0);
  return {
    success: Boolean(raw.success ?? true),
    ingredient: (data as { name?: string }).name ?? (raw as { ingredient?: string }).ingredient,
    source: (data as { source?: string }).source ?? (raw as { source?: string }).source,
    ...((data as { unit?: string }).unit ? { unit: (data as { unit?: string }).unit } : {}),
    ...(possibleUnits.length > 0 && { possibleUnits }),
    ...(possibleUnitWeights.length > 0 && { possibleUnitWeights }),
    ...(servingWeights && Object.keys(servingWeights).length > 0 && { servingWeights }),
    ...(possibleUnitMlWeights.length > 0 && { possibleUnitMlWeights }),
    ...(servingMlWeights && Object.keys(servingMlWeights).length > 0 && { servingMlWeights }),
    ...(unitOptions && unitOptions.length > 0 && { unitOptions }),
    ...(amountInGrams != null && { amountInGrams }),
    ...(!shouldHideMlForFrontend && amountInMl != null && { amountInMl }),
    ...(yieldFactor != null && Number.isFinite(Number(yieldFactor)) ? { yieldFactor: Number(yieldFactor) } : {}),
    ...(retentionFactor != null && Number.isFinite(Number(retentionFactor)) ? { retentionFactor: Number(retentionFactor) } : {}),
    ...(allergens.length > 0 ? { allergens } : {}),
    ...(allergenTagIds.length > 0 ? { allergenTagIds } : {}),
    ...(gramsPerMl != null && Number.isFinite(Number(gramsPerMl)) ? { gramsPerMl: Number(gramsPerMl) } : {}),
    nutritionForAmount: n,
    nutrition: {
      calories: num('calories'),
      macros: {
        protein: num('protein'),
        carbs: num('carbohydrate'),
        fat: num('total_fat'),
        fiber: num('dietary_fibre'),
        sugar: num('sugar'),
        sodium: num('sodium'),
        cholesterol: num('cholesterol'),
        saturatedFat: num('saturated_fat'),
        transFat: num('trans_fat'),
        monounsaturatedFat: num('mufa'),
        polyunsaturatedFat: num('pufa'),
      },
      micros: {
        vitamins: {
          vitaminA: num('vitamin_a'),
          vitaminC: num('vitamin_c'),
          vitaminD: num('vitamin_d'),
          vitaminE: num('vitamin_e'),
          vitaminK: num('vitamin_k'),
          thiamin: num('vitamin_b1'),
          riboflavin: num('vitamin_b2'),
          niacin: num('vitamin_b3'),
          vitaminB6: num('vitamin_b6'),
          folate: num('vitamin_b9'),
          vitaminB12: num('vitamin_b12'),
          biotin: num('biotin'),
          pantothenicAcid: num('vitamin_b5'),
        },
        minerals: {
          calcium: num('calcium'),
          iron: num('iron'),
          magnesium: num('magnesium'),
          phosphorus: num('phosphorus'),
          potassium: num('potassium'),
          zinc: num('zinc'),
          copper: num('copper'),
          manganese: num('manganese'),
          selenium: num('selenium'),
          iodine: num('iodine'),
          chromium: num('chromium'),
          molybdenum: num('molybdenum'),
        },
      },
    },
  };
}

function normalizeIngredientLookupId(ingredientId: string): string {
  const normalizedIngredientId = String(ingredientId ?? '').trim();
  return /^\d+$/.test(normalizedIngredientId) && !normalizedIngredientId.startsWith('spoonacular_')
    ? `spoonacular_${normalizedIngredientId}`
    : normalizedIngredientId;
}

function shouldHideMlForIngredientPayload(payload: Record<string, unknown> | undefined): boolean {
  return shouldHideMlForSpoonacularPayload(payload?.source, payload?.category, payload?.name);
}

// Get ingredient nutrition by recipe ingredient id (AI meal plan flow)
export const getIngredientNutritionByRecipeIngredientId = async (
  recipeIngredientId: string,
  amount: number,
  unit?: string
): Promise<IngredientNutritionResponse> => {
  try {
    const response = await api.get(`/ingredients/nutrition`, {
      params: {
        recipeIngredientId,
        amount,
        ...(unit ? { unit } : {}),
      },
    });
    const data = response.data;
    const payload = data?.data ?? data;
    const hasNutritionForAmount = payload && typeof payload === 'object' && 'nutritionForAmount' in payload && payload.nutritionForAmount;
    if (hasNutritionForAmount) {
      return normalizeNutritionForAmountToResponse(data);
    }
    return data;
  } catch (error) {
    throw error;
  }
};

// Get ingredient nutrition/details by exact ingredient lookup id.
export const getIngredientNutritionByIngredientId = async (
  ingredientId: string,
  amount?: number,
  unit?: string
): Promise<IngredientNutritionResponse> => {
  try {
    const detailsLookupId = normalizeIngredientLookupId(ingredientId);
    const hasUnit = typeof unit === 'string' && unit.trim().length > 0;
    const response = await api.get('/ingredients/details', {
      params: {
        id: detailsLookupId,
        ...(hasUnit && typeof amount === 'number' && Number.isFinite(amount) && amount > 0
          ? {
              amount,
              unit: unit.trim(),
            }
          : {}),
      },
    });
    return normalizeNutritionForAmountToResponse(response.data);
  } catch (error) {
    throw error;
  }
};

// Get ingredient nutrition information (legacy: ingredient + source)
export const getIngredientNutrition = async (
  ingredient: string,
  source: 'all' | 'spoonacular' | 'edamam' | 'internal' = 'all',
  amount: number,
  unit: string
): Promise<IngredientNutritionResponse> => {
  try {
    const normalizedIngredient = stripIngredientSourceSuffix(ingredient);
    const normalizedSource: 'all' | 'spoonacular' | 'internal' =
      source === 'spoonacular' || source === 'internal' || source === 'all'
        ? source
        : 'all';
    const response = await api.get(`/ingredients/nutrition`, {
      params: {
        ingredient: normalizedIngredient || ingredient,
        source: normalizedSource,
        amount: amount,
        unit: unit
      }
    });
    const data = response.data;
    const payload = data?.data ?? data;
    const hasNutritionForAmount = payload && typeof payload === 'object' && 'nutritionForAmount' in payload && payload.nutritionForAmount;
    if (hasNutritionForAmount) {
      return normalizeNutritionForAmountToResponse(data);
    }
    return data;
  } catch (error) {
    throw error;
  }
};

/** Unit option with display label (e.g. "serving (56g)") */
export type IngredientUnitOption = { value: string; label: string };

// Get ingredient details by ingredient object id (used for unit options in manual meal plan ingredient editor)
export const getIngredientDetails = async (
  ingredientId: string,
  amount: number = 1,
  unit?: string
): Promise<{
  success: boolean;
  data?: unknown;
  message?: string;
  units?: string[];
  /** Unit options with labels like "serving (56g)" when API returns possibleUnitWeights or servingWeights */
  unitOptions?: IngredientUnitOption[];
}> => {
  try {
    const detailsLookupId = normalizeIngredientLookupId(ingredientId);

    const response = await api.get('/ingredients/details', {
      params: {
        id: detailsLookupId,
        amount,
        ...(unit ? { unit } : {}),
      },
    });

    const raw = response.data;
    const payload = raw?.data ?? raw;
    const hideMl = shouldHideMlForIngredientPayload(payload as Record<string, unknown> | undefined);
    const unitMeasurements = collectIngredientPayloadUnitMeasurements(
      payload as Record<string, unknown> | undefined,
      hideMl,
    );
    const units = unitMeasurements.map((measurement) => measurement.value);
    const normalizedServingWeights = buildIngredientServingWeights(unitMeasurements);
    const gramsPerMl = hideMl
      ? null
      : resolveGramsPerMl(
          normalizedServingWeights as Record<string, unknown> | undefined,
          payload?.gramsPerMl as number | undefined,
        );

    const unitOptions = buildIngredientUnitOptions(unitMeasurements, gramsPerMl, hideMl);

    return {
      success: Boolean(raw?.success ?? true),
      data: payload,
      message: raw?.message,
      units,
      unitOptions,
    };
  } catch (error) {
    throw error;
  }
};

// Map legacy create/update payload to API body (POST/PUT /recipes/custom)
// API spec: name, description, servings, isPublic, ingredients, instructions, cuisines, mealTypes,
// dishTypes, dietLabels, allergens, intoleranceTags, tags, cookingTips, prepMinutes, cookMinutes,
// totalMinutes, imageUrl, cookingMethod, defaultServingId, defaultServingGrams. No healthLabels
// (use dietLabels). Ingredient nutrition is intentionally omitted; the API resolves linked
// ingredients and recalculates nutrition from backend ingredient data.
function toCustomRecipeApiBody(
  data: CreateCustomRecipeRequest | (Partial<CreateCustomRecipeRequest> & Record<string, unknown>)
): CustomRecipeCreateUpdateBody {
  const d = data as any;
  const normalizeServingId = (value: unknown): string | undefined =>
    typeof value === 'string' && value.trim().length > 0 ? value.trim().toLowerCase() : undefined;

  const seenServingIds = new Set<string>();
  let servingSizes: RecipeServingSize[] | undefined = Array.isArray(d.servingSizes)
    ? d.servingSizes
        .map((entry: any) => {
          const servingId = normalizeServingId(entry?.servingId);
          const grams = Number(entry?.grams);
          if (!servingId || !Number.isFinite(grams) || grams <= 0 || seenServingIds.has(servingId)) return null;
          seenServingIds.add(servingId);
          const ml = Number(entry?.ml);
          const densityGPerMl = Number(entry?.densityGPerMl);
          const quantity = Number(entry?.quantity);
          const label = typeof entry?.label === 'string' && entry.label.trim().length > 0
            ? entry.label.trim()
            : undefined;
          return {
            servingId,
            grams: Math.round(grams * 100) / 100,
            ...(Number.isFinite(ml) && ml > 0 ? { ml: Math.round(ml * 100) / 100 } : {}),
            ...(Number.isFinite(densityGPerMl) && densityGPerMl > 0 ? { densityGPerMl: Math.round(densityGPerMl * 10000) / 10000 } : {}),
            ...(Number.isFinite(quantity) && quantity > 0 ? { quantity: Math.round(quantity * 100) / 100 } : {}),
            ...(label ? { label } : {}),
            isDefault: entry?.isDefault === true,
          } as RecipeServingSize;
        })
        .filter((entry): entry is RecipeServingSize => entry !== null)
    : undefined;

  const defaultServingId = normalizeServingId(d.defaultServingId ?? d.portionSizeId);
  let defaultServingGrams = Number(d.defaultServingGrams);
  if (!Number.isFinite(defaultServingGrams) || defaultServingGrams <= 0) {
    defaultServingGrams = NaN;
  } else {
    defaultServingGrams = Math.round(defaultServingGrams * 100) / 100;
  }

  if (servingSizes?.length && defaultServingId) {
    const defaultServing = servingSizes.find((entry) => entry.servingId === defaultServingId);
    if (defaultServing) {
      if (!Number.isFinite(defaultServingGrams)) {
        defaultServingGrams = defaultServing.grams;
      } else if (Math.abs(defaultServing.grams - defaultServingGrams) > 0.01) {
        servingSizes = servingSizes.map((entry) => (
          entry.servingId === defaultServingId
            ? { ...entry, grams: defaultServingGrams, isDefault: true }
            : { ...entry, isDefault: false }
        ));
      }
    }
  }

  return {
    name: d.recipeName ?? d.name,
    aliases: Array.isArray(d.aliases)
      ? d.aliases.map((alias: unknown) => String(alias).trim()).filter(Boolean)
      : undefined,
    description: d.description,
    servings: d.servings,
    nutritionServings: d.nutritionServings,
    isPublic: d.isPublic,
    cookingTips: d.cookingTips ?? d.customNotes,
    ingredients: Array.isArray(d.ingredients)
      ? d.ingredients.map((ing: any) => ({
          ingredientId: ing.ingredientId,
          name: ing.name,
          amount: Number(ing.amount ?? ing.quantity ?? 0),
          unit: ing.unit || 'g',
          amountGrams: ing.amountGrams,
          amountMl: ing.amountMl,
          unitWeightGrams: ing.unitWeightGrams,
          unitWeightMl: ing.unitWeightMl,
          yieldFactor: ing.yieldFactor,
          retentionFactor: ing.retentionFactor,
          allergens: Array.isArray(ing.allergens) ? ing.allergens : undefined,
          allergenTagIds: Array.isArray(ing.allergenTagIds) ? ing.allergenTagIds : undefined,
          intoleranceTags: Array.isArray(ing.intoleranceTags) ? ing.intoleranceTags : undefined,
          intoleranceTagIds: Array.isArray(ing.intoleranceTagIds) ? ing.intoleranceTagIds : undefined,
          preparationNote: ing.preparationNote,
        }))
      : undefined,
    instructions: Array.isArray(d.instructions) ? d.instructions : (d.instructions && typeof d.instructions === 'string' ? d.instructions.split('\n').filter((s: string) => s.trim()) : undefined),
    cuisines: d.cuisineTypes ?? d.cuisines,
    mealTypes: d.mealTypes,
    dishTypes: d.dishTypes,
    dietLabels: (d.dietLabels ?? [])?.length ? (d.dietLabels ?? []) : undefined,
    allergens: (d.allergens ?? d.allergies)?.length ? (d.allergens ?? d.allergies) : undefined,
    manualAllergens: Array.isArray(d.manualAllergens) ? d.manualAllergens : undefined,
    intoleranceTags: d.intoleranceTags?.length ? d.intoleranceTags : undefined,
    manualIntoleranceTags: Array.isArray(d.manualIntoleranceTags) ? d.manualIntoleranceTags : undefined,
    tags: d.tags?.length ? d.tags : undefined,
    cookingMethod: d.cookingMethod,
    prepMinutes: d.prepTimeMinutes ?? d.prepMinutes,
    cookMinutes: d.cookTimeMinutes ?? d.cookMinutes,
    totalMinutes: d.totalTimeMinutes ?? d.totalMinutes,
    imageUrl: d.imageUrl ?? d.recipeImageUrl,
    defaultServingId,
    defaultServingGrams: Number.isFinite(defaultServingGrams) ? defaultServingGrams : undefined,
    servingSizes,
  };
}

// Create custom recipe (POST /recipes/custom)
export const createCustomRecipe = async (
  recipeData: CreateCustomRecipeRequest
): Promise<CreateCustomRecipeResponse & { data?: { id?: string } }> => {
  try {
    const body = toCustomRecipeApiBody(recipeData);
    if (!body.name || body.servings == null || body.isPublic == null || !body.ingredients?.length) {
      throw new Error('Missing required fields: name, servings, isPublic, ingredients');
    }
    const response = await api.post(`/recipes/custom`, body);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Update custom recipe (PUT /recipes/custom/{id})
export const updateCustomRecipe = async (
  recipeId: string,
  recipeData: Partial<CreateCustomRecipeRequest> & Record<string, unknown>
): Promise<CreateCustomRecipeResponse> => {
  try {
    const body = toCustomRecipeApiBody(recipeData);
    const response = await api.put(`/recipes/custom/${recipeId}`, body);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/** Body for POST /recipes/custom/upload-url (omit recipeId when creating a new recipe). */
export interface CustomRecipeImageUploadUrlRequest {
  fileName: string;
  mimeType: string;
  /** Recommended; must be ≤ 5 MB and must match the PUT body size if sent. */
  fileSize?: number;
  /** Include when replacing image on an existing recipe. */
  recipeId?: string;
}

/** Typical response from POST /recipes/custom/upload-url — use `fileUrl` as recipe `imageUrl` after PUT succeeds. */
export interface CustomRecipeImageUploadUrlPayload {
  method?: string;
  uploadUrl: string;
  requiredHeaders?: Record<string, string>;
  /** Store on the recipe as `imageUrl` (e.g. s3://bucket/key). */
  fileUrl?: string;
  expiresInSeconds?: number;
  imageUrl?: string;
  publicUrl?: string;
  publicImageUrl?: string;
}

const RECIPE_IMAGE_MAX_BYTES = 5 * 1024 * 1024;

/** Normalize to one of image/jpeg, image/png, image/webp (API accepts image/jpg as alias). */
export function normalizeRecipeImageMimeType(file: File): string {
  const raw = (file.type || '').toLowerCase();
  if (raw === 'image/jpg' || raw === 'image/pjpeg') return 'image/jpeg';
  if (raw === 'image/jpeg' || raw === 'image/png' || raw === 'image/webp') return raw;
  const name = file.name.toLowerCase();
  if (name.endsWith('.png')) return 'image/png';
  if (name.endsWith('.webp')) return 'image/webp';
  return 'image/jpeg';
}

/** Ensure fileName extension matches the declared image type. */
export function deriveRecipeImageFileName(file: File, mimeType: string): string {
  const raw = file.name?.trim() || 'recipe';
  const base = raw.replace(/\.[^.]+$/, '') || 'recipe';
  if (mimeType === 'image/png') return `${base}.png`;
  if (mimeType === 'image/webp') return `${base}.webp`;
  return /\.(jpe?g)$/i.test(raw) ? raw : `${base}.jpg`;
}

function buildRecipeImagePutHeaders(
  config: CustomRecipeImageUploadUrlPayload,
  file: File,
  mimeType: string,
): Headers {
  const h = new Headers();
  if (config.requiredHeaders) {
    for (const [key, value] of Object.entries(config.requiredHeaders)) {
      if (typeof value === 'string' && value.length > 0) {
        h.set(key, value);
      }
    }
  }
  if (h.has('Content-Length')) {
    h.set('Content-Length', String(file.size));
  }
  if (!h.has('Content-Type')) {
    h.set('Content-Type', mimeType);
  }
  return h;
}

/**
 * POST /recipes/custom/upload-url — presigned URL for recipe image upload.
 */
export const postCustomRecipeImageUploadUrl = async (
  params: CustomRecipeImageUploadUrlRequest,
): Promise<CustomRecipeImageUploadUrlPayload> => {
  const body: Record<string, unknown> = {
    fileName: params.fileName,
    mimeType: params.mimeType,
  };
  if (params.fileSize != null && Number.isFinite(params.fileSize)) {
    body.fileSize = params.fileSize;
  }
  if (params.recipeId?.trim()) {
    body.recipeId = params.recipeId.trim();
  }
  const response = await api.post(`/recipes/custom/upload-url`, body);
  const raw = response.data as { data?: CustomRecipeImageUploadUrlPayload } & CustomRecipeImageUploadUrlPayload;
  const payload = (raw?.data ?? raw) as CustomRecipeImageUploadUrlPayload;
  if (!payload || typeof payload.uploadUrl !== 'string') {
    throw new Error('Invalid response from recipe image upload URL endpoint.');
  }
  if (typeof payload.fileUrl !== 'string' || !payload.fileUrl.trim()) {
    throw new Error('Recipe upload URL response did not include fileUrl.');
  }
  return payload;
};

export interface UploadCustomRecipeImageFileOptions {
  recipeId?: string;
}

/**
 * Presigned flow: POST /recipes/custom/upload-url → PUT file bytes to uploadUrl → return `fileUrl` for recipe `imageUrl`.
 */
export const uploadCustomRecipeImageFile = async (
  file: File,
  options?: UploadCustomRecipeImageFileOptions,
): Promise<string> => {
  if (file.size > RECIPE_IMAGE_MAX_BYTES) {
    throw new Error('Image must be 5MB or smaller.');
  }
  const mimeType = normalizeRecipeImageMimeType(file);
  const fileName = deriveRecipeImageFileName(file, mimeType);
  const config = await postCustomRecipeImageUploadUrl({
    fileName,
    mimeType,
    fileSize: file.size,
    ...(options?.recipeId ? { recipeId: options.recipeId } : {}),
  });
  const method = (config.method || 'PUT').toUpperCase();
  const headers = buildRecipeImagePutHeaders(config, file, mimeType);
  const fileUrl = config.fileUrl!.trim();

  const uploadResponse = await fetch(config.uploadUrl, {
    method,
    headers,
    body: file,
  });
  if (!uploadResponse.ok) {
    const text = await uploadResponse.text().catch(() => '');
    throw new Error(
      text
        ? `Recipe image upload failed (${uploadResponse.status}): ${text}`
        : `Recipe image upload failed (${uploadResponse.status}).`
    );
  }
  return fileUrl;
};

// Modify ingredients in custom recipe (add, edit, delete)
export const modifyCustomRecipeIngredients = async (
  recipeId: string,
  modifications: Array<{
    type: 'add' | 'omit' | 'replace';
    originalIngredient?: string;
    originalAmount?: number;
    originalUnit?: string;
    newIngredient?: string;
    amount?: number;
    unit?: string;
    notes?: string;
  }>,
  autoCalculateNutrition: boolean = true
): Promise<{ success: boolean; message?: string; data?: any }> => {
  try {
    const response = await api.put(`/recipes/custom/${recipeId}/modify-ingredients`, {
      modifications,
      autoCalculateNutrition
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Get custom recipes (GET /recipes/custom) — page, pageSize (default 20, max 100),
// q, mealType, cuisine, diet, healthLabels, intolerances
export const getCustomRecipes = async (
  params: GetCustomRecipesParams = {}
): Promise<CustomRecipesListResponse> => {
  try {
    const queryParams = new URLSearchParams();
    const page = params.page ?? 1;
    const pageSize = Math.min(
      params.pageSize ?? params.limit ?? 20,
      100
    );
    queryParams.append('page', String(page));
    queryParams.append('pageSize', String(pageSize));
    if (params.q ?? params.search) queryParams.append('q', String(params.q ?? params.search));
    if (params.mealType ?? params.mealTypes) queryParams.append('mealType', String(params.mealType ?? params.mealTypes));
    if (params.cuisine ?? params.cuisineTypes) queryParams.append('cuisine', String(params.cuisine ?? params.cuisineTypes));
    if (params.diet ?? params.dietLabels) queryParams.append('diet', String(params.diet ?? params.dietLabels));
    if (params.healthLabels) queryParams.append('healthLabels', String(params.healthLabels));
    if (params.intolerances) queryParams.append('intolerances', String(params.intolerances));
    if (params.allergens) queryParams.append('allergens', String(params.allergens));
    if (params.allergies) queryParams.append('allergies', String(params.allergies));
    if (params.nutritionistId?.trim()) {
      queryParams.append('nutritionistId', params.nutritionistId.trim());
    }

    const response = await api.get(`/recipes/custom?${queryParams.toString()}`);
    const raw = response.data;
    // API returns { success, data: { items: Recipe[], pagination: {} }, message }
    const dataObj = raw?.data && typeof raw.data === 'object' ? raw.data : {};
    const list = Array.isArray(dataObj.items)
      ? dataObj.items
      : Array.isArray(raw?.data)
        ? raw.data
        : raw?.data?.recipes ?? raw?.recipes ?? [];
    const pagination = dataObj.pagination ?? raw?.pagination ?? {};
    const normalizedList = list.map((r: any) => {
      const n = r.nutritionPerServing ?? {};
      const servings = r.servings ?? 1;
      const canEdit = typeof r?.canEdit === 'boolean'
        ? r.canEdit
        : (typeof r?.isOwn === 'boolean' ? r.isOwn : false);
      const isOwn = typeof r?.isOwn === 'boolean' ? r.isOwn : canEdit;
      const servingSizes = Array.isArray(r.servingSizes)
        ? r.servingSizes
            .map((entry: any) => {
              const servingId = typeof entry?.servingId === 'string' ? entry.servingId.trim().toLowerCase() : '';
              const grams = Number(entry?.grams);
              if (!servingId || !Number.isFinite(grams) || grams <= 0) return null;
              const ml = Number(entry?.ml);
              const densityGPerMl = Number(entry?.densityGPerMl);
              const quantity = Number(entry?.quantity);
              return {
                servingId,
                grams: Math.round(grams * 100) / 100,
                ...(Number.isFinite(ml) && ml > 0 ? { ml: Math.round(ml * 100) / 100 } : {}),
                ...(Number.isFinite(densityGPerMl) && densityGPerMl > 0 ? { densityGPerMl: Math.round(densityGPerMl * 10000) / 10000 } : {}),
                ...(Number.isFinite(quantity) && quantity > 0 ? { quantity: Math.round(quantity * 100) / 100 } : {}),
                label: typeof entry?.label === 'string' && entry.label.trim().length > 0 ? entry.label.trim() : null,
                isDefault: entry?.isDefault === true,
              };
            })
            .filter((entry: any) => entry !== null)
        : [];
      const allergens = [
        ...normalizeStringArray(r.allergens),
        ...normalizeStringArray(r.allergyTags),
        ...normalizeStringArray(r.cautions),
      ].filter((value, index, arr) => arr.findIndex((candidate) => candidate.toLowerCase() === value.toLowerCase()) === index);
      return {
        ...r,
        isOwn,
        canEdit,
        canCopy: typeof r?.canCopy === 'boolean' ? r.canCopy : undefined,
        copyUnavailableReason:
          typeof r?.copyUnavailableReason === 'string' ? r.copyUnavailableReason : null,
        title: r.title ?? r.name ?? r.recipeName ?? '',
        aliases: Array.isArray(r.aliases) ? r.aliases : [],
        allergens,
        image: r.image ?? r.imageUrl ?? r.recipeImageUrl ?? '',
        totalTimeMinutes: r.totalTimeMinutes ?? r.totalMinutes ?? r.readyInMinutes,
        isVerified: r.isVerified,
        totalCalories: r.totalCalories ?? (n.calories != null ? n.calories * servings : 0),
        totalProtein: r.totalProtein ?? (n.protein != null ? n.protein * servings : 0),
        totalCarbs: r.totalCarbs ?? (n.carbohydrate != null ? n.carbohydrate * servings : 0),
        totalFat: r.totalFat ?? (n.total_fat != null ? n.total_fat * servings : 0),
        totalFiber: r.totalFiber ?? (n.dietary_fibre != null ? n.dietary_fibre * servings : (n.fiber != null ? n.fiber * servings : 0)),
        ingredientLines: r.ingredientLines ?? (Array.isArray(r.ingredients) ? r.ingredients.map((i: any) => `${i.name} ${i.amount} ${i.unit}`) : []),
        defaultServingId: r.defaultServingId ?? null,
        defaultServingGrams: r.defaultServingGrams ?? null,
        servingSizes,
      };
    });
    const hasNameSearch = String(params.q ?? params.search ?? '').trim().length > 0;
    const rankedList = hasNameSearch
      ? [...normalizedList].sort((left, right) => Number(Boolean(right.isOwn)) - Number(Boolean(left.isOwn)))
      : normalizedList;

    return {
      success: raw?.success !== false,
      data: rankedList,
      pagination: {
        page: pagination.page ?? page,
        limit: pagination.limit ?? pagination.pageSize ?? pageSize,
        totalCount: pagination.totalCount ?? pagination.total ?? 0,
        totalPages: pagination.totalPages ?? Math.max(1, Math.ceil((pagination.totalCount ?? pagination.total ?? 0) / (pagination.pageSize ?? pageSize))),
      },
      message: raw?.message,
      error: raw?.error,
    };
  } catch (error) {
    throw error;
  }
};

// Ensure label arrays are string[] (API may return objects with value/id/name)
function normalizeStringArray(arr: unknown): string[] {
  if (!Array.isArray(arr)) return [];
  return arr.map((item) => {
    if (typeof item === 'string') return item;
    if (item && typeof item === 'object') {
      const o = item as Record<string, unknown>;
      const v = o.value ?? o.id ?? o.name;
      return v != null ? String(v) : '';
    }
    return '';
  }).filter(Boolean);
}

// Normalize GET /recipes/custom/{id} response (API shape -> NormalizedRecipeDetailsData)
function normalizeCustomRecipeDetailsResponse(raw: { success?: boolean; data?: CustomRecipeDetailsApiData; message?: string; error?: string }): CustomRecipeDetailsResponse {
  const d = raw.data as CustomRecipeDetailsApiData | undefined;
  if (!d) {
    return { success: raw.success !== false, data: null, message: raw.message, error: raw.error };
  }
  const n = d.nutritionPerServing || {};
  const servings = d.servings;
  const nutritionServings = Number((d as { nutritionServings?: number }).nutritionServings ?? 1);
  const instructions = Array.isArray(d.instructions) ? d.instructions : [];
  const ingredientsList = Array.isArray(d.ingredients) ? d.ingredients : [];
  const legacyCookingMethodByDishType: Record<string, string> = {
    grilled: 'grilled',
    baked: 'baked',
    fried: 'fried_deep_fried',
  };
  const receivedDishTypes = Array.isArray(d.dishTypes) ? d.dishTypes : [];
  const normalizedDishTypes = receivedDishTypes.filter((dishType) => !legacyCookingMethodByDishType[dishType]);
  const derivedCookingMethodRaw =
    (typeof (d as { cookingMethod?: unknown }).cookingMethod === 'string'
      ? (d as { cookingMethod?: string }).cookingMethod ?? null
      : null)
    || receivedDishTypes.find((dishType) => legacyCookingMethodByDishType[dishType])
    || null;
  const derivedCookingMethod = derivedCookingMethodRaw
    ? (legacyCookingMethodByDishType[derivedCookingMethodRaw] ?? derivedCookingMethodRaw)
    : null;
  const servingSizes = Array.isArray(d.servingSizes)
    ? d.servingSizes
        .map((entry) => {
          const servingId = typeof entry?.servingId === 'string' ? entry.servingId.trim().toLowerCase() : '';
          const grams = Number(entry?.grams);
          if (!servingId || !Number.isFinite(grams) || grams <= 0) return null;
          const ml = Number(entry?.ml);
          const densityGPerMl = Number(entry?.densityGPerMl);
          const quantity = Number(entry?.quantity);
          return {
            servingId,
            grams: Math.round(grams * 100) / 100,
            ...(Number.isFinite(ml) && ml > 0 ? { ml: Math.round(ml * 100) / 100 } : {}),
            ...(Number.isFinite(densityGPerMl) && densityGPerMl > 0 ? { densityGPerMl: Math.round(densityGPerMl * 10000) / 10000 } : {}),
            ...(Number.isFinite(quantity) && quantity > 0 ? { quantity: Math.round(quantity * 100) / 100 } : {}),
            label: typeof entry?.label === 'string' && entry.label.trim().length > 0 ? entry.label.trim() : null,
            isDefault: entry?.isDefault === true,
          } as RecipeServingSize;
        })
        .filter((entry): entry is RecipeServingSize => entry !== null)
    : [];
  return {
    success: raw.success !== false,
    data: {
      id: d.id,
      isOwn: typeof d.isOwn === 'boolean' ? d.isOwn : undefined,
      canEdit: typeof d.canEdit === 'boolean' ? d.canEdit : (typeof d.isOwn === 'boolean' ? d.isOwn : undefined),
      canCopy: typeof d.canCopy === 'boolean'
        ? d.canCopy
        : ingredientsList.length > 0 && ingredientsList.every((ingredient) => Boolean(ingredient.ingredientId)),
      copyUnavailableReason:
        typeof d.copyUnavailableReason === 'string' ? d.copyUnavailableReason : null,
      nutritionistId: typeof d.nutritionistId === 'string' ? d.nutritionistId : undefined,
      recipeName: d.name,
      aliases: Array.isArray(d.aliases) ? d.aliases : [],
      recipeImageUrl: d.imageUrl ?? null,
      description: d.description,
      servings,
      nutritionServings,
      cuisines: Array.isArray(d.cuisines) ? d.cuisines : [],
      mealTypes: Array.isArray(d.mealTypes) ? d.mealTypes : [],
      dishTypes: normalizedDishTypes,
      cookingMethod: derivedCookingMethod,
      dietLabels: Array.isArray(d.dietLabels) ? d.dietLabels : [],
      tags: Array.isArray(d.tags) ? d.tags : [],
      allergens: [
        ...normalizeStringArray(d.allergens),
        ...normalizeStringArray((d as { allergyTags?: unknown }).allergyTags),
        ...normalizeStringArray((d as { cautions?: unknown }).cautions),
      ].filter((value, index, arr) => arr.findIndex((candidate) => candidate.toLowerCase() === value.toLowerCase()) === index),
      manualAllergens: normalizeStringArray((d as { manualAllergens?: unknown }).manualAllergens),
      ingredientDerivedAllergens: normalizeStringArray((d as { ingredientDerivedAllergens?: unknown }).ingredientDerivedAllergens),
      intoleranceTags: Array.isArray(d.intoleranceTags) ? d.intoleranceTags : [],
      defaultServingId: d.defaultServingId ?? null,
      defaultServingGrams: d.defaultServingGrams ?? null,
      servingSizes,
      prepTimeMinutes: d.prepMinutes ?? 0,
      cookTimeMinutes: d.cookMinutes ?? 0,
      totalTimeMinutes: d.totalMinutes ?? 0,
      isPublic: d.isPublic ?? false,
      isVerified: d.isVerified,
      ingredientLines: Array.isArray(d.ingredientLines) ? d.ingredientLines : [],
      cookingInstructions: instructions.map((s) => (typeof s === 'object' && s && 'instruction' in s ? s.instruction : String(s ?? ''))),
      cookingTips:
        typeof d.cookingTips === 'string'
          ? d.cookingTips
          : typeof d.customNotes === 'string'
            ? d.customNotes
            : '',
      ingredients: ingredientsList.map((i) => ({
        id: i.id,
        ingredientId: i.ingredientId,
        name: i.name,
        unit: i.servingId ?? 'g',
        quantity: i.amount ?? 0,
        amountGrams: i.amountGrams,
        amountMl: (i as { amountMl?: number; servingMl?: number }).amountMl
          ?? (i as { amountMl?: number; servingMl?: number }).servingMl,
        unitWeightGrams: i.unitWeightGrams ?? (
          typeof i.amount === 'number'
          && i.amount > 0
          && typeof i.amountGrams === 'number'
          && i.amountGrams > 0
            ? Math.round((i.amountGrams / i.amount) * 100) / 100
            : undefined
        ),
        unitWeightMl: (i as { unitWeightMl?: number }).unitWeightMl,
        allergens: normalizeStringArray((i as { allergens?: unknown }).allergens),
        allergenTagIds: normalizeStringArray((i as { allergenTagIds?: unknown }).allergenTagIds),
        yieldFactor: (i as { yieldFactor?: number | null }).yieldFactor,
        retentionFactor: (i as { retentionFactor?: number | null }).retentionFactor,
        preparationNote: i.preparationNote ?? null,
        nutrition: i.nutrition,
      })),
      nutritionPerServing: d.nutritionPerServing,
      caloriesPerServing: Number(n.calories ?? 0),
      proteinPerServing: Number(n.protein ?? 0),
      carbsPerServing: Number(n.carbohydrate ?? 0),
      fatPerServing: Number(n.total_fat ?? 0),
      fiberPerServing: Number(n.dietary_fibre ?? 0),
      alcoholPerServing: Number((n as Record<string, unknown>).alcohol ?? 0) || undefined,
      saturatedFatPerServing: Number((n.saturated_fat as number) ?? 0),
      sugarPerServing: Number(n.sugar ?? 0),
      sodiumPerServing: Number(n.sodium ?? 0),
      ironPerServing: Number(n.iron ?? 0),
      calciumPerServing: Number(n.calcium ?? 0),
      potassiumPerServing: Number(n.potassium ?? 0),
      vitaminCPerServing: Number(n.vitamin_c ?? 0),
      createdAt: d.createdAt,
      updatedAt: d.updatedAt,
    },
    message: raw.message,
    error: raw.error,
  };
}

// Get custom recipe details (GET /recipes/custom/{id})
export const getCustomRecipeDetails = async (
  recipeId: string
): Promise<CustomRecipeDetailsResponse> => {
  try {
    const response = await api.get(`/recipes/custom/${recipeId}`);
    return normalizeCustomRecipeDetailsResponse(response.data);
  } catch (error) {
    throw error;
  }
};

export interface GetAdminCustomRecipesParams {
  page?: number;
  pageSize?: number;
  q?: string;
  approvalStatus?: 'all' | 'pending' | 'approved';
  sharingStatus?: 'all' | 'public' | 'private';
}

export const getAdminCustomRecipes = async (
  params: GetAdminCustomRecipesParams = {}
): Promise<CustomRecipesListResponse> => {
  const queryParams = new URLSearchParams();
  const page = params.page ?? 1;
  const pageSize = Math.min(params.pageSize ?? 25, 100);
  queryParams.append('page', String(page));
  queryParams.append('pageSize', String(pageSize));
  if (params.q?.trim()) queryParams.append('q', params.q.trim());
  if (params.approvalStatus && params.approvalStatus !== 'all') {
    queryParams.append('approvalStatus', params.approvalStatus);
  }
  if (params.sharingStatus && params.sharingStatus !== 'all') {
    queryParams.append('sharingStatus', params.sharingStatus);
  }

  const response = await api.get(`/recipes/custom/admin?${queryParams.toString()}`);
  const raw = response.data;
  const dataObj = raw?.data && typeof raw.data === 'object' ? raw.data : {};
  const list = Array.isArray(dataObj.items) ? dataObj.items : [];
  const pagination = dataObj.pagination ?? {};

  return {
    success: raw?.success !== false,
    data: list.map((r: any) => {
      const n = r.nutritionPerServing ?? {};
      const servings = r.servings ?? 1;
      return {
        ...r,
        title: r.title ?? r.name ?? r.recipeName ?? '',
        image: r.image ?? r.imageUrl ?? r.recipeImageUrl ?? '',
        totalTimeMinutes: r.totalTimeMinutes ?? r.totalMinutes ?? r.readyInMinutes,
        totalCalories: r.totalCalories ?? (n.calories != null ? n.calories * servings : 0),
        totalProtein: r.totalProtein ?? (n.protein != null ? n.protein * servings : 0),
        totalCarbs: r.totalCarbs ?? (n.carbohydrate != null ? n.carbohydrate * servings : 0),
        totalFat: r.totalFat ?? (n.total_fat != null ? n.total_fat * servings : 0),
        ingredientLines: Array.isArray(r.ingredientLines) ? r.ingredientLines : [],
      };
    }),
    pagination: {
      page: pagination.page ?? page,
      limit: pagination.limit ?? pagination.pageSize ?? pageSize,
      totalCount: pagination.totalCount ?? pagination.total ?? 0,
      totalPages: pagination.totalPages ?? Math.max(1, Math.ceil((pagination.totalCount ?? pagination.total ?? 0) / (pagination.pageSize ?? pageSize))),
    },
    message: raw?.message,
    error: raw?.error,
  };
};

export const getAdminCustomRecipeDetails = async (
  recipeId: string
): Promise<CustomRecipeDetailsResponse> => {
  const response = await api.get(`/recipes/custom/admin/${recipeId}`);
  return normalizeCustomRecipeDetailsResponse(response.data);
};

export const approveAdminCustomRecipe = async (
  recipeId: string
): Promise<{ success: boolean; message?: string; data?: { id: string; isPublic: boolean; isVerified: boolean; updatedAt: string } }> => {
  const response = await api.post(`/recipes/custom/admin/${recipeId}/approve`);
  return response.data;
};

export interface AdminCustomRecipeAuditLogItem {
  id?: string;
  recipeId: string;
  action: 'approved' | 'deleted' | string;
  actor?: {
    id?: string | null;
    email?: string | null;
    role?: string | null;
    roles?: string[];
  };
  recipeSnapshot?: {
    name?: string;
    nutritionistId?: string | null;
    isPublic?: boolean;
    isVerified?: boolean;
    isActive?: boolean;
  };
  metadata?: Record<string, unknown>;
  createdAt: string;
}

export const getAdminCustomRecipeAuditLog = async (
  recipeId: string
): Promise<{ success: boolean; data: AdminCustomRecipeAuditLogItem[]; message?: string; error?: string }> => {
  const response = await api.get(`/recipes/custom/admin/${recipeId}/audit`);
  return {
    success: response.data?.success !== false,
    data: Array.isArray(response.data?.data) ? response.data.data : [],
    message: response.data?.message,
    error: response.data?.error,
  };
};

export const deleteAdminCustomRecipe = async (
  recipeId: string
): Promise<{ success: boolean; message?: string }> => {
  const response = await api.delete(`/recipes/custom/admin/${recipeId}`);
  return response.data;
};

export const copyCustomRecipe = async (
  recipeId: string,
  options: {
    isPublic?: boolean;
    recipeName?: string;
  } = {}
): Promise<CreateCustomRecipeResponse & { data?: { id?: string } }> => {
  const body = {
    isPublic: options.isPublic ?? false,
    ...(options.recipeName?.trim() ? { recipeName: options.recipeName.trim() } : {}),
  };
  const response = await api.post(`/recipes/custom/${recipeId}/copy`, body);
  return response.data;
};

// Delete custom recipe (DELETE /recipes/custom/{id})
export const deleteCustomRecipe = async (
  recipeId: string
): Promise<DeleteCustomRecipeResponse> => {
  try {
    const response = await api.delete(`/recipes/custom/${recipeId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Map frontend IngredientModification to API IngredientOverride
function modificationsToIngredientOverrides(
  modifications: RecipeCustomizations['modifications']
): IngredientOverride[] {
  if (!Array.isArray(modifications) || modifications.length === 0) return [];
  return modifications
    .filter((m) => {
      // add doesn't require originalIngredientId; others do
      if (m.type === 'add') return true;
      return m.originalIngredientId && (m.type === 'replace' || m.type === 'modify' || m.type === 'omit');
    })
    .map((m) => {
      // Map frontend 'omit' to API 'remove'
      const action = m.type === 'omit' ? 'remove' : m.type;

      const base: IngredientOverride = {
        action,
        reason: 'preference',
      };

      // originalIngredientId is required for modify/replace/remove, not for add
      if (m.type !== 'add' && m.originalIngredientId) {
        base.originalIngredientId = m.originalIngredientId;
      }

      if (action === 'modify') {
        if (m.originalAmount != null) base.originalAmount = m.originalAmount;
        if (typeof m.amount === 'number' && !Number.isNaN(m.amount)) base.newAmount = m.amount;
        if (typeof m.unit === 'string' && m.unit.trim()) base.servingId = m.unit.trim();
        if (m.newAmountGrams != null) base.newAmountGrams = m.newAmountGrams;
      } else if (action === 'replace') {
        if (m.replacementIngredientId) base.replacementIngredientId = m.replacementIngredientId;
        if (m.newIngredient) base.replacementName = m.newIngredient;
        if (typeof m.amount === 'number' && !Number.isNaN(m.amount)) base.newAmount = m.amount;
        if (typeof m.unit === 'string' && m.unit.trim()) base.servingId = m.unit.trim();
        if (m.newAmountGrams != null) base.newAmountGrams = m.newAmountGrams;
      } else if (action === 'add') {
        if (m.replacementIngredientId) base.replacementIngredientId = m.replacementIngredientId;
        if (m.newIngredient) base.replacementName = m.newIngredient;
        if (typeof m.amount === 'number' && !Number.isNaN(m.amount)) base.newAmount = m.amount;
        if (typeof m.unit === 'string' && m.unit.trim()) base.servingId = m.unit.trim();
        if (m.newAmountGrams != null) base.newAmountGrams = m.newAmountGrams;
      }
      // 'remove' only needs originalIngredientId and action (already set)

      return base;
    });
}

// Recipe Customizations (automated drafts) - via PUT /meal-plans/drafts/{id}
// Note: Notes are updated separately via updateDraftRecipeNotes to avoid overwriting existing notes
export const updateRecipeCustomizations = async (
  draftId: string,
  dayNumber: number,
  mealOrder: number,
  recipeId: string,
  customizations: RecipeCustomizations
): Promise<UpdateCustomizationsResponse> => {
  if (!Number.isFinite(mealOrder) || mealOrder < 1) {
    throw new Error('Meal order is required and must be greater than 0');
  }
  const ingredientOverrides = modificationsToIngredientOverrides(customizations.modifications);
  const response = await updateDraft(draftId, {
    action: 'updateCustomization',
    dayNumber,
    mealOrder,
    recipeId,
    customization: {
      ...(customizations.nutritionServings != null && { nutritionServings: customizations.nutritionServings }),
      ...(ingredientOverrides.length > 0 && { ingredientOverrides }),
    },
  });
  return { success: response.success ?? false, message: response.message ?? '', data: response.data };
};

// Remove ingredient from an automated draft recipe - payload aligned to backend curl contract
export const removeDraftRecipeIngredient = async (
  draftId: string,
  dayNumber: number,
  mealOrder: number,
  recipeId: string,
  originalIngredientId: string
): Promise<UpdateCustomizationsResponse> => {
  if (!Number.isFinite(mealOrder) || mealOrder < 1) {
    throw new Error('Meal order is required and must be greater than 0');
  }
  const response = await api.put(`/meal-plans/drafts/${draftId}`, {
    action: 'updateCustomization',
    dayNumber,
    mealOrder,
    recipeId,
    customization: {
      ingredientOverrides: [
        {
          originalIngredientId,
          action: 'remove',
          reason: 'preference',
        },
      ],
    },
  });

  return {
    success: response.data?.success ?? false,
    message: response.data?.message ?? '',
    data: response.data?.data,
  };
};

// Update notes for a recipe in draft - PUT /meal-plans/{draftId}/notes
export const updateDraftRecipeNotes = async (
  draftId: string,
  dayNumber: number,
  mealName: string,
  recipeId: string,
  notes: string
): Promise<UpdateCustomizationsResponse> => {
  const normalizedMealName = String(mealName ?? '').trim();
  if (!normalizedMealName) {
    throw new Error('Meal name is required');
  }
  const response = await updateMealPlanNotes(draftId, {
    level: 'item',
    day: dayNumber,
    mealName: normalizedMealName,
    recipeId,
    notes,
  });
  return {
    success: response.success ?? false,
    message: response.message ?? '',
    data: response.data,
  };
};

// Update nutrition servings for a recipe in automated draft - via PUT /meal-plans/drafts/{id}
export const updateNutritionServings = async (
  draftId: string,
  dayNumber: number,
  mealOrder: number,
  recipeId: string,
  nutritionServings: number,
  _source?: any
): Promise<UpdateCustomizationsResponse> => {
  if (nutritionServings < 0.1 || nutritionServings > 20) {
    throw new Error('Nutrition servings must be between 0.1 and 20');
  }
  if (!Number.isFinite(mealOrder) || mealOrder < 1) {
    throw new Error('Meal order is required and must be greater than 0');
  }
  const response = await updateDraft(draftId, {
    action: 'updateCustomization',
    dayNumber,
    mealOrder,
    recipeId,
    customization: { nutritionServings },
  });
  return { success: response.success ?? false, message: response.message ?? '', data: response.data };
};

// Get meal plan by ID (finalized view) - GET /meal-plans/{id}; returns data.days[].meals[].items[].id (itemId)
export const getMealPlan = async (mealPlanId: string): Promise<MealPlanByIdResponse> => {
  const response = await api.get(`/meal-plans/${mealPlanId}`);
  return response.data;
};

/** Change dates for manual meal plan only. Shift: newStartDate only. Change duration: newStartDate + newEndDate (1–7 days). Shorten = delete last day(s); extend = add empty day(s). */
export const changeMealPlanDates = async (
  mealPlanId: string,
  body: ChangeMealPlanDatesRequest
): Promise<ChangeMealPlanDatesResponse> => {
  const response = await api.put(`/meal-plans/${mealPlanId}/change-dates`, body);
  return response.data;
};

/** Update day name only. Works for manual drafts, automated drafts, and finalized plans. */
export const updateMealPlanDayName = async (
  mealPlanId: string,
  body: { day: number; dayName: string | null }
): Promise<{ success: boolean; message?: string; data?: { day: number; dayName: string | null } }> => {
  const response = await api.put(`/meal-plans/${mealPlanId}/day-name`, body);
  return response.data;
};

/** Delete one day from a meal plan or manual draft. */
export const deleteMealPlanDay = async (
  mealPlanId: string,
  dayNumber: number
): Promise<{ success: boolean; message?: string; data?: unknown }> => {
  const response = await api.delete(`/meal-plans/${mealPlanId}/day/${dayNumber}`);
  return response.data;
};

/** Reorder days in a manual meal plan or manual draft. */
export const reorderMealPlanDays = async (
  mealPlanId: string,
  body: { dayOrder: number[] }
): Promise<{ success: boolean; message?: string; data?: { id: string; isDraft: boolean; dayOrder: number[] } }> => {
  const response = await api.put(`/meal-plans/${mealPlanId}/reorder-days`, body);
  return response.data;
};

/** API often sends `{ quantity }` (and sometimes strings); coerce so kcal/macros stay fractional. */
function readFlatNutrientNumber(raw: unknown): number | undefined {
  if (raw == null || raw === '') return undefined;
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  if (typeof raw === 'string') {
    const parsed = Number(raw.trim());
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  if (typeof raw === 'object' && raw !== null && 'quantity' in raw) {
    const q = Number((raw as { quantity?: unknown }).quantity);
    return Number.isFinite(q) ? q : undefined;
  }
  return undefined;
}

function flatNutritionToMacros(nut: Record<string, unknown> | undefined): FinalizedMeal['nutrition'] | undefined {
  if (!nut || typeof nut !== 'object') return undefined;
  const macrosRecord = (nut as { macros?: Record<string, unknown> }).macros;
  const cal =
    readFlatNutrientNumber(nut.calories)
    ?? readFlatNutrientNumber(macrosRecord?.calories);
  const protein =
    readFlatNutrientNumber(nut.protein)
    ?? readFlatNutrientNumber(macrosRecord?.protein);
  const carbs =
    readFlatNutrientNumber(nut.carbohydrate)
    ?? readFlatNutrientNumber(nut.carbs)
    ?? readFlatNutrientNumber(macrosRecord?.carbohydrate)
    ?? readFlatNutrientNumber(macrosRecord?.carbs);
  const fat =
    readFlatNutrientNumber(nut.total_fat)
    ?? readFlatNutrientNumber(nut.fat)
    ?? readFlatNutrientNumber(macrosRecord?.fat);
  const fiber =
    readFlatNutrientNumber(nut.dietary_fibre)
    ?? readFlatNutrientNumber(nut.fiber);
  const sodium = readFlatNutrientNumber(nut.sodium);
  const sugar = readFlatNutrientNumber(nut.sugar);
  const cholesterol = readFlatNutrientNumber(nut.cholesterol);
  if (cal == null && protein == null && carbs == null && fat == null) return undefined;
  const macros = {
    calories: cal ?? 0,
    protein: protein ?? 0,
    carbs: carbs ?? 0,
    fat: fat ?? 0,
    fiber,
    sodium,
    sugar,
    cholesterol,
  };
  return {
    calories: macros.calories,
    protein: macros.protein,
    carbs: macros.carbs,
    fat: macros.fat,
    fiber: macros.fiber,
    sodium: macros.sodium,
    sugar: macros.sugar,
    cholesterol: macros.cholesterol,
    macros,
  };
}

function aggregateMealItemsNutrition(recipes: FinalizedMeal[]): FinalizedMeal['nutrition'] | undefined {
  if (!Array.isArray(recipes) || recipes.length === 0) return undefined;

  const totals = {
    calories: 0,
    protein: 0,
    carbs: 0,
    fat: 0,
    fiber: 0,
    sodium: 0,
    sugar: 0,
    cholesterol: 0,
  };

  let hasNutrition = false;

  recipes.forEach((recipe) => {
    const nut = (recipe.nutrition?.macros ?? recipe.nutrition) as Record<string, unknown> | undefined;
    if (!nut) return;
    hasNutrition = true;
    totals.calories += readFlatNutrientNumber(nut.calories) ?? 0;
    totals.protein += readFlatNutrientNumber(nut.protein) ?? 0;
    totals.carbs +=
      readFlatNutrientNumber(nut.carbohydrate) ?? readFlatNutrientNumber(nut.carbs) ?? 0;
    totals.fat += readFlatNutrientNumber(nut.total_fat) ?? readFlatNutrientNumber(nut.fat) ?? 0;
    totals.fiber += readFlatNutrientNumber(nut.dietary_fibre) ?? readFlatNutrientNumber(nut.fiber) ?? 0;
    totals.sodium += readFlatNutrientNumber(nut.sodium) ?? 0;
    totals.sugar += readFlatNutrientNumber(nut.sugar) ?? 0;
    totals.cholesterol += readFlatNutrientNumber(nut.cholesterol) ?? 0;
  });

  if (!hasNutrition) return undefined;

  return {
    calories: totals.calories,
    protein: totals.protein,
    carbs: totals.carbs,
    fat: totals.fat,
    fiber: totals.fiber,
    sodium: totals.sodium,
    sugar: totals.sugar,
    cholesterol: totals.cholesterol,
    macros: { ...totals },
  };
}

function buildEmptyMealNutrition(): NonNullable<FinalizedMeal['nutrition']> {
  const macros = {
    calories: 0,
    protein: 0,
    carbs: 0,
    fat: 0,
    fiber: 0,
    sodium: 0,
    sugar: 0,
    cholesterol: 0,
  };

  return {
    ...macros,
    macros,
  };
}

function filterItemsForSelectedOptionGroup<T extends { optionGroupId?: string | null }>(
  items: T[],
  selectedOptionGroupId: unknown
): T[] {
  if (!Array.isArray(items) || items.length === 0) return [];
  const optionGroupIds = items
    .map((item) => readOptionalString(item.optionGroupId))
    .filter((value): value is string => Boolean(value));
  if (optionGroupIds.length === 0) return items;

  const selectedGroupId = readOptionalString(selectedOptionGroupId);
  if (!selectedGroupId) return [];

  const selectedItems = items.filter((item) => readOptionalString(item.optionGroupId) === selectedGroupId);
  if (selectedItems.length > 0) return selectedItems;

  return [];
}

function readOptionalString(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function normalizeCulinaryRating(value: unknown): CulinaryRating | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const rating = value as Record<string, unknown>;
  const score = Number(rating.score);
  if (!Number.isFinite(score) || score < 0 || score > 10) return null;

  const label = readOptionalString(rating.label) ?? (
    score >= 9 ? 'Excellent'
      : score >= 8 ? 'Very good'
        : score >= 7 ? 'Good'
          : score >= 6 ? 'Fair'
            : 'Needs improvement'
  );
  const classificationValue = readOptionalString(rating.classification);
  const classification = classificationValue
    && ['sweet', 'savoury', 'mixed', 'beverage'].includes(classificationValue)
    ? classificationValue as CulinaryRating['classification']
    : null;
  const confidenceValue = Number(rating.classificationConfidence);
  const classificationConfidence = Number.isFinite(confidenceValue)
    ? Math.min(1, Math.max(0, confidenceValue))
    : null;
  const componentsRecord = rating.components && typeof rating.components === 'object'
    && !Array.isArray(rating.components)
    ? rating.components as Record<string, unknown>
    : {};
  const component = (key: string): number | null => {
    const numeric = Number(componentsRecord[key]);
    return Number.isFinite(numeric) && numeric >= 0 && numeric <= 10 ? numeric : null;
  };
  const signalsRecord = rating.detectedSignals && typeof rating.detectedSignals === 'object'
    && !Array.isArray(rating.detectedSignals)
    ? rating.detectedSignals as Record<string, unknown>
    : {};

  return {
    score: Math.round(score * 10) / 10,
    scale: 10,
    label,
    advisory: true,
    scoringVersion: readOptionalString(rating.scoringVersion) ?? null,
    promptVersion: readOptionalString(rating.promptVersion) ?? null,
    classification,
    classificationConfidence,
    components: {
      flavour: component('flavour'),
      seasoning: component('seasoning'),
      moisture: component('moisture'),
      texture: component('texture'),
      cuisineAuthenticity: component('cuisineAuthenticity'),
    },
    detectedSignals: {
      flavour: normalizeStringArray(signalsRecord.flavour),
      seasoning: normalizeStringArray(signalsRecord.seasoning),
      moisture: normalizeStringArray(signalsRecord.moisture),
      texture: normalizeStringArray(signalsRecord.texture),
      cuisine: normalizeStringArray(signalsRecord.cuisine),
    },
    warnings: normalizeStringArray(rating.warnings),
  };
}

function buildRecipeGenerationMetadata(source: Record<string, unknown>): Partial<FinalizedMeal> {
  const indicatorRecord =
    source.generationIndicator && typeof source.generationIndicator === 'object'
      ? source.generationIndicator as Record<string, unknown>
      : {};
  const generatedVia =
    readOptionalString(source.generatedVia) ??
    readOptionalString(source.generated_via) ??
    readOptionalString(indicatorRecord.type);
  if (!generatedVia) {
    return {};
  }

  const generatedFromRecipeId =
    readOptionalString(source.generatedFromRecipeId) ??
    readOptionalString(source.generated_from_recipe_id) ??
    readOptionalString(indicatorRecord.generatedFromRecipeId);
  const generatedFromSourceId =
    readOptionalString(source.generatedFromSourceId) ??
    readOptionalString(source.generated_from_source_id) ??
    readOptionalString(indicatorRecord.generatedFromSourceId);
  const generatedFromTitle =
    readOptionalString(source.generatedFromTitle) ??
    readOptionalString(source.generated_from_title) ??
    readOptionalString(indicatorRecord.generatedFromTitle);
  const generationGroupId =
    readOptionalString(source.generationGroupId) ??
    readOptionalString(source.generation_group_id) ??
    readOptionalString(indicatorRecord.generationGroupId);
  const generatedAt =
    readOptionalString(source.generatedAt) ??
    readOptionalString(source.generated_at) ??
    readOptionalString(indicatorRecord.generatedAt);
  const label =
    readOptionalString(indicatorRecord.label) ??
    (generatedVia === 'smart_edit' ? 'Smart Edit' : 'Regenerated');

  return {
    generatedVia,
    generatedFromRecipeId: generatedFromRecipeId ?? null,
    generatedFromSourceId: generatedFromSourceId ?? null,
    generatedFromTitle: generatedFromTitle ?? null,
    generationGroupId: generationGroupId ?? null,
    generatedAt: generatedAt ?? null,
    generationIndicator: {
      type: generatedVia,
      label,
      generatedFromRecipeId: generatedFromRecipeId ?? null,
      generatedFromSourceId: generatedFromSourceId ?? null,
      generatedFromTitle: generatedFromTitle ?? null,
      generationGroupId: generationGroupId ?? null,
      generatedAt: generatedAt ?? null,
    },
  };
}

function buildNutritionSummaryFromFlat(
  avg: Record<string, unknown> | undefined,
  daily: Array<{ dayNumber: number; nutrition?: Record<string, unknown> }> | undefined
): FinalizedMealPlanData['nutritionSummary'] {
  const fromFlat = (n: Record<string, unknown> | undefined) => {
    if (!n) return { macros: { calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0, sugar: 0, sodium: 0, cholesterol: 0, saturatedFat: 0 }, micronutrients: {} as any };
    return {
      macros: {
        calories: readFlatNutrientNumber(n.calories) ?? 0,
        protein: readFlatNutrientNumber(n.protein) ?? 0,
        carbs: readFlatNutrientNumber(n.carbohydrate) ?? readFlatNutrientNumber(n.carbs) ?? 0,
        fat: readFlatNutrientNumber(n.total_fat) ?? readFlatNutrientNumber(n.fat) ?? 0,
        fiber: readFlatNutrientNumber(n.dietary_fibre) ?? readFlatNutrientNumber(n.fiber) ?? 0,
        sugar: readFlatNutrientNumber(n.sugar) ?? 0,
        sodium: readFlatNutrientNumber(n.sodium) ?? 0,
        cholesterol: readFlatNutrientNumber(n.cholesterol) ?? 0,
        saturatedFat: readFlatNutrientNumber(n.saturated_fat) ?? 0,
      },
      micronutrients: {} as any,
    };
  };
  const dailyNut = daily?.[0]?.nutrition as Record<string, unknown> | undefined;
  const macros = fromFlat(avg ?? dailyNut).macros;
  const empty = fromFlat(undefined);
  return {
    overall: { ...empty, macros },
    dailyAverages: { ...empty, macros },
  } as FinalizedMealPlanData['nutritionSummary'];
}

/** Day suggestion shape from finalized meal plan API (daySuggestions) */
interface DaySuggestionMeal {
  mealOrder?: number;
  mealName?: string;
  mealType?: string;
  mealTime?: string;
  recipeSuggestions?: Array<{
    id: string;
    sourceId?: string;
    title?: string;
    image?: string;
    servings?: number;
    nutritionServings?: number;
    calories?: number;
    protein?: number;
    carbs?: number;
    fat?: number;
    fiber?: number;
    nutrition?: Record<string, unknown>;
    customization?: { notes?: string } | null;
    cuisines?: string[];
    cuisineTypes?: string[];
    cuisineType?: string[];
    mealTypes?: string[];
    mealType?: string[];
    itemId?: string;
    mealPlanItemId?: string;
    itemType?: 'recipe' | 'ingredient' | string;
    optionGroupId?: string | null;
    optionGroupLabel?: string | null;
    selectedOptionGroupId?: string | null;
    [key: string]: unknown;
  }>;
  selectedRecipeId?: string;
  invalidSelection?: {
    recipeId?: string | null;
    code?: string;
    violations?: string[];
    message?: string;
  } | null;
  selectedOptionGroupId?: string | null;
  recipes?: Array<Record<string, unknown>>;
  ingredients?: Array<Record<string, unknown>>;
  /** Meal-slot rollup (calories, macros, etc.); applied to normalized slot `nutrition` when present. */
  totalNutrition?: Record<string, unknown>;
  [key: string]: unknown;
}

interface DaySuggestion {
  dayNumber?: number;
  date?: string;
  meals?: DaySuggestionMeal[];
  [key: string]: unknown;
}

function buildDayTotalsFromFlat(nut: Record<string, unknown> | undefined): { macros: { calories: number; protein: number; carbs: number; fat: number; fiber: number; sugar: number; sodium: number; cholesterol: number; saturatedFat: number }; micronutrients: { vitamins: Record<string, unknown>; minerals: Record<string, unknown> } } {
  if (!nut) {
    return { macros: { calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0, sugar: 0, sodium: 0, cholesterol: 0, saturatedFat: 0 }, micronutrients: { vitamins: {} as any, minerals: {} as any } };
  }
  return {
    macros: {
      calories: readFlatNutrientNumber(nut.calories) ?? 0,
      protein: readFlatNutrientNumber(nut.protein) ?? 0,
      carbs: readFlatNutrientNumber(nut.carbohydrate) ?? readFlatNutrientNumber(nut.carbs) ?? 0,
      fat: readFlatNutrientNumber(nut.total_fat) ?? readFlatNutrientNumber(nut.fat) ?? 0,
      fiber: readFlatNutrientNumber(nut.dietary_fibre) ?? readFlatNutrientNumber(nut.fiber) ?? 0,
      sugar: readFlatNutrientNumber(nut.sugar) ?? 0,
      sodium: readFlatNutrientNumber(nut.sodium) ?? 0,
      cholesterol: readFlatNutrientNumber(nut.cholesterol) ?? 0,
      saturatedFat: readFlatNutrientNumber(nut.saturated_fat) ?? 0,
    },
    micronutrients: { vitamins: {} as any, minerals: {} as any },
  };
}

const MEAL_PLAN_TARGET_DAILY_KEYS = [
  'calories',
  'proteinMin',
  'proteinMax',
  'carbsMin',
  'carbsMax',
  'fatMin',
  'fatMax',
] as const;

function pickFirstPositiveDailyKcal(...candidates: unknown[]): number | undefined {
  for (const v of candidates) {
    const n = typeof v === 'string' && v.trim() !== '' ? Number(v) : Number(v);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return undefined;
}

function pickFirstPositiveDailyTarget(...candidates: unknown[]): number | undefined {
  for (const value of candidates) {
    const n = typeof value === 'string' && value.trim() !== '' ? Number(value) : Number(value);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return undefined;
}

function readLegacyTargetRangeEndpoint(value: unknown, endpoint: 'min' | 'max'): number | undefined {
  const direct = pickFirstPositiveDailyTarget(value);
  if (direct !== undefined) return direct;
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const range = value as Record<string, unknown>;
  return pickFirstPositiveDailyTarget(range[endpoint], range.value);
}

/** Merge API `targetDaily` onto legacy `targetProtein`/`targetFat`/… so partial API objects do not drop e.g. `fatMax` from meal-plan targets. */
function mergeMealPlanTargetDaily(
  legacy: NonNullable<FinalizedMealPlanData['targetDaily']>,
  api: FinalizedMealPlanData['targetDaily'] | undefined
): FinalizedMealPlanData['targetDaily'] {
  const merged: Record<string, number> = {};
  for (const key of MEAL_PLAN_TARGET_DAILY_KEYS) {
    const value = key === 'calories'
      ? pickFirstPositiveDailyKcal(api?.[key], legacy[key])
      : pickFirstPositiveDailyTarget(api?.[key], legacy[key]);
    if (value !== undefined) {
      merged[key] = value;
    }
  }
  return merged as FinalizedMealPlanData['targetDaily'];
}

const normalizeServingOverrideUnits = (rawServingOverrides: unknown): string[] => {
  if (!Array.isArray(rawServingOverrides)) return [];
  const units = rawServingOverrides.map((entry) => {
    if (!entry || typeof entry !== 'object') return '';
    const record = entry as Record<string, unknown>;
    return record.servingId ?? record.unit ?? record.name ?? '';
  });
  return normalizeAutocompleteUnits(units);
};

const extractNormalizedPossibleUnitsFromMealPlanItem = (
  rawItem: Record<string, unknown>,
): string[] => {
  const ingredientRecord =
    rawItem.ingredient && typeof rawItem.ingredient === 'object'
      ? (rawItem.ingredient as Record<string, unknown>)
      : undefined;
  return normalizeAutocompleteUnits([
    ...normalizeAutocompleteUnits(Array.isArray(rawItem.possibleUnits) ? rawItem.possibleUnits : []),
    ...normalizeAutocompleteUnits(Array.isArray(ingredientRecord?.possibleUnits) ? ingredientRecord.possibleUnits : []),
    ...normalizeServingOverrideUnits(rawItem.servingOverrides),
    ...normalizeServingOverrideUnits(ingredientRecord?.servingOverrides),
    ...(typeof rawItem.defaultServingId === 'string' ? [rawItem.defaultServingId] : []),
    ...(typeof ingredientRecord?.defaultServingId === 'string' ? [ingredientRecord.defaultServingId] : []),
  ]);
};

const normalizeIngredientUnitMetadata = (rawItem: Record<string, unknown>): Record<string, unknown> | undefined => {
  const ingredientRecord =
    rawItem.ingredient && typeof rawItem.ingredient === 'object'
      ? (rawItem.ingredient as Record<string, unknown>)
      : undefined;
  const possibleUnits = extractNormalizedPossibleUnitsFromMealPlanItem(rawItem);
  const defaultServingId = [
    rawItem.defaultServingId,
    ingredientRecord?.defaultServingId,
  ].find((value): value is string => typeof value === 'string' && value.trim().length > 0);
  const servingOverrides =
    Array.isArray(rawItem.servingOverrides) && rawItem.servingOverrides.length > 0
      ? rawItem.servingOverrides
      : Array.isArray(ingredientRecord?.servingOverrides) && ingredientRecord.servingOverrides.length > 0
        ? ingredientRecord.servingOverrides
        : [];
  const metadata: Record<string, unknown> = {};
  if (possibleUnits.length > 0) metadata.possibleUnits = possibleUnits;
  if (defaultServingId) metadata.defaultServingId = defaultServingId;
  if (servingOverrides.length > 0) metadata.servingOverrides = servingOverrides;
  return Object.keys(metadata).length > 0 ? metadata : undefined;
};

const resolveMealPlanItemType = (
  item: Record<string, unknown>,
  fallback: 'recipe' | 'ingredient' = 'recipe',
): 'recipe' | 'ingredient' => {
  const explicit = String(item.itemType ?? item.type ?? '').trim().toLowerCase();
  if (explicit === 'ingredient') return 'ingredient';
  if (explicit === 'recipe') return 'recipe';
  if (fallback === 'ingredient') return 'ingredient';

  const hasIngredientServingMetadata =
    Array.isArray(item.possibleUnits)
    || Array.isArray(item.servingOverrides)
    || Boolean(item.ingredient && typeof item.ingredient === 'object' && !Array.isArray(item.ingredient));

  return hasIngredientServingMetadata ? 'ingredient' : fallback;
};

const withMealPlanItemType = (
  rawItems: unknown,
  fallback: 'recipe' | 'ingredient',
): Array<Record<string, unknown>> => {
  if (!Array.isArray(rawItems)) return [];

  return rawItems
    .filter((item): item is Record<string, unknown> => Boolean(item && typeof item === 'object' && !Array.isArray(item)))
    .map((item) => ({
      ...item,
      itemType: resolveMealPlanItemType(item, fallback),
    }));
};

const mealPlanItemDedupeKey = (item: Record<string, unknown>): string => {
  const itemType = resolveMealPlanItemType(item);
  const id = String(
    item.itemId
    ?? item.mealPlanItemId
    ?? item.recipeId
    ?? item.ingredientId
    ?? item.originalIngredientId
    ?? item.id
    ?? item._id
    ?? item.sourceId
    ?? item.name
    ?? item.title
    ?? item.recipeName
    ?? '',
  ).trim().toLowerCase();
  if (!id) return '';
  const provider = String(item.provider ?? item.source ?? '').trim().toLowerCase();
  return [itemType, provider, id].join(':');
};

const mergeMealPlanItems = (
  ...groups: Array<Array<Record<string, unknown>>>
): Array<Record<string, unknown>> => {
  const seen = new Set<string>();
  const merged: Array<Record<string, unknown>> = [];

  groups.flat().forEach((item, index) => {
    const key = mealPlanItemDedupeKey(item) || `index:${index}`;
    if (seen.has(key)) return;
    seen.add(key);
    merged.push(item);
  });

  return merged;
};

type MealSlotInput = {
  keyHint?: string;
  value: Record<string, unknown>;
};

const toMealSlotInputs = (rawMeals: unknown): MealSlotInput[] => {
  if (Array.isArray(rawMeals)) {
    return rawMeals
      .filter((meal) => meal && typeof meal === 'object')
      .map((meal) => ({ value: meal as Record<string, unknown> }));
  }

  if (rawMeals && typeof rawMeals === 'object') {
    return Object.entries(rawMeals as Record<string, unknown>)
      .filter(([, meal]) => meal && typeof meal === 'object')
      .map(([keyHint, meal]) => ({ keyHint, value: meal as Record<string, unknown> }));
  }

  return [];
};

function normalizeMealPlanByIdToFinalizedData(
  raw: MealPlanByIdResponse,
  mealPlanId: string
): FinalizedMealPlanData {
  const rawRecord = (raw && typeof raw === 'object') ? (raw as Record<string, unknown>) : {};
  const wrappedData = (rawRecord.data && typeof rawRecord.data === 'object')
    ? (rawRecord.data as MealPlanByIdResponse['data'])
    : undefined;
  // Support both API shapes:
  // 1) { success, data: { ...mealPlan } }
  // 2) { ...mealPlan } (top-level payload, no `data` envelope)
  const unwrappedData = rawRecord as MealPlanByIdResponse['data'];
  const hasUnwrappedPlanShape = Array.isArray((unwrappedData as any)?.days)
    || Array.isArray((unwrappedData as any)?.daySuggestions)
    || typeof (unwrappedData as any)?.startDate === 'string'
    || typeof (unwrappedData as any)?.endDate === 'string';
  const data = wrappedData ?? (hasUnwrappedPlanShape ? unwrappedData : undefined);
  const dataRecord = (data ?? {}) as Record<string, unknown>;
  const resolvedPlanName = readOptionalString(dataRecord.name)
    ?? readOptionalString(dataRecord.planName)
    ?? readOptionalString(dataRecord.plan_name)
    ?? readOptionalString(dataRecord.title);
  const daySuggestions = data?.daySuggestions as DaySuggestion[] | undefined;
  const days: MealPlanByIdDay[] = data?.days ?? [];

  const dailyNutritionByDay = new Map<number, Record<string, unknown>>();
  const dailyNutritionList = (data?.dailyNutrition ?? []) as Array<{
    dayNumber?: number;
    nutrition?: Record<string, unknown>;
  }>;
  for (const entry of dailyNutritionList) {
    const n = entry.dayNumber;
    if (typeof n === 'number' && entry.nutrition && typeof entry.nutrition === 'object') {
      dailyNutritionByDay.set(n, entry.nutrition as Record<string, unknown>);
    }
  }

  let normalizedDays: Array<{ day: number; date: string; meals: Record<string, FinalizedMeal>; dayTotals: ReturnType<typeof buildDayTotalsFromFlat> }>;
  const buildEmptyMealSlot = (mealOrder: number, mealTime?: string): FinalizedMeal => {
    const normalizedTime = (mealTime ?? '').trim();
    const emptyMacros = {
      calories: 0,
      protein: 0,
      carbs: 0,
      fat: 0,
      fiber: 0,
      sodium: 0,
      sugar: 0,
      cholesterol: 0,
    };

    const baseMeal: FinalizedMeal = {
      recipeId: '',
      recipeName: '',
      recipeImage: '',
      recipeSource: 'internal',
      mealOrder,
      nutrition: {
        ...emptyMacros,
        macros: emptyMacros,
      },
      recipes: [],
    };

    if (!normalizedTime) return baseMeal;
    return {
      ...baseMeal,
      ...( { time: normalizedTime } as Record<string, unknown> ),
    } as FinalizedMeal;
  };

  const normalizeFinalizedRecipeSource = (value: unknown): FinalizedMeal['recipeSource'] => {
    const normalized = String(value ?? '').trim().toLowerCase();
    if (normalized === 'internal' || normalized === 'edamam' || normalized === 'spoonacular') {
      return normalized;
    }
    return undefined;
  };

  if (daySuggestions && daySuggestions.length > 0) {
    const dailyNutrition = (data?.dailyNutrition ?? []) as Array<{ dayNumber: number; date?: string; nutrition?: Record<string, unknown> }>;
    normalizedDays = daySuggestions.map((ds) => {
      const dayNum = ds.dayNumber ?? 0;
      const dayNut = dailyNutrition.find((dn) => dn.dayNumber === dayNum)?.nutrition;
      let dayTotals = buildDayTotalsFromFlat(dayNut);
      const mealsRecord: Record<string, FinalizedMeal> = {};
      (ds.meals ?? []).forEach((m: DaySuggestionMeal, mealIndex: number) => {
        const baseMealKey = resolveMealDisplayKey(
          m.mealName,
          m.mealType,
          String(Object.keys(mealsRecord).length + 1)
        );
        let mealKey = baseMealKey;
        let duplicateIndex = 2;
        while (mealsRecord[mealKey]) {
          mealKey = `${baseMealKey}_${duplicateIndex}`;
          duplicateIndex += 1;
        }
        const selectedId = m.selectedRecipeId;
        const suggestions = m.recipeSuggestions ?? [];
        const slotOrder =
          typeof m.mealOrder === 'number' && m.mealOrder > 0
            ? m.mealOrder
            : mealIndex + 1;
        const slotTime = String((m as { mealTime?: string; time?: string }).mealTime ?? (m as { time?: string }).time ?? '').trim();
        const suggestionRecipes = suggestions
          .map((recipeSuggestion) => {
            const recipeId = String(recipeSuggestion?.id ?? '').trim();
            if (!recipeId) return null;
            const suggestionImage =
              recipeSuggestion.image ??
              (recipeSuggestion as { imageUrl?: string }).imageUrl ??
              '';
            const flatNut = recipeSuggestion.nutrition ?? {
              calories: recipeSuggestion.calories,
              protein: recipeSuggestion.protein,
              carbohydrate: recipeSuggestion.carbs,
              total_fat: recipeSuggestion.fat,
              dietary_fibre: recipeSuggestion.fiber,
            };
            const nutrition = flatNutritionToMacros(flatNut as Record<string, unknown>);
            const suggestionItemId = String(
              (recipeSuggestion as { itemId?: unknown; mealPlanItemId?: unknown }).itemId
              ?? (recipeSuggestion as { mealPlanItemId?: unknown }).mealPlanItemId
              ?? '',
            ).trim();
            const optionGroupId = readOptionalString(
              (recipeSuggestion as Record<string, unknown>).optionGroupId ??
              (recipeSuggestion as Record<string, unknown>).option_group_id
            );
            const optionGroupLabel = readOptionalString(
              (recipeSuggestion as Record<string, unknown>).optionGroupLabel ??
              (recipeSuggestion as Record<string, unknown>).option_group_label
            );
            const selectedOptionGroupId = readOptionalString(
              (recipeSuggestion as Record<string, unknown>).selectedOptionGroupId ??
              (recipeSuggestion as Record<string, unknown>).selected_option_group_id ??
              (m as Record<string, unknown>).selectedOptionGroupId ??
              (m as Record<string, unknown>).selected_option_group_id
            );
            const generationMetadata = buildRecipeGenerationMetadata(recipeSuggestion as Record<string, unknown>);
            return {
              recipeId,
              recipeName: recipeSuggestion.title ?? '',
              recipeImage: suggestionImage,
              recipeSource: normalizeFinalizedRecipeSource(recipeSuggestion.source),
              ...generationMetadata,
              culinaryRating: normalizeCulinaryRating(
                (recipeSuggestion as Record<string, unknown>).culinaryRating
              ),
              customNotes: recipeSuggestion.customization?.notes,
              ...(suggestionItemId ? { itemId: suggestionItemId } : {}),
              ...(optionGroupId ? { optionGroupId } : {}),
              ...(optionGroupLabel ? { optionGroupLabel } : {}),
              ...(selectedOptionGroupId ? { selectedOptionGroupId } : {}),
              itemType: 'recipe',
              servings: recipeSuggestion.servings,
              nutritionServings: recipeSuggestion.nutritionServings ?? recipeSuggestion.servings,
              cuisines: normalizeStringArray(
                (recipeSuggestion as Record<string, unknown>).cuisines ??
                (recipeSuggestion as Record<string, unknown>).cuisineTypes ??
                (recipeSuggestion as Record<string, unknown>).cuisineType
              ),
              cuisineTypes: normalizeStringArray(
                (recipeSuggestion as Record<string, unknown>).cuisineTypes ??
                (recipeSuggestion as Record<string, unknown>).cuisines ??
                (recipeSuggestion as Record<string, unknown>).cuisineType
              ),
              mealTypes: normalizeStringArray(
                (recipeSuggestion as Record<string, unknown>).mealTypes ??
                (recipeSuggestion as Record<string, unknown>).mealType
              ),
              allergens: normalizeStringArray((recipeSuggestion as Record<string, unknown>).allergens),
              intoleranceTags: normalizeStringArray((recipeSuggestion as Record<string, unknown>).intoleranceTags),
              nutrition,
              mealOrder: slotOrder,
              ...(slotTime ? ({ time: slotTime } as Record<string, unknown>) : {}),
            } as FinalizedMeal;
          })
          .filter((recipe): recipe is FinalizedMeal => recipe !== null);

        const selectedRecipe = selectedId
          ? suggestionRecipes.find((recipe) => recipe.recipeId === selectedId)
          : undefined;
        const explicitItems = mergeMealPlanItems(
          withMealPlanItemType(m.items, 'recipe'),
          withMealPlanItemType(m.recipes, 'recipe'),
          withMealPlanItemType(m.ingredients, 'ingredient'),
        );

        if (explicitItems.length > 0) {
          const recipes = explicitItems.map((item) => {
            const flatNut =
              (item.nutrition as Record<string, unknown> | undefined) ??
              (item.totalNutrition as Record<string, unknown> | undefined);
            const nutrition = flatNutritionToMacros(flatNut);
            const rawAmount = item.amount ?? item.quantity;
            const parsedAmount = Number(rawAmount);
            const parsedUnit = String(item.servingId ?? item.unit ?? '').trim();
            const parsedSourceId = String(item.sourceId ?? '').trim();
            const rawItem = item as Record<string, unknown>;
            const parsedItemType = resolveMealPlanItemType(rawItem);
            const explicitIngredientId =
              parsedItemType === 'ingredient' && typeof rawItem.ingredientId === 'string'
                ? rawItem.ingredientId.trim()
                : '';
            const servingGrams = toPositiveNumber(rawItem.servingGrams ?? rawItem.serving_grams);
            const selectedServingGrams = toPositiveNumber(
              rawItem.selectedServingGrams ?? rawItem.selected_serving_grams,
            );
            const defaultServingGrams = toPositiveNumber(
              rawItem.defaultServingGrams ?? rawItem.default_serving_grams,
            );
            const base: FinalizedMeal = {
              recipeId: String(item.recipeId ?? item.id ?? ''),
              recipeName: String(item.name ?? item.title ?? item.recipeName ?? ''),
              recipeImage: String(item.image ?? item.imageUrl ?? item.recipeImage ?? item.recipeImageUrl ?? ''),
              recipeSource: normalizeFinalizedRecipeSource(item.source),
              ...buildRecipeGenerationMetadata(item as Record<string, unknown>),
              culinaryRating: normalizeCulinaryRating(
                (item as Record<string, unknown>).culinaryRating
              ),
              itemId: String(
                item.itemId ?? (explicitIngredientId || undefined) ?? item.id ?? '',
              ),
              servings: Number(item.servings) || undefined,
              nutritionServings: Number(item.nutritionServings ?? item.servings) || undefined,
              cuisines: normalizeStringArray(rawItem.cuisines ?? rawItem.cuisineTypes ?? rawItem.cuisineType),
              cuisineTypes: normalizeStringArray(rawItem.cuisineTypes ?? rawItem.cuisines ?? rawItem.cuisineType),
              mealTypes: normalizeStringArray(rawItem.mealTypes ?? rawItem.mealType),
              allergens: normalizeStringArray(rawItem.allergens),
              intoleranceTags: normalizeStringArray(rawItem.intoleranceTags),
              nutrition,
              mealOrder: slotOrder,
            };
            const ext: Record<string, unknown> = { itemType: parsedItemType };
            if (Number.isFinite(parsedAmount)) ext.amount = parsedAmount;
            if (parsedUnit) ext.servingId = parsedUnit;
            if (parsedSourceId) ext.sourceId = parsedSourceId;
            if (parsedItemType === 'ingredient') {
              if (explicitIngredientId) {
                ext.ingredientId = explicitIngredientId;
              }
              const ingredientUnitMetadata = normalizeIngredientUnitMetadata(rawItem);
              if (ingredientUnitMetadata) {
                Object.assign(ext, ingredientUnitMetadata);
                ext.ingredient = ingredientUnitMetadata;
              }
            }
            if (Array.isArray(rawItem.servingSizes) && rawItem.servingSizes.length > 0) {
              ext.servingSizes = rawItem.servingSizes;
            }
            const selectedServing = rawItem.selectedServingId;
            if (typeof selectedServing === 'string' && selectedServing.trim().length > 0) {
              ext.selectedServingId = selectedServing.trim();
            }
            const defaultServing = rawItem.defaultServingId;
            if (typeof defaultServing === 'string' && defaultServing.trim().length > 0) {
              ext.defaultServingId = defaultServing.trim();
            }
            if (servingGrams != null) {
              ext.servingGrams = servingGrams;
            }
            if (selectedServingGrams != null) {
              ext.selectedServingGrams = selectedServingGrams;
            }
            if (defaultServingGrams != null) {
              ext.defaultServingGrams = defaultServingGrams;
            }
            if (rawItem.portionSizeMultiplier != null && Number.isFinite(Number(rawItem.portionSizeMultiplier))) {
              ext.portionSizeMultiplier = Number(rawItem.portionSizeMultiplier);
            }
            if (rawItem.servingSizeMultiplier != null && Number.isFinite(Number(rawItem.servingSizeMultiplier))) {
              ext.servingSizeMultiplier = Number(rawItem.servingSizeMultiplier);
            }
            const optionGroupId = readOptionalString(rawItem.optionGroupId ?? rawItem.option_group_id);
            if (optionGroupId) {
              ext.optionGroupId = optionGroupId;
            }
            const optionGroupLabel = readOptionalString(rawItem.optionGroupLabel ?? rawItem.option_group_label);
            if (optionGroupLabel) {
              ext.optionGroupLabel = optionGroupLabel;
            }
            const itemSelectedOptionGroupId = readOptionalString(
              rawItem.selectedOptionGroupId ??
              rawItem.selected_option_group_id ??
              (m as Record<string, unknown>).selectedOptionGroupId ??
              (m as Record<string, unknown>).selected_option_group_id
            );
            if (itemSelectedOptionGroupId) {
              ext.selectedOptionGroupId = itemSelectedOptionGroupId;
            }
            return Object.assign(base, ext) as FinalizedMeal;
          });

          const primaryRecipe = recipes[0];
          const selectedOptionGroupId = readOptionalString(
            (m as Record<string, unknown>).selectedOptionGroupId ??
            (m as Record<string, unknown>).selected_option_group_id
          );
          const hasOptionGroups = recipes.some((item) => Boolean(readOptionalString(item.optionGroupId)));
          const nutritionItems = filterItemsForSelectedOptionGroup(recipes, selectedOptionGroupId);
          const aggregatedMealNutrition = aggregateMealItemsNutrition(nutritionItems);
          mealsRecord[mealKey] = {
            ...primaryRecipe,
            ...(slotTime ? ({ time: slotTime } as Record<string, unknown>) : {}),
            ...(selectedOptionGroupId ? { selectedOptionGroupId } : {}),
            nutrition: aggregatedMealNutrition ?? (hasOptionGroups ? buildEmptyMealNutrition() : primaryRecipe.nutrition),
            recipes,
            items: recipes,
          } as FinalizedMeal;
        } else if (selectedRecipe) {
          const selectedOptionGroupId = readOptionalString(
            (m as Record<string, unknown>).selectedOptionGroupId ??
            (m as Record<string, unknown>).selected_option_group_id
          );
          mealsRecord[mealKey] = {
            ...selectedRecipe,
            ...(selectedOptionGroupId ? { selectedOptionGroupId } : {}),
            recipes: suggestionRecipes,
            items: suggestionRecipes,
          } as FinalizedMeal;
        } else {
          const selectedOptionGroupId = readOptionalString(
            (m as Record<string, unknown>).selectedOptionGroupId ??
            (m as Record<string, unknown>).selected_option_group_id
          );
          mealsRecord[mealKey] = {
            ...buildEmptyMealSlot(slotOrder, slotTime),
            ...(selectedOptionGroupId ? { selectedOptionGroupId } : {}),
            recipes: suggestionRecipes,
            items: suggestionRecipes,
          } as FinalizedMeal & { items: FinalizedMeal[] };
        }

        /** Authoritative meal-level rollup from API (`daySuggestions[].meals[].totalNutrition`); overrides aggregated line-item nutrition when present. */
        const mealTotalNutritionRaw = (m as Record<string, unknown>).totalNutrition;
        if (
          mealTotalNutritionRaw &&
          typeof mealTotalNutritionRaw === 'object' &&
          !Array.isArray(mealTotalNutritionRaw) &&
          !(Array.isArray((mealsRecord[mealKey] as { recipes?: FinalizedMeal[] } | undefined)?.recipes)
            && ((mealsRecord[mealKey] as { recipes?: FinalizedMeal[] }).recipes ?? []).some((item) => readOptionalString(item.optionGroupId)))
        ) {
          const fromMealTotal = flatNutritionToMacros(mealTotalNutritionRaw as Record<string, unknown>);
          if (fromMealTotal) {
            mealsRecord[mealKey] = {
              ...mealsRecord[mealKey],
              nutrition: fromMealTotal,
            } as FinalizedMeal;
          }
        }

        const apiMealName = String(m.mealName ?? '').trim() || mealKey;
        const rawInvalidSelection = m.invalidSelection;
        const invalidSelection = rawInvalidSelection && typeof rawInvalidSelection === 'object'
          ? {
              recipeId: readOptionalString(rawInvalidSelection.recipeId),
              code: readOptionalString(rawInvalidSelection.code),
              violations: normalizeStringArray(rawInvalidSelection.violations),
              message: readOptionalString(rawInvalidSelection.message),
            }
          : null;
        const mealSlotNotes =
          typeof (m as { notes?: unknown }).notes === 'string'
            ? String((m as { notes?: string }).notes)
            : (m as { notes?: unknown }).notes === null
              ? null
              : undefined;
        mealsRecord[mealKey] = {
          ...mealsRecord[mealKey],
          mealName: apiMealName,
          ...(mealSlotNotes !== undefined ? { notes: mealSlotNotes } : {}),
          invalidSelection,
        } as FinalizedMeal;
      });
      if (!dayNut) {
        const summed: Record<string, number> = { calories: 0, protein: 0, carbohydrate: 0, total_fat: 0, dietary_fibre: 0, sugar: 0, sodium: 0, cholesterol: 0, saturated_fat: 0 };
        Object.values(mealsRecord).forEach((recipe) => {
          const nut = (recipe.nutrition?.macros ?? recipe.nutrition) as Record<string, unknown> | undefined;
          if (!nut) return;
          summed.calories += (nut.calories as number) ?? 0;
          summed.protein += (nut.protein as number) ?? 0;
          summed.carbohydrate += ((nut.carbohydrate ?? nut.carbs) as number) ?? 0;
          summed.total_fat += ((nut.total_fat ?? nut.fat) as number) ?? 0;
          summed.dietary_fibre += ((nut.dietary_fibre ?? nut.fiber) as number) ?? 0;
          summed.sugar += (nut.sugar as number) ?? 0;
          summed.sodium += (nut.sodium as number) ?? 0;
          summed.cholesterol += (nut.cholesterol as number) ?? 0;
          summed.saturated_fat += ((nut.saturated_fat ?? nut.saturatedFat) as number) ?? 0;
        });
        dayTotals = buildDayTotalsFromFlat(summed);
      }
      const dsRecord = ds as { dayName?: string | null; notes?: string | null };
      return {
        day: dayNum,
        nutritionReview: ds.nutritionReview as FinalizedMealPlanData['days'][number]['nutritionReview'],
        cookingReview: ds.cookingReview as FinalizedMealPlanData['days'][number]['cookingReview'],
        date: ds.date ?? '',
        dayName: dsRecord.dayName ?? (ds as Record<string, unknown>).day_name ?? undefined,
        notes:
          typeof dsRecord.notes === 'string'
            ? dsRecord.notes
            : dsRecord.notes === null
              ? null
              : typeof (ds as Record<string, unknown>).notes === 'string'
                ? String((ds as Record<string, unknown>).notes)
                : undefined,
        meals: mealsRecord,
        dayTotals,
      };
    });
  } else {
    normalizedDays = days.map((d) => {
      const mealsRecord: Record<string, FinalizedMeal> = {};
      const mealSlots = toMealSlotInputs(d.meals);
      mealSlots.forEach(({ keyHint, value }, mealIndex: number) => {
        const m = value as MealPlanByIdMeal & Record<string, unknown>;
        const baseMealKey = resolveMealDisplayKey(
          m.mealName ?? keyHint,
          m.mealType ?? keyHint,
          String(Object.keys(mealsRecord).length + 1)
        );
        let mealKey = baseMealKey;
        let duplicateIndex = 2;
        while (mealsRecord[mealKey]) {
          mealKey = `${baseMealKey}_${duplicateIndex}`;
          duplicateIndex += 1;
        }
        const items = mergeMealPlanItems(
          withMealPlanItemType(m.items, 'recipe'),
          withMealPlanItemType(m.recipes, 'recipe'),
          withMealPlanItemType(m.ingredients, 'ingredient'),
        ) as MealPlanByIdItem[];
        const slotOrder =
          typeof m.sortOrder === 'number' && m.sortOrder > 0
            ? m.sortOrder
            : mealIndex + 1;
        const slotTime = String(m.time ?? '').trim();
        if (!items.length) {
          const emptySlot = buildEmptyMealSlot(slotOrder, slotTime);
          const apiMealName = String(m.mealName ?? keyHint ?? '').trim() || mealKey;
          const mealSlotNotes =
            typeof m.notes === 'string' ? m.notes : m.notes === null ? null : undefined;
          mealsRecord[mealKey] = {
            ...emptySlot,
            mealName: apiMealName,
            ...(mealSlotNotes !== undefined ? { notes: mealSlotNotes } : {}),
          };
          return;
        }

        const recipes = items.map((item: MealPlanByIdItem) => {
          const flatNut = item.nutrition as Record<string, unknown> | undefined;
          const nutrition = flatNutritionToMacros(flatNut);
          const rawItem = item as Record<string, unknown>;
          const ingredients = rawItem.ingredients;
          const ingredientLines = rawItem.ingredientLines;
          const rawAmount = rawItem.amount ?? rawItem.quantity;
          const parsedAmount = Number(rawAmount);
          const parsedUnit = String(rawItem.servingId ?? rawItem.unit ?? '').trim();
          const parsedSourceId = String(rawItem.sourceId ?? '').trim();
          const parsedItemType = resolveMealPlanItemType(rawItem);
          const explicitIngredientId =
            parsedItemType === 'ingredient' && typeof rawItem.ingredientId === 'string'
              ? rawItem.ingredientId.trim()
              : '';
          const servingGrams = toPositiveNumber(rawItem.servingGrams ?? rawItem.serving_grams);
          const selectedServingGrams = toPositiveNumber(
            rawItem.selectedServingGrams ?? rawItem.selected_serving_grams,
          );
          const defaultServingGrams = toPositiveNumber(
            rawItem.defaultServingGrams ?? rawItem.default_serving_grams,
          );
          const base: FinalizedMeal = {
            recipeId: item.recipeId ?? item.id ?? '',
            recipeName: item.name ?? item.title ?? item.recipeName ?? '',
            recipeImage:
              item.recipeImage ??
              (rawItem.recipeImageUrl as string | undefined) ??
              item.image ??
              (rawItem.imageUrl as string | undefined) ??
              '',
            recipeSource: normalizeFinalizedRecipeSource(item.source),
            culinaryRating: normalizeCulinaryRating(rawItem.culinaryRating),
            customNotes: item.notes ?? item.customNotes,
            itemId: item.itemId ?? item.id,
            servings: item.servings,
            nutritionServings: item.nutritionServings,
            cuisines: normalizeStringArray(rawItem.cuisines ?? rawItem.cuisineTypes ?? rawItem.cuisineType),
            cuisineTypes: normalizeStringArray(rawItem.cuisineTypes ?? rawItem.cuisines ?? rawItem.cuisineType),
            mealTypes: normalizeStringArray(rawItem.mealTypes ?? rawItem.mealType),
            allergens: normalizeStringArray(rawItem.allergens),
            intoleranceTags: normalizeStringArray(rawItem.intoleranceTags),
            nutrition,
            mealOrder: slotOrder,
          };
          const ext: Record<string, unknown> = {};
          ext.itemType = parsedItemType;
          if (Array.isArray(ingredients) && ingredients.length > 0) ext.ingredients = ingredients;
          if (Array.isArray(ingredientLines) && ingredientLines.length > 0) ext.ingredientLines = ingredientLines;
          if (Number.isFinite(parsedAmount)) ext.amount = parsedAmount;
          if (parsedUnit) ext.servingId = parsedUnit;
          if (parsedSourceId) ext.sourceId = parsedSourceId;
          if (parsedItemType === 'ingredient') {
            if (explicitIngredientId) {
              ext.ingredientId = explicitIngredientId;
            }
            const ingredientUnitMetadata = normalizeIngredientUnitMetadata(rawItem);
            if (ingredientUnitMetadata) {
              Object.assign(ext, ingredientUnitMetadata);
              ext.ingredient = ingredientUnitMetadata;
            }
          }
          if (Array.isArray(rawItem.servingSizes) && rawItem.servingSizes.length > 0) {
            ext.servingSizes = rawItem.servingSizes;
          }
          const selectedServing = rawItem.selectedServingId;
          if (typeof selectedServing === 'string' && selectedServing.trim().length > 0) {
            ext.selectedServingId = selectedServing.trim();
          }
          const defaultServing = rawItem.defaultServingId;
          if (typeof defaultServing === 'string' && defaultServing.trim().length > 0) {
            ext.defaultServingId = defaultServing.trim();
          }
          if (servingGrams != null) {
            ext.servingGrams = servingGrams;
          }
          if (selectedServingGrams != null) {
            ext.selectedServingGrams = selectedServingGrams;
          }
          if (defaultServingGrams != null) {
            ext.defaultServingGrams = defaultServingGrams;
          }
          if (rawItem.portionSizeMultiplier != null && Number.isFinite(Number(rawItem.portionSizeMultiplier))) {
            ext.portionSizeMultiplier = Number(rawItem.portionSizeMultiplier);
          }
          if (rawItem.servingSizeMultiplier != null && Number.isFinite(Number(rawItem.servingSizeMultiplier))) {
            ext.servingSizeMultiplier = Number(rawItem.servingSizeMultiplier);
          }
          const optionGroupId = readOptionalString(rawItem.optionGroupId ?? rawItem.option_group_id);
          if (optionGroupId) {
            ext.optionGroupId = optionGroupId;
          }
          const optionGroupLabel = readOptionalString(rawItem.optionGroupLabel ?? rawItem.option_group_label);
          if (optionGroupLabel) {
            ext.optionGroupLabel = optionGroupLabel;
          }
          const itemSelectedOptionGroupId = readOptionalString(
            rawItem.selectedOptionGroupId ??
            rawItem.selected_option_group_id ??
            (m as Record<string, unknown>).selectedOptionGroupId ??
            (m as Record<string, unknown>).selected_option_group_id
          );
          if (itemSelectedOptionGroupId) {
            ext.selectedOptionGroupId = itemSelectedOptionGroupId;
          }
          return Object.assign(base, ext) as FinalizedMeal;
        });

        const primaryRecipe = recipes[0];
        const selectedOptionGroupId = readOptionalString(
          (m as Record<string, unknown>).selectedOptionGroupId ??
          (m as Record<string, unknown>).selected_option_group_id
        );
        const hasOptionGroups = recipes.some((item) => Boolean(readOptionalString(item.optionGroupId)));
        const nutritionItems = filterItemsForSelectedOptionGroup(recipes, selectedOptionGroupId);
        const aggregatedMealNutrition = aggregateMealItemsNutrition(nutritionItems);
        const mealEntry = {
          ...primaryRecipe,
          ...(slotTime ? ({ time: slotTime } as Record<string, unknown>) : {}),
          ...(selectedOptionGroupId ? { selectedOptionGroupId } : {}),
          nutrition: aggregatedMealNutrition ?? (hasOptionGroups ? buildEmptyMealNutrition() : primaryRecipe.nutrition),
          recipes,
          items: recipes,
        } as FinalizedMeal;

        const apiMealName = String(m.mealName ?? keyHint ?? '').trim() || mealKey;
        const mealSlotNotes =
          typeof m.notes === 'string' ? m.notes : m.notes === null ? null : undefined;
        mealsRecord[mealKey] = {
          ...mealEntry,
          mealName: apiMealName,
          ...(mealSlotNotes !== undefined ? { notes: mealSlotNotes } : {}),
        };
      });
      const dayNum = d.day ?? d.dayNumber ?? 0;
      const flatDailyNutrition = dailyNutritionByDay.get(dayNum);
      let dayTotals: ReturnType<typeof buildDayTotalsFromFlat>;
      if (flatDailyNutrition) {
        dayTotals = buildDayTotalsFromFlat(flatDailyNutrition);
      } else if (d.dayTotals?.macros) {
        const m = d.dayTotals.macros;
        dayTotals = buildDayTotalsFromFlat({
          calories: m.calories,
          protein: m.protein,
          carbohydrate: m.carbs,
          total_fat: m.fat,
          dietary_fibre: m.fiber,
          sugar: m.sugar,
          sodium: m.sodium,
          cholesterol: m.cholesterol,
          saturated_fat: m.saturatedFat,
        });
      } else {
        const summed: Record<string, number> = { calories: 0, protein: 0, carbohydrate: 0, total_fat: 0, dietary_fibre: 0, sugar: 0, sodium: 0, cholesterol: 0, saturated_fat: 0 };
        Object.values(mealsRecord).forEach((r) => {
          const nut = (r.nutrition?.macros ?? r.nutrition) as Record<string, unknown> | undefined;
          if (nut) {
            summed.calories += (nut.calories as number) ?? 0;
            summed.protein += (nut.protein as number) ?? 0;
            summed.carbohydrate += ((nut.carbohydrate ?? nut.carbs) as number) ?? 0;
            summed.total_fat += ((nut.total_fat ?? nut.fat) as number) ?? 0;
            summed.dietary_fibre += ((nut.dietary_fibre ?? nut.fiber) as number) ?? 0;
            summed.sugar += (nut.sugar as number) ?? 0;
            summed.sodium += (nut.sodium as number) ?? 0;
            summed.cholesterol += (nut.cholesterol as number) ?? 0;
            summed.saturated_fat += ((nut.saturated_fat ?? nut.saturatedFat) as number) ?? 0;
          }
        });
        dayTotals = buildDayTotalsFromFlat(summed);
      }
      const dRecord = d as MealPlanByIdDay & { day_name?: string | null };
      return {
        day: dayNum,
        date: d.date ?? '',
        dayName: dRecord.dayName ?? dRecord.day_name ?? undefined,
        notes:
          typeof dRecord.notes === 'string'
            ? dRecord.notes
            : dRecord.notes === null
              ? null
              : undefined,
        meals: mealsRecord,
        dayTotals,
      };
    });
  }

  const nutritionSummary =
    data?.nutritionSummary ??
    buildNutritionSummaryFromFlat(
      data?.averageDailyNutrition as Record<string, unknown> | undefined,
      data?.dailyNutrition as Array<{ dayNumber: number; nutrition?: Record<string, unknown> }> | undefined
    );

  const targetDailyFromLegacyFields: NonNullable<FinalizedMealPlanData['targetDaily']> = {
    calories: pickFirstPositiveDailyKcal(
      dataRecord.eerOverride,
      (dataRecord as { eer?: unknown }).eer,
      dataRecord.eerCalories,
      (dataRecord as { eer_override?: unknown }).eer_override,
      dataRecord.targetCalories
    ),
    proteinMin: readLegacyTargetRangeEndpoint(dataRecord.targetProtein, 'min'),
    proteinMax: readLegacyTargetRangeEndpoint(dataRecord.targetProtein, 'max'),
    carbsMin: readLegacyTargetRangeEndpoint(dataRecord.targetCarbs, 'min'),
    carbsMax: readLegacyTargetRangeEndpoint(dataRecord.targetCarbs, 'max'),
    fatMin: readLegacyTargetRangeEndpoint(dataRecord.targetFat, 'min'),
    fatMax: readLegacyTargetRangeEndpoint(dataRecord.targetFat, 'max'),
  };

  const mergedTargetDaily = mergeMealPlanTargetDaily(targetDailyFromLegacyFields, data?.targetDaily);

  return {
    id: data?.id ?? mealPlanId,
    ...(resolvedPlanName ? { name: resolvedPlanName, planName: resolvedPlanName } : {}),
    clientId: data?.clientId ?? '',
    nutritionistId: data?.nutritionistId ?? '',
    status: (data?.status as 'finalized' | 'draft' | 'processing') ?? 'finalized',
    planNotes:
      typeof dataRecord.planNotes === 'string'
        ? dataRecord.planNotes
        : dataRecord.planNotes === null
          ? null
          : typeof dataRecord.plan_notes === 'string'
            ? String(dataRecord.plan_notes)
            : undefined,
    description:
      typeof dataRecord.description === 'string'
        ? dataRecord.description
        : dataRecord.description === null
          ? null
          : undefined,
    generationPreviewDays: (dataRecord.daySuggestions ?? []) as FinalizedMealPlanData['generationPreviewDays'],
    generationState: (
      (dataRecord.generationState as FinalizedMealPlanData['generationState'] | undefined) ??
      (dataRecord.generation_state as FinalizedMealPlanData['generationState'] | undefined)
    ),
    generationError: (
      (dataRecord.generationError as string | null | undefined) ??
      (dataRecord.generation_error as string | null | undefined) ??
      null
    ),
    shuffleState: (
      (dataRecord.shuffleState as FinalizedMealPlanData['shuffleState'] | undefined) ??
      (dataRecord.shuffle_state as FinalizedMealPlanData['shuffleState'] | undefined)
    ),
    shuffleError: (
      (dataRecord.shuffleError as string | null | undefined) ??
      (dataRecord.shuffle_error as string | null | undefined) ??
      null
    ),
    shuffleTarget: (
      (dataRecord.shuffleTarget as FinalizedMealPlanData['shuffleTarget'] | undefined) ??
      (dataRecord.shuffle_target as FinalizedMealPlanData['shuffleTarget'] | undefined) ??
      null
    ),
    generationType: (data?.generationType ?? (data as { generation_type?: string })?.generation_type) as string | undefined,
    allergens: normalizeStringArray(dataRecord.allergens),
    intoleranceTags: normalizeStringArray(dataRecord.intoleranceTags),
    searchParams: {
      aiProvider: ((dataRecord.aiProvider as string | undefined) ?? (dataRecord.source as string | undefined) ?? '').toString(),
      days: (typeof dataRecord.days === 'number' ? dataRecord.days : normalizedDays.length) as number,
      startDate: (dataRecord.startDate as string | undefined) ?? normalizedDays[0]?.date ?? '',
      endDate:
        (dataRecord.endDate as string | undefined) ??
        normalizedDays[normalizedDays.length - 1]?.date ??
        normalizedDays[0]?.date ??
        '',
      clientGoals: {
        calories: mergedTargetDaily.calories ?? 0,
        protein: 0,
        proteinMin: mergedTargetDaily.proteinMin ?? 0,
        proteinMax: mergedTargetDaily.proteinMax ?? 0,
        carbs: 0,
        carbsMin: mergedTargetDaily.carbsMin ?? 0,
        carbsMax: mergedTargetDaily.carbsMax ?? 0,
        fat: 0,
        fatMin: mergedTargetDaily.fatMin ?? 0,
        fatMax: mergedTargetDaily.fatMax ?? 0,
        fiber: 0,
      },
      dietaryPreferences: {
        allergies: (Array.isArray(dataRecord.allergiesApplied) ? dataRecord.allergiesApplied : []) as string[],
        intolerances: (Array.isArray(dataRecord.intolerancesApplied) ? dataRecord.intolerancesApplied : []) as string[],
        cuisineTypes: (Array.isArray(dataRecord.cuisineTypesApplied) ? dataRecord.cuisineTypesApplied : []) as string[],
        dietaryPreferences: (Array.isArray(dataRecord.dietaryPreferencesApplied) ? dataRecord.dietaryPreferencesApplied : []) as string[],
      },
      mealProgram: undefined,
      overrideClientGoals: null,
      overrideMealProgram: undefined,
    } as FinalizedMealPlanData['searchParams'],
    days: normalizedDays as unknown as FinalizedMealDay[],
    nutritionSummary,
    targetDaily: mergedTargetDaily,
    createdAt: data?.createdAt ?? data?.generatedAt ?? '',
    updatedAt: data?.updatedAt ?? '',
    expiresAt: data?.expiresAt ?? null,
  };
}

/** GET /meal-plans/{id}/export — client, nutritionist, days/meals/items with ingredients & instructions (no nutrition). */
export const getMealPlanExportForPdf = async (
  mealPlanId: string,
): Promise<MealPlanExportForPdfResponse> => {
  const response = await api.get(`/meal-plans/${mealPlanId}/export`, uncachedGetConfig());
  return response.data;
};

/** GET /meal-plans/generic/{id}/export — full generic meal plan for PDF export (no client assignment). */
export const getGenericMealPlanExportForPdf = async (
  genericPlanId: string,
): Promise<MealPlanExportForPdfResponse> => {
  const response = await api.get(
    `/meal-plans/generic/${encodeURIComponent(genericPlanId)}/export`,
    uncachedGetConfig(),
  );
  return response.data;
};

/** Get finalized meal plan by ID and return normalized FinalizedMealPlanData (for view page). */
export const getMealPlanForDisplay = async (
  mealPlanId: string
): Promise<{ success: boolean; message?: string; data?: FinalizedMealPlanData }> => {
  try {
    const raw = await getMealPlan(mealPlanId);
    const data = normalizeMealPlanByIdToFinalizedData(raw, mealPlanId);
    return { success: true, data };
  } catch (error: unknown) {
    const message = error && typeof error === 'object' && 'message' in error ? String((error as { message: string }).message) : 'Failed to load meal plan';
    return { success: false, message };
  }
};

/** List feedback a client submitted against a finalized meal plan and its nested targets. */
export const listMealPlanClientFeedback = async (
  mealPlanId: string,
  params: {
    level?: MealPlanFeedbackLevel;
    page?: number;
    pageSize?: number;
  } = {},
): Promise<MealPlanFeedbackListResponse> => {
  const response = await api.get(
    `/meal-plans/${encodeURIComponent(mealPlanId)}/feedback`,
    { params },
  );
  return response.data;
};

const genericMealPlanDetailToMealPlanByIdData = (
  detail: GenericMealPlanDetails,
  genericPlanId: string,
): NonNullable<MealPlanByIdResponse['data']> => {
  const toGenericDisplayMeal = (slot: Record<string, unknown>, index: number): Record<string, unknown> => {
    const recipes = withMealPlanItemType(slot.recipes, 'recipe');
    const ingredients = withMealPlanItemType(slot.ingredients, 'ingredient');
    const items = mergeMealPlanItems(
      withMealPlanItemType(slot.items, 'recipe'),
      recipes,
      ingredients,
    );
    const order = slot.sortOrder ?? slot.mealOrder ?? index + 1;

    return {
      ...slot,
      id: slot.id,
      mealName: slot.mealName ?? slot.name ?? `Meal ${index + 1}`,
      mealType: slot.mealType ?? 'snack',
      mealOrder: slot.mealOrder ?? slot.sortOrder ?? index + 1,
      sortOrder: order,
      time: slot.mealTime ?? slot.time,
      nutrition: slot.nutrition ?? slot.totalNutrition,
      totalNutrition: slot.totalNutrition ?? slot.nutrition,
      recipes,
      ingredients,
      items,
    };
  };

  const toGenericDisplayDay = (day: Record<string, unknown>, index: number): Record<string, unknown> => {
    const dayNumber = day.dayNumber ?? day.day ?? index + 1;
    const meals = Array.isArray(day.meals)
      ? day.meals
      : Array.isArray(day.mealSlots)
        ? day.mealSlots
        : [];

    return {
      ...day,
      day: day.day ?? dayNumber,
      dayNumber,
      dayName: day.dayName ?? detail.name,
      meals: meals
        .filter((meal): meal is Record<string, unknown> => Boolean(meal && typeof meal === 'object' && !Array.isArray(meal)))
        .map(toGenericDisplayMeal),
    };
  };

  const daySuggestions = Array.isArray(detail.daySuggestions) ? detail.daySuggestions : [];
  const detailDays = Array.isArray(detail.days) ? detail.days : [];
  const sourceDays = (daySuggestions.length > 0 ? daySuggestions : detailDays).map(toGenericDisplayDay);
  const fallbackMeals = Array.isArray(detail.mealSlots) && detail.mealSlots.length > 0
    ? detail.mealSlots
    : Array.isArray(detail.meals)
      ? detail.meals
      : [];
  const normalizedDays = sourceDays.length > 0
    ? sourceDays
    : [
        {
          day: 1,
          dayNumber: 1,
          dayName: detail.name,
          meals: fallbackMeals.map(toGenericDisplayMeal),
        },
      ];

  return {
    ...detail,
    id: detail.id ?? genericPlanId,
    name: detail.name ?? 'Cohort Meal Plan',
    status: 'draft',
    generationType: 'generic',
    generationSource: 'generic',
    durationDays: normalizedDays.length || 1,
    days: normalizedDays as MealPlanByIdDay[],
    dailyNutrition: detail.dailyNutrition as MealPlanByIdResponse['data']['dailyNutrition'],
    targetDaily: detail.targetDaily as MealPlanByIdResponse['data']['targetDaily'],
  };
};

export const getGenericMealPlanForDisplay = async (
  genericPlanId: string,
): Promise<{ success: boolean; message?: string; data?: FinalizedMealPlanData }> => {
  const response = await getGenericMealPlan(genericPlanId);
  if (!response.success || !response.data) {
    return { success: false, message: response.message || 'Failed to load cohort meal plan' };
  }

  const data = normalizeMealPlanByIdToFinalizedData(
    { success: response.success, data: genericMealPlanDetailToMealPlanByIdData(response.data, genericPlanId) },
    genericPlanId,
  );
  return {
    success: true,
    data: {
      ...data,
      clientId: response.data.clientId ?? data.clientId,
      clinicalTags: response.data.clinicalTags,
      availableClinicalTags: response.data.availableClinicalTags,
    },
  };
};

const CUSTOMIZED_RECIPE_MICRONUTRIENT_KEYS = ['sodium', 'calcium', 'potassium'] as const;
const CUSTOMIZED_RECIPE_MIN_COVERAGE_RATIO = 0.5;

type CustomizedRecipeMicronutrientKey = (typeof CUSTOMIZED_RECIPE_MICRONUTRIENT_KEYS)[number];

const getObjectRecord = (value: unknown): Record<string, unknown> | undefined => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  return value as Record<string, unknown>;
};

const toFiniteNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined;
  }
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
};

const roundNutritionValue = (value: number): number => Math.round(value * 100) / 100;

const collectIngredientMicronutrientTotals = (
  ingredients: unknown[]
): {
  totals: Partial<Record<CustomizedRecipeMicronutrientKey, number>>;
  coverageCounts: Partial<Record<CustomizedRecipeMicronutrientKey, number>>;
  ingredientsWithNutrition: number;
} => {
  const totals: Partial<Record<CustomizedRecipeMicronutrientKey, number>> = {};
  const coverageCounts: Partial<Record<CustomizedRecipeMicronutrientKey, number>> = {};

  CUSTOMIZED_RECIPE_MICRONUTRIENT_KEYS.forEach((key) => {
    totals[key] = 0;
    coverageCounts[key] = 0;
  });

  let ingredientsWithNutrition = 0;

  ingredients.forEach((rawIngredient) => {
    const ingredient = getObjectRecord(rawIngredient);
    const nutrition = getObjectRecord(ingredient?.nutrition);
    if (!nutrition) return;

    ingredientsWithNutrition += 1;
    CUSTOMIZED_RECIPE_MICRONUTRIENT_KEYS.forEach((key) => {
      const value = toFiniteNumber(nutrition[key]);
      if (value === undefined) return;
      totals[key] = (totals[key] ?? 0) + value;
      coverageCounts[key] = (coverageCounts[key] ?? 0) + 1;
    });
  });

  return { totals, coverageCounts, ingredientsWithNutrition };
};

const reconcileCustomizedRecipeMicronutrients = (payloadData: unknown): void => {
  const data = getObjectRecord(payloadData);
  if (!data) return;

  const customization = getObjectRecord(data.customization);
  const recipe = getObjectRecord(data.recipe);
  const customNutrition = getObjectRecord(customization?.nutrition);
  if (!customization || !customNutrition) return;

  const topLevelNutrition = getObjectRecord(data.nutrition);
  const recipePerServingNutrition = getObjectRecord(recipe?.nutritionPerServing);
  const ingredientCandidates = [
    customization.finalIngredients,
    customization.ingredients,
    data.ingredients,
    recipe?.ingredients,
  ];
  const sourceIngredients = ingredientCandidates.find(
    (candidate) => Array.isArray(candidate) && candidate.length > 0
  ) as unknown[] | undefined;
  if (!sourceIngredients || sourceIngredients.length === 0) return;

  const { totals, coverageCounts, ingredientsWithNutrition } = collectIngredientMicronutrientTotals(sourceIngredients);
  if (ingredientsWithNutrition === 0) return;

  const keysToReconcile = CUSTOMIZED_RECIPE_MICRONUTRIENT_KEYS.filter((key) => {
    const summedValue = totals[key];
    if (summedValue === undefined) return false;

    const coverageCount = coverageCounts[key] ?? 0;
    const coverageRatio = coverageCount / ingredientsWithNutrition;
    if (coverageRatio < CUSTOMIZED_RECIPE_MIN_COVERAGE_RATIO) return false;

    const currentValue = toFiniteNumber(customNutrition[key]);
    // Respect backend-provided customization nutrition values.
    // Only backfill when the nutrient is absent/invalid in API response.
    return (currentValue === undefined || currentValue <= 0) && summedValue > 0;
  });

  if (keysToReconcile.length === 0) return;

  const applyReconciledValues = (targetNutrition?: Record<string, unknown>) => {
    if (!targetNutrition) return;
    keysToReconcile.forEach((key) => {
      const value = totals[key];
      if (value === undefined) return;
      targetNutrition[key] = roundNutritionValue(value);
    });
  };

  applyReconciledValues(customNutrition);
  applyReconciledValues(topLevelNutrition);
  applyReconciledValues(recipePerServingNutrition);
};

/** Get customized recipe by meal plan item - GET /recipes/customized?mealPlanId=&itemId= (for finalized view). */
export const getCustomizedRecipeByItem = async (
  mealPlanId: string,
  itemId: string
): Promise<{ success: boolean; data?: any; message?: string }> => {
  const response = await api.get('/recipes/customized', {
    params: { mealPlanId, itemId },
  });
  const payload = response.data as { data?: unknown } | undefined;
  reconcileCustomizedRecipeMicronutrients(payload?.data);
  return response.data;
};

// Get customized recipe details (draft context: mealPlanId + itemId required)
export const getCustomizedRecipeDetails = async (
  recipeId: string,
  draftId: string,
  day: number,
  mealName: string,
  itemId: string,
): Promise<{
  success: boolean;
  data?: any;
  message?: string;
}> => {
  const normalizedItemId = String(itemId ?? '').trim();
  if (!normalizedItemId) {
    throw new Error('itemId is required for draft customized recipe lookup');
  }

  const response = await api.get('/recipes/customized', {
    params: {
      mealPlanId: draftId,
      itemId: normalizedItemId,
      recipeId,
      day,
      mealName,
    },
  });
  const payload = response.data as { data?: unknown } | undefined;
  reconcileCustomizedRecipeMicronutrients(payload?.data);
  return response.data;
};

// Get specific draft by ID - keeps DraftData shape for draft editing flows (auto/manual pages).
export const getMealPlanDraftById = async (
  draftId: string
): Promise<{ success: boolean; message: string; data?: DraftData | FinalizedMealPlanData }> => {
  const result = await getDraftForDisplay(draftId);
  return { success: result.success ?? true, message: result.message ?? '', data: result.data };
};

// Get specific draft by ID for AI draft display (normalized to FinalizedMealPlanData-like shape with days).
export const getMealPlanDraftByIdForAIDisplay = async (
  draftId: string
): Promise<{ success: boolean; message: string; data?: FinalizedMealPlanData }> => {
  const raw = await getDraftById(draftId);
  const payload = raw.data ?? raw;
  if (isApiDraftShape(payload)) {
    const normalized = normalizeMealPlanByIdToFinalizedData(
      { data: payload } as unknown as MealPlanByIdResponse,
      draftId
    );
    return { success: raw.success ?? true, message: raw.message ?? '', data: normalized };
  }
  const result = await getDraftForDisplay(draftId);
  const maybeFinalized = result.data as unknown as FinalizedMealPlanData | undefined;
  return { success: result.success ?? true, message: result.message ?? '', data: maybeFinalized };
};

// DELETE /meal-plans/{id}
export const deleteMealPlan = async (mealPlanId: string): Promise<{ success: boolean; message?: string }> => {
  try {
    const response = await api.delete(`/meal-plans/${mealPlanId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// DELETE /meal-plans/drafts/{id}
export const deleteDraft = async (draftId: string): Promise<{ success: boolean; message?: string }> => {
  try {
    const response = await api.delete(`/meal-plans/drafts/${draftId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Get detailed meal plan (for PDF generation and detailed view)
export const getDetailedMealPlan = async (draftId: string): Promise<DetailedMealPlanResponse> => {
  try {
    const response = await api.get(`/meal-plans/detailed?draftId=${draftId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// GET /meal-plans/all - list all meal plans for a client (replaces drafts list for client page)
export const getMealPlansAll = async (params: GetMealPlansAllParams): Promise<GetMealPlansAllResponse> => {
  try {
    const {
      clientId,
      type,
      status,
      search,
      page = 1,
      pageSize = 20,
      sortBy = 'updated_at',
      sortOrder = 'desc',
    } = params;

    const queryParams = new URLSearchParams({
      page: page.toString(),
      pageSize: Math.min(Math.max(1, pageSize || 20), 100).toString(),
      sortBy,
      sortOrder,
    });
    if (clientId?.trim()) queryParams.set('clientId', clientId.trim());
    if (type) queryParams.set('type', type);
    if (status) queryParams.set('status', status);
    if (search?.trim()) queryParams.set('search', search.trim());

    const response = await api.get(`/meal-plans/all?${queryParams.toString()}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Get finalized meal plans / list drafts - legacy (prefer getMealPlansAll for client page)
export const getFinalizedMealPlans = async (params: GetFinalizedMealPlansParams): Promise<GetFinalizedMealPlansResponse> => {
  try {
    const {
      status = 'finalized',
      creationMethod,
      page = 1,
      pageSize = 20,
      limit,
      includeNutrition = true,
      clientId
    } = params;

    const limitVal = limit ?? pageSize;
    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limitVal.toString(),
      includeNutrition: includeNutrition.toString(),
    });
    if (pageSize !== limitVal) queryParams.set('pageSize', pageSize.toString());

    if (status && status !== 'all') {
      queryParams.append('status', status);
    }

    if (clientId) {
      queryParams.append('clientId', clientId);
    }

    if (creationMethod) {
      queryParams.append('creationMethod', creationMethod);
    }

    const response = await api.get(`/meal-plans/drafts?${queryParams.toString()}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

function parseNutritionalAnalysisMealPlanNameRow(
  raw: unknown,
): NutritionalAnalysisMealPlanNameItem | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const id = typeof o.id === 'string' ? o.id.trim() : '';
  const name = typeof o.name === 'string' ? o.name.trim() : '';
  if (!id || !name) return null;
  const row: NutritionalAnalysisMealPlanNameItem = { id, name };
  if (typeof o.clientId === 'string' && o.clientId.trim()) row.clientId = o.clientId.trim();
  if (typeof o.planType === 'string') row.planType = o.planType;
  if (typeof o.status === 'string') row.status = o.status;
  if (typeof o.generationType === 'string') row.generationType = o.generationType;
  if (typeof o.generationSource === 'string') row.generationSource = o.generationSource;
  if ('aiProvider' in o) row.aiProvider = o.aiProvider == null ? null : String(o.aiProvider);
  if (typeof o.createdAt === 'string') row.createdAt = o.createdAt;
  if (typeof o.updatedAt === 'string') row.updatedAt = o.updatedAt;
  return row;
}

function normalizeNutritionalAnalysisMealPlanNamesPayload(body: unknown): NutritionalAnalysisMealPlanNameItem[] {
  if (body == null) return [];
  const envelope = typeof body === 'object' && body !== null && 'data' in body ? (body as { data: unknown }).data : body;
  let list: unknown = envelope;

  if (list && typeof list === 'object' && 'mealPlanNames' in (list as object)) {
    list = (list as { mealPlanNames: unknown }).mealPlanNames;
  } else if (list && typeof list === 'object' && 'names' in (list as object)) {
    list = (list as { names: unknown }).names;
  }

  if (!Array.isArray(list)) return [];

  const rows: NutritionalAnalysisMealPlanNameItem[] = [];
  for (const item of list) {
    if (typeof item === 'string') {
      const label = item.trim();
      if (label) rows.push({ id: label, name: label });
      continue;
    }
    const parsed = parseNutritionalAnalysisMealPlanNameRow(item);
    if (parsed) rows.push(parsed);
  }
  return rows;
}

/**
 * GET /meal-plans/nutritional-analysis/names — AI + manual meal plans for nutritional analysis pickers.
 * Response: `{ success, data: { mealPlanNames: [{ id, name, ... }] }, message }`.
 * Optional `clientId` query when the API supports scoping.
 */
export const getNutritionalAnalysisMealPlanNames = async (params?: {
  clientId?: string;
}): Promise<NutritionalAnalysisMealPlanNameItem[]> => {
  const query = new URLSearchParams();
  if (params?.clientId) query.set('clientId', params.clientId);
  const qs = query.toString();
  const url = qs ? `/meal-plans/nutritional-analysis/names?${qs}` : '/meal-plans/nutritional-analysis/names';
  const response = await api.get(url);
  return normalizeNutritionalAnalysisMealPlanNamesPayload(response.data);
};

/**
 * GET /meal-plans/{mealPlanOrDraftId}/nutritional-analysis — analysis payload for a finalized plan or draft.
 */
export const getMealPlanNutritionalAnalysis = async (
  mealPlanOrDraftId: string,
): Promise<MealPlanNutritionalAnalysisApiResponse> => {
  const raw = String(mealPlanOrDraftId ?? '').trim();
  if (!raw) {
    throw new Error('Missing meal plan id');
  }
  const response = await api.get(`/meal-plans/${encodeURIComponent(raw)}/nutritional-analysis`);
  return response.data as MealPlanNutritionalAnalysisApiResponse;
};

/**
 * GET /meal-plans/{id}/nutritional-analysis/foods?nutrient=...&limit=...
 * Per-food contributions for a single nutrient on the plan analysis.
 */
export const getMealPlanNutritionalAnalysisFoods = async (
  mealPlanOrDraftId: string,
  params: { nutrient: string; limit?: number; day?: number },
): Promise<MealPlanNutritionalAnalysisFoodsApiResponse> => {
  const raw = String(mealPlanOrDraftId ?? '').trim();
  if (!raw) {
    throw new Error('Missing meal plan id');
  }
  const nutrient = String(params.nutrient ?? '').trim().toLowerCase();
  if (!nutrient) {
    throw new Error('Missing nutrient');
  }
  const query = new URLSearchParams();
  query.set('nutrient', nutrient);
  if (params.limit != null && Number.isFinite(params.limit) && params.limit > 0) {
    query.set('limit', String(Math.min(500, Math.floor(params.limit))));
  }
  if (params.day != null && Number.isFinite(params.day) && params.day >= 1) {
    query.set('day', String(Math.floor(params.day)));
  }
  const qs = query.toString();
  const response = await api.get(
    `/meal-plans/${encodeURIComponent(raw)}/nutritional-analysis/foods${qs ? `?${qs}` : ''}`,
  );
  return response.data as MealPlanNutritionalAnalysisFoodsApiResponse;
};

function normalizeNutrientKeysForVisibilityBody(nutrients: readonly string[]): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const n of nutrients) {
    const key = String(n ?? '').trim().toLowerCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(key);
  }
  return out;
}

/**
 * POST /meal-plans/{id}/hide-nutrients — persist nutrients hidden from plan-level nutrition views.
 * After success, refetch `getMealPlanNutritionalAnalysis` if the UI should reflect server state.
 */
export const hideMealPlanNutrients = async (
  mealPlanId: string,
  body: MealPlanNutrientsVisibilityBody,
): Promise<MealPlanNutrientsVisibilityApiResponse> => {
  const raw = String(mealPlanId ?? '').trim();
  if (!raw) {
    throw new Error('Missing meal plan id');
  }
  const nutrients = normalizeNutrientKeysForVisibilityBody(body.nutrients ?? []);
  if (!nutrients.length) {
    throw new Error('Missing nutrients');
  }
  const response = await api.post(`/meal-plans/${encodeURIComponent(raw)}/hide-nutrients`, { nutrients });
  return response.data as MealPlanNutrientsVisibilityApiResponse;
};

/**
 * POST /meal-plans/{id}/unhide-nutrients — show nutrients again on the meal plan.
 */
export const unhideMealPlanNutrients = async (
  mealPlanId: string,
  body: MealPlanNutrientsVisibilityBody,
): Promise<MealPlanNutrientsVisibilityApiResponse> => {
  const raw = String(mealPlanId ?? '').trim();
  if (!raw) {
    throw new Error('Missing meal plan id');
  }
  const nutrients = normalizeNutrientKeysForVisibilityBody(body.nutrients ?? []);
  if (!nutrients.length) {
    throw new Error('Missing nutrients');
  }
  const response = await api.post(`/meal-plans/${encodeURIComponent(raw)}/unhide-nutrients`, { nutrients });
  return response.data as MealPlanNutrientsVisibilityApiResponse;
};

// Get portion sizes
export const getPortionSizes = async (): Promise<{
  success: boolean;
  data?: any;
  message?: string;
}> => {
  try {
    const response = await api.get('/portion-sizes');
    return response.data;
  } catch (error) {
    throw error;
  }
};
